import React, { useState, useEffect, useRef, useCallback } from 'react';
import { searchAllocationsByWindow, startAllocation, getAllocationJobStatus, ManualAllocation, wipeAllocations, isTestModeEnabled, createOperatingRoomSchedule, createShift, updateOperatingRoomSchedule, deleteOperatingRoomSchedule, getEmployeeTypeDisplayName, updateAllocations, saveGradingBaseline, publishGradingDay } from '../services/api.ts';
import ManualAllocationModal from './ManualAllocationModal.tsx';
import AdminOnly from './AdminOnly.tsx';
import ReadOnlyBanner from './ReadOnlyBanner.tsx';
import { useAuth } from '../contexts/AuthContext.tsx';
import ShiftsCard from './ShiftsCard.tsx';
import AllocatedShiftsCard from './AllocatedShiftsCard.tsx';
import { crnaNameplateColor, doctorNameplateColor, doctorBullpenColor, studentColorFor, INHERITED_TEAM_COLOR, NAMEPLATE_COLORS } from './staffNameplates.ts';
import { matchesPaletteColor, reloadPaletteOverrides, PALETTE_CHANGED_EVENT, PALETTE_STORAGE_KEY } from './nameplatePalette.ts';
import { loadViewMode, saveViewMode, loadShiftsView, saveShiftsView, loadSelectedHospitalIds, saveSelectedHospitalIds, pruneUnknownHospitalIds, loadForcedHospitalIds, saveForcedHospitalIds, loadSitesOpen, saveSitesOpen, loadBullpenOpen, saveBullpenOpen, loadSitesWidth, saveSitesWidth, loadBullpenWidth, saveBullpenWidth, SITES_WIDTH, BULLPEN_WIDTH, maxSidebarWidth } from './boardPrefs.ts';
import { SidebarRail, SidebarCollapseButton, SidebarResizeHandle } from './SidebarToggle.tsx';
import RoomNamePlates, { DoctorCoin } from './RoomNamePlates.tsx';
import CaseMarkersPopover from './CaseMarkersPopover.tsx';
import { loadSiteLocks, setSiteLock } from './siteLocks.ts';
import { loadRoomTransfers, setRoomTransfer, effectiveDoctorIdsForRoom, groupTransferCandidates, clearRoomTransfers } from './roomTransfers.ts';
import { loadNurseTransfers, effectiveNurseIdsForRoom, saveNurseTransfers, clearNurseTransfers } from './nurseTransfers.ts';
import { moveNurseToRoom, moveNurseToRooms, swapNurseRooms, removeNurseFromRoom, dropNurseOnRoom, nurseEditsForOverlay, type RawCrnaByRoom } from './nurseRoomMoves.ts';
import { loadObHold, applyObHold, clearObHold, obHoldEmployeeIds, ObHoldMap } from './obHold.ts';
import { loadNoCred, applyNoCred, NoCredMap } from './noCredRooms.ts';
import { loadFloatCrnas, setFloatCrna as persistFloatCrna, clearFloatCrnaForEmployee, floatCrnaEmployeeIds, floatCrnaAt, applyFloatAssignments, clearFloatCrnas, FloatCrnaMap, loadFloatSlots, floatSlotsAt, FloatSlotMap } from './floatCrnas.ts';
import { peakConcurrentSignatures, signaturesAtMoment } from './supervisionUtils.ts';
import type { DoctorDayStatus, ReliefPickerGroup } from './EmployeeDetailsPopover.tsx';
import EmployeeDetailsPopover, { normalizeDoctorStatuses } from './EmployeeDetailsPopover.tsx';
import { loadSlotMarks, setSlotMark, getSlotMark, clearSlotMarks, SlotMark } from './slotMarks.ts';
import { loadCaseMarkers, setCaseMarker, CaseMarker, CaseMarkerKind } from './caseMarkers.ts';
import { loadMoveLog, appendMove, clearMoveLog, MoveLogEntry } from './moveLog.ts';
import { buildDoctorEditPayload, editsForRooms, editsFromUndo, DoctorEdit, buildNurseEditPayload, nurseEditsFromUndo, NurseUndoChange, DoctorUndoChange } from './doctorMoves.ts';
import { pushUndo, popUndo, loadUndoStack, clearUndo, MoveUndoEntry } from './moveUndo.ts';
import { commitAllocationEdit, editedScheduleIds, GuardableAllocation } from './allocationWriteGuard.ts';
import Bullpen from './Bullpen.tsx';
import WhosFreePanel, { WHOS_FREE_PURPLE, FreeBody, FreeColumn } from './WhosFreePanel.tsx';
import ColorLegend from './ColorLegend.tsx';
import Toast from './Toast.tsx';
import HospitalORSidebar from './HospitalORSidebar.tsx';
import BoardScheduleBlock from './BoardScheduleBlock.tsx';
import { loadReliefOverlay, saveReliefOverlay, applyReliefHandoffs, handoffTimesFor, withoutHandoffTimes, type ReliefOverlay, type ReliefHandoff } from './reliefOverlay.ts';
import { colorForHospital, darkenHex } from './hospitalColors.ts';
import { localDateToUtcStart, localDateToUtcEnd, utcToLocalTime, localDateTimeToUtc, getCentralOffsetStringForDate } from '../utils/timezone.ts';
import { planAllocationCompletion, shouldShowRoomInActive, toggleHospitalIdInSet, computeStateAfterModalClose, buildScheduleHoverDetails, isAllocateDisabled, resolvePinPrefill, earliestShiftByEmployee, effectivePeckingOrder, providerCardShift, effectiveCaseFlags, hospitalOrderFromRooms, boardCardState, doctorCoinSortKey, shortPrepMinutes, takeoverScheduleIds, reportInMs, daySupervisingDoctorIds, isCredentialedAtHospital, BOARD_OR_COLUMN_PX, BOARD_HOUR_COLUMN_PX, supervisorsNeeded, scheduleIdsToVacateForSingleRoomPlacement, shouldAutoParkTimeBar } from './boardViewHelpers.ts';
import { CaseWindow, AFTER_HOURS_CUTOFF_MIN, BUCKET_MINUTES, caseWindow, roomsRunningAfter, roomsRunningAt, peakRoomsPerBucket, peakConcurrency, loadBand, firstCaseStartOnDay } from './caseConcurrency.ts';
import { carriedRelief, clearReliefAt, caseCoverageSegments, coverageSegmentsBackground, presenceIntervals, CoverageSegment, carriedReliefEntry, caseRoleHolders, roleHoldersAt, type CaseRelief } from './caseCoverage.ts';
import { OffLog, recordOffToggle, setOffEntryTime, awayIntervals, hmNow } from './offTimes.ts';
import { isLocalMode } from '../utils/localMode.ts';
import { useQueryClient } from '@tanstack/react-query';
import { useWorkingDate } from '../contexts/WorkingDateContext.tsx';
import { useDayBundle } from '../data/queries.ts';
import DataFreshnessNote from './DataFreshnessNote.tsx';
import { queryKeys } from '../data/queryKeys.ts';

// A per-site drop target that sits at the end of the Float CRNAs row. Dragging an available CRNA (from the
// bullpen, or off a room) onto it floats them at that site — creating a float slot if the site has none yet.
// Only CRNAs (employeeType 'nurse') are accepted; doctors/students dropped here are ignored. Its own `over`
// state gives the same dashed-outline affordance the room slots use.
// Case-load strip colours, keyed by loadBand()'s ABSOLUTE thresholds (0-9 / 10-19 / 20-29 / 30+).
// Each band is an explicit background/numeral PAIR rather than a background alone, so contrast holds
// in both the light and dark board themes instead of inheriting a theme variable that might match it.
// The load strip pins to the top of .board-table's scrollport, and the already-sticky hour-label
// thead is pushed down by exactly this much so the two stack instead of overlapping. One constant,
// used by both, so they cannot drift apart.
const LOAD_STRIP_HEIGHT_PX = 28;

const LOAD_BAND_STYLE: Record<string, { bg: string; fg: string }> = {
  low: { bg: '#dbeafe', fg: '#1e40af' },        // faded blue, dark blue numeral
  medium: { bg: '#fef3c7', fg: '#a16207' },     // soft yellow, darker sunflower numeral
  high: { bg: '#fecaca', fg: '#991b1b' },       // soft red, dark red numeral
  critical: { bg: '#b91c1c', fg: '#ffffff' },   // dark vibrant red, white numeral — 30+ only
};


const FloatDropZone: React.FC<{ hospColor: string; onDropCrna: (employeeId: string) => void }> = ({ hospColor, onDropCrna }) => {
  const [over, setOver] = useState(false);
  return (
    <span
      onDragOver={(e) => { e.preventDefault(); e.dataTransfer.dropEffect = 'copy'; }}
      onDragEnter={(e) => { e.preventDefault(); setOver(true); }}
      onDragLeave={(e) => { if (!e.currentTarget.contains(e.relatedTarget as Node)) setOver(false); }}
      onDrop={(e) => {
        e.preventDefault();
        setOver(false);
        try {
          const raw = e.dataTransfer.getData('application/x-staff');
          if (!raw) return;
          const { employeeId, employeeType } = JSON.parse(raw);
          if (employeeType !== 'nurse') return; // only CRNAs float
          onDropCrna(employeeId);
        } catch { /* ignore malformed drag payloads */ }
      }}
      title="Drag an available CRNA here to float them at this site"
      style={{
        fontSize: '0.62em', fontWeight: 700, padding: '3px 11px', borderRadius: '999px', whiteSpace: 'nowrap',
        background: over ? hospColor : 'transparent', color: over ? '#fff' : 'var(--text-muted)',
        border: `1px dashed ${hospColor}`,
        outline: over ? `2px solid ${hospColor}` : 'none', outlineOffset: '2px', transition: 'background 0.1s',
      }}
    >
      ＋ Float (drop CRNA)
    </span>
  );
};

interface OperatingRoomSchedule {
  scheduleId: string;
  operatingRoomId: string;
  startTimeUtc: string;
  endTimeUtc: string;
  surgeryType: string;
  status: 'scheduled' | 'in-progress' | 'completed' | 'cancelled';
  allocatedNurseIds: string[];
  allocatedDoctorIds: string[];
}

interface Allocation {
  allocationId: string;
  day: string;
  operatingRoomScheduleId: string;
  allocatedNurseIds: string[];
  allocatedDoctorIds: string[];
  createdAt: string;
  colorOverrides?: Record<string, string>;
}

interface Employee {
  employeeId: string;
  employeeType: 'nurse' | 'doctor' | 'nurse-student-junior' | 'nurse-student-senior';
  firstName: string;
  lastName: string;
  homeHospitalId?: string;
}

interface OperatingRoom {
  operatingRoomId: string;
  roomNumber: string;
  name: string;
  hospitalId: string;
  credential?: string;
}

interface Hospital {
  hospitalId: string;
  name: string;
  address: {
    street: string;
    city: string;
    state: string;
    zipCode: string;
    country: string;
  };
}

// Anesthesiologists supervise up to this many rooms concurrently; their bullpen brick stays
// available (copyable to multiple rooms) until they reach it. CRNAs/students are one room each.
const DOCTOR_MAX_ROOMS = 4;

const BoardView: React.FC = () => {
  const { isAdmin } = useAuth();
  // Board date rule: the first time you open the board on a given real-world day, start on today.
  // If you then move to a past/future date, that choice sticks across refreshes — but only for the
  // rest of that same day. The next day you open the browser, you're back on "today" again.
  // The date is the app-wide working date (contexts/WorkingDateContext.tsx), shared with Manpower and Shifts and
  // persisted under the same keys the board always used.
  const [selectedDate, setSelectedDate] = useWorkingDate();
  // The date on screen RIGHT NOW, for async work (an allocation run's completion) that started on another one.
  const selectedDateRef = useRef(selectedDate);
  selectedDateRef.current = selectedDate;

  // Annotations set on the Anesthesiologists tab — pecking-order (drag-reorder) mirrored into the
  // bullpen, and the brown "meeting" flag shown on the doctor nameplate. Stored per day under
  // `oc-anes-<date>`; re-read on date change and when the window regains focus (so edits on the other
  // tab show up here after switching back).
  const [peckingOrderOverride, setPeckingOrderOverride] = useState<Record<string, number>>({});
  const [meetingByEmployee, setMeetingByEmployee] = useState<Record<string, boolean>>({});
  const [statusByEmployee, setStatusByEmployee] = useState<Record<string, DoctorDayStatus[]>>({});
  const [notesByEmployee, setNotesByEmployee] = useState<Record<string, string>>({});
  // When each provider was sent home / called back today (offTimes.ts) — drives the red spans on the case blocks.
  const [offLogByEmployee, setOffLogByEmployee] = useState<OffLog>({});
  // Manual in-times ("HH:MM") typed on the Anesthesiologists / CRNA tabs — the arrival the short-prep outline uses.
  const [inTimeByEmployee, setInTimeByEmployee] = useState<Record<string, string>>({});
  useEffect(() => {
    const load = () => {
      try {
        const raw = localStorage.getItem(`oc-anes-${selectedDate}`);
        const parsed = raw ? JSON.parse(raw) : {};
        // Mirror the Anesthesiologists tab's migration: fold the legacy boolean `meeting` flag into the
        // newer `status` disposition so a pre-existing day reads the same on the board as on the tab.
        const meeting = parsed.meeting || {};
        // Normalize every stored disposition to a LIST (legacy single value -> [value]); then fold the legacy
        // boolean `meeting` flag in so a pre-existing day reads the same here as on the Anesthesiologists tab.
        const status: Record<string, DoctorDayStatus[]> = {};
        for (const id of Object.keys(parsed.status || {})) { const list = normalizeDoctorStatuses(parsed.status[id]); if (list.length) status[id] = list; }
        for (const id of Object.keys(meeting)) { if (meeting[id] && !(status[id] || []).includes('meeting')) status[id] = [...(status[id] || []), 'meeting']; }
        setPeckingOrderOverride(parsed.order || {});
        setMeetingByEmployee(meeting);
        setStatusByEmployee(status);
        setNotesByEmployee(parsed.notes || {});
        setOffLogByEmployee(parsed.offLog || {});
        setInTimeByEmployee(parsed.inTime || {});
      } catch { setPeckingOrderOverride({}); setMeetingByEmployee({}); setStatusByEmployee({}); setNotesByEmployee({}); setOffLogByEmployee({}); setInTimeByEmployee({}); }
    };
    load();
    window.addEventListener('focus', load);
    return () => window.removeEventListener('focus', load);
  }, [selectedDate]);
  // Write disposition/notes back into the shared `oc-anes-<date>` store (read-modify-write so the
  // Anesthesiologists tab's order / in-times / etc. survive) and mirror into local state. Keeps the legacy
  // `meeting` boolean in sync with status==='meeting' so the nameplate's brown meeting tab still works.
  const writeAnesAnno = (mutate: (prev: any) => any) => {
    try {
      const raw = localStorage.getItem(`oc-anes-${selectedDate}`);
      const prev = { order: {}, notes: {}, meeting: {}, inTime: {}, status: {}, offLog: {}, ...(raw ? JSON.parse(raw) : {}) };
      const next = mutate(prev);
      localStorage.setItem(`oc-anes-${selectedDate}`, JSON.stringify(next));
      const normStatus: Record<string, DoctorDayStatus[]> = {};
      for (const id of Object.keys(next.status || {})) { const list = normalizeDoctorStatuses(next.status[id]); if (list.length) normStatus[id] = list; }
      setStatusByEmployee(normStatus);
      setNotesByEmployee(next.notes || {});
      setMeetingByEmployee(next.meeting || {});
      setOffLogByEmployee(next.offLog || {});
      setInTimeByEmployee(next.inTime || {});
    } catch { /* ignore */ }
  };
  // Toggle one disposition for a doctor (add if absent, remove if present) — dispositions are multi-select.
  // Keeps the legacy `meeting` boolean in sync so the nameplate's brown meeting tab still works.
  const toggleAnesStatus = (id: string, status: DoctorDayStatus) => writeAnesAnno((prev: any) => {
    const s = { ...(prev.status || {}) };
    const cur = normalizeDoctorStatuses(s[id]);
    const nextList = cur.includes(status) ? cur.filter(x => x !== status) : [...cur, status];
    if (nextList.length) s[id] = nextList; else delete s[id];
    // Setting Off records when they were sent home; clearing it records when they were called back (offTimes.ts).
    const offLog = status === 'off' ? recordOffToggle(prev.offLog, id, !cur.includes('off'), hmNow()) : prev.offLog;
    return { ...prev, status: s, offLog, meeting: { ...(prev.meeting || {}), [id]: nextList.includes('meeting') } };
  });
  const setAnesNote = (id: string, note: string) => writeAnesAnno((prev: any) => ({ ...prev, notes: { ...(prev.notes || {}), [id]: note } }));
  // Correct a recorded sent-home / called-back time from the person card.
  const setAnesOffTime = (id: string, index: number, field: 'off' | 'back', hm: string) =>
    writeAnesAnno((prev: any) => ({ ...prev, offLog: setOffEntryTime(prev.offLog, id, index, field, hm) }));

  // Relief-planning overlay (board-runner planning, per day, kept LOCAL so it never disturbs the real
  // allocation records). `inRoom[scheduleId] = doctorId` marks an anesthesiologist who's been dragged
  // onto a room's CRNA to go in solo (shown above the CRNA in the "doc in room" color; the CRNA stays
  // until the runner removes them). `reliefSup[scheduleId] = doctorId` is a relief supervisor dropped
  // into the blank box that appears above the original supervisor on rooms whose supervisor has gone
  // in-room somewhere.
  // Symphony's handoffs partway through a case also record WHEN the reliever takes over (inRoomFrom / reliefSupFrom) —
  // see reliefOverlay.ts.
  const [reliefOverlay, setReliefOverlay] = useState<ReliefOverlay>({ inRoom: {}, reliefSup: {} });
  const reliefKey = `oc-board-relief-${selectedDate}`;
  useEffect(() => {
    const load = () => {
      setReliefOverlay(loadReliefOverlay(selectedDate));
    };
    load();
    window.addEventListener('focus', load);
    return () => window.removeEventListener('focus', load);
  }, [selectedDate]);
  const mutateRelief = (fn: (prev: ReliefOverlay) => ReliefOverlay) => {
    setReliefOverlay(prev => {
      const next = fn(prev);
      try { localStorage.setItem(reliefKey, JSON.stringify(next)); } catch { /* ignore */ }
      return next;
    });
  };
  // Drop a doctor onto a room's CRNA -> that doctor goes into the room solo (above the CRNA).
  const handleDropDoctorInRoom = (scheduleId: string, doctorId: string) => {
    if (blockIfUncredentialed(doctorId, scheduleMap.get(scheduleId)?.operatingRoomId)) return;
    mutateRelief(p => ({ ...p, inRoom: { ...p.inRoom, [scheduleId]: doctorId }, inRoomFrom: withoutHandoffTimes(p.inRoomFrom, [scheduleId]) }));
  };
  // Drop a doctor into the blank relief-supervisor box above a room's supervisor.
  const handleDropReliefSupervisor = (scheduleId: string, doctorId: string) => {
    if (blockIfUncredentialed(doctorId, scheduleMap.get(scheduleId)?.operatingRoomId)) return;
    mutateRelief(p => ({ ...p, reliefSup: { ...p.reliefSup, [scheduleId]: doctorId }, reliefSupFrom: withoutHandoffTimes(p.reliefSupFrom, [scheduleId]) }));
  };
  // Relief carries to the room's later cases, so clearing a box empties THAT case and the room's later ones — even
  // when the relief shown there was carried in from an earlier case (caseCoverage.clearReliefAt). Earlier cases keep it.
  const roomCasesOf = (scheduleId: string) => getSchedulesForRoom(scheduleMap.get(scheduleId)?.operatingRoomId || '');
  const clearInRoom = (scheduleId: string) =>
    mutateRelief(p => ({ ...p, inRoom: clearReliefAt(scheduleId, roomCasesOf(scheduleId), p.inRoom), inRoomFrom: withoutHandoffTimes(p.inRoomFrom, [scheduleId]) }));
  const clearReliefSupervisor = (scheduleId: string) =>
    mutateRelief(p => ({ ...p, reliefSup: clearReliefAt(scheduleId, roomCasesOf(scheduleId), p.reliefSup), reliefSupFrom: withoutHandoffTimes(p.reliefSupFrom, [scheduleId]) }));
  // NOTE: inRoomDoctorIds and reliefEmployeeIds are derived LOWER DOWN (after schedules load) so relief
  // entries keyed by a scheduleId that a re-simulation has replaced can be filtered out — see "Live relief".
  // Remove a person from ALL relief assignments (used when they're placed into a real slot instead).
  const clearReliefForEmployee = (empId: string) => mutateRelief((prev) => {
    const inRoom = { ...prev.inRoom }; const inRoomGone = Object.keys(inRoom).filter(k => inRoom[k] === empId); for (const k of inRoomGone) delete inRoom[k];
    const reliefSup = { ...prev.reliefSup }; const supGone = Object.keys(reliefSup).filter(k => reliefSup[k] === empId); for (const k of supGone) delete reliefSup[k];
    return { inRoom, reliefSup, inRoomFrom: withoutHandoffTimes(prev.inRoomFrom, inRoomGone), reliefSupFrom: withoutHandoffTimes(prev.reliefSupFrom, supGone) };
  });

  // Editable site "coin" (hard lock, per day) + room-transfer overlay (per day) — both local to the Board and
  // reversible. Site lock confines a person to one site when Allocate is next run; room transfer moves a room's
  // supervising anesthesiologist to another doctor for display + in-time purposes. Reloaded on date change / focus.
  const [siteLocks, setSiteLocks] = useState<Record<string, string>>(() => loadSiteLocks(selectedDate));
  const [roomTransfers, setRoomTransfers] = useState<Record<string, string>>(() => loadRoomTransfers(selectedDate));
  const [undoStack, setUndoStack] = useState<MoveUndoEntry[]>(() => loadUndoStack(selectedDate));
  // Per-day NURSE-transfer overlay (operatingRoomId -> toNurseId; see nurseTransfers.ts) — the CRNA analogue of
  // roomTransfers. Moving/swapping a CRNA on their person card writes here; the board shows that room's CRNA as
  // the target nurse for display, bullpen, concurrency + supervision purposes. Reloaded on date change / focus.
  const [nurseTransfers, setNurseTransfers] = useState<Record<string, string>>(() => loadNurseTransfers(selectedDate));
  // Per-day FLOAT-CRNA overlay (hospitalId -> CRNA per float slot; see floatCrnas.ts). This records who's
  // pre-assigned into each slot; the slot COUNT is the per-day map below.
  const [floatCrnas, setFloatCrnasState] = useState<FloatCrnaMap>(() => loadFloatCrnas(selectedDate));
  // CRNAs Symphony is holding on an empty OB room for an add-on C-section (obHold.ts).
  const [obHold, setObHold] = useState<ObHoldMap>(() => loadObHold(selectedDate));
  // Rooms nobody working is credentialed for -> the slot reads NO CRED instead of NEED MD (noCredRooms.ts).
  const [noCred, setNoCred] = useState<NoCredMap>(() => loadNoCred(selectedDate));
  // Per-day FLOAT-CRNA slot COUNT (hospitalId -> number of float boxes), written by that day's Simulate run.
  // Per-day so floats set on one day never bleed onto another — replaces the old global Hospital.floatCrnaSlots.
  const [floatSlotMap, setFloatSlotMap] = useState<FloatSlotMap>(() => loadFloatSlots(selectedDate));
  // Which empty float slot's assign-picker is open ({hospitalId, index}), or null.
  const [floatPicker, setFloatPicker] = useState<{ hospitalId: string; index: number } | null>(null);
  // Which site's late-staff popup is open (a "N late MDs" / "N late CRNAs" badge in the float row was clicked),
  // plus the screen position it was opened at. Computed at click time (see openLateCounter) and rendered with
  // position:fixed — every hospital-section box clips overflow (see the "N ORs running" grid below it), so an
  // absolute-positioned popup anchored inside it would get silently clipped on a site with only a few OR rows.
  const [lateCounter, setLateCounter] = useState<{ hospitalId: string; type: 'doctor' | 'nurse'; top: number; left: number } | null>(null);
  // Manual empty-slot marks (see slotMarks.ts): `${roomId}::${slotKind}` -> 'need' | 'none'. Lets the runner
  // hand-flag a slot required (red NEED) or intentionally empty (muted NONE), overriding the auto behavior.
  const [slotMarks, setSlotMarks] = useState<Record<string, SlotMark>>(() => loadSlotMarks(selectedDate));
  // Per-day, per-case markers (see caseMarkers.ts): scheduleId -> { nerveBlock?, pediatric? } explicit
  // overrides that drive the yellow (nerve-block) / hot-pink (pediatric ≤12) borders on the case blocks.
  const [caseMarkers, setCaseMarkers] = useState<Record<string, CaseMarker>>(() => loadCaseMarkers(selectedDate));
  // The case whose markers popover is open (anchored to that block's on-screen rect), or null.
  const [flagPopover, setFlagPopover] = useState<{ schedule: OperatingRoomSchedule; anchor: DOMRect } | null>(null);
  // Passive per-day journal of the manual moves made on the board (see moveLog.ts). Never changes the
  // allocation — it just records what the runner did so the logic can be studied later.
  const [moveLog, setMoveLog] = useState<MoveLogEntry[]>(() => loadMoveLog(selectedDate));
  const [showMoveLog, setShowMoveLog] = useState<boolean>(false);
  const [publishing, setPublishing] = useState<boolean>(false);
  // Bumped whenever the Color Legend edits a palette colour. The palette is a module store (see
  // nameplatePalette.ts), so changing it does not re-render React on its own; this counter is the
  // board's subscription. Rendered onto the container as a data attribute so it is genuinely read.
  const [paletteVersion, setPaletteVersion] = useState<number>(0);
  useEffect(() => {
    const load = () => { setSiteLocks(loadSiteLocks(selectedDate)); setRoomTransfers(loadRoomTransfers(selectedDate)); setNurseTransfers(loadNurseTransfers(selectedDate)); setFloatCrnasState(loadFloatCrnas(selectedDate)); setObHold(loadObHold(selectedDate)); setNoCred(loadNoCred(selectedDate)); setFloatSlotMap(loadFloatSlots(selectedDate)); setMoveLog(loadMoveLog(selectedDate)); setSlotMarks(loadSlotMarks(selectedDate)); setCaseMarkers(loadCaseMarkers(selectedDate)); setUndoStack(loadUndoStack(selectedDate)); };
    load();
    setFlagPopover(null); // a day change invalidates any open case-markers popover (it points at the old day's case)
    window.addEventListener('focus', load);
    // Cross-window/tab sync: a swap (or lock) made on the Anesthesiologists tab — or the board in another window —
    // writes a per-day overlay key; the `storage` event lets this board re-read it LIVE instead of waiting for a
    // focus/navigation. (The event only fires in OTHER windows, so same-tab route changes still rely on `load()`.)
    const onStorage = (e: StorageEvent) => { if (e.key === null || (e.key && e.key.includes(selectedDate))) load(); };
    window.addEventListener('storage', onStorage);
    return () => { window.removeEventListener('focus', load); window.removeEventListener('storage', onStorage); };
  }, [selectedDate]);

  // Palette subscription. Not keyed on the date: the palette is global, not per-day. The custom event
  // covers edits made in THIS tab (a `storage` event never fires in the window that wrote it); the
  // storage listener covers another tab, and re-reads the module store before re-rendering.
  useEffect(() => {
    const bump = () => setPaletteVersion(v => v + 1);
    const onPaletteStorage = (e: StorageEvent) => {
      if (e.key === null || e.key === PALETTE_STORAGE_KEY) { reloadPaletteOverrides(); bump(); }
    };
    window.addEventListener(PALETTE_CHANGED_EVENT, bump);
    window.addEventListener('storage', onPaletteStorage);
    return () => {
      window.removeEventListener(PALETTE_CHANGED_EVENT, bump);
      window.removeEventListener('storage', onPaletteStorage);
    };
  }, []);

  // The day's data comes from the shared in-memory cache (data/queries.ts), keyed by date: a day already viewed —
  // here, on Manpower, or prefetched as a neighbour — renders at once and revalidates in the background, and a day
  // never fetched shows "Loading..." rather than the previous day's rooms. Allocation writes go into the same cache,
  // so an edit made here is what the Manpower views show too.
  const queryClient = useQueryClient();
  const day = useDayBundle(selectedDate);
  const schedules = day.schedules as OperatingRoomSchedule[];
  const allocations = day.allocations as Allocation[];
  const employees = day.employees as Employee[];
  const operatingRooms = day.operatingRooms as OperatingRoom[];
  const hospitals = day.hospitals as unknown as Hospital[];
  const shifts = day.shifts as any[];
  const { setAllocations, refetchDay } = day;
  const loading = day.coreLoading;
  // The board has always treated an allocation fetch that FAILED as "loaded, none" (with a console warning) so
  // the rest of the day still renders; only "not answered yet" counts as not loaded.
  const allocationsLoaded = !day.allocationsPending || day.allocationsError !== null;
  const [error, setError] = useState<string>('');
  const [allocating, setAllocating] = useState<boolean>(false);
  const [toast, setToast] = useState<{ type: 'success' | 'error' | 'warning' | 'info'; text: string } | null>(null);
  const [manualAllocations, setManualAllocations] = useState<ManualAllocation[]>([]);
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [selectedSchedule, setSelectedSchedule] = useState<OperatingRoomSchedule | null>(null);
  const [wiping, setWiping] = useState<boolean>(false);
  const [isViewModalOpen, setIsViewModalOpen] = useState<boolean>(false);
  const [isModifying, setIsModifying] = useState<boolean>(false);
  const [modifiedAllocations, setModifiedAllocations] = useState<Map<string, ManualAllocation>>(new Map());
  // Master time slider (minutes since midnight). Board shows 00:00–23:00 (full day in local time)
  // 07:00 is only the pre-load placeholder now. Once the day's cases arrive the bar parks on the
  // FIRST CASE START (see the effect below) -- 07:00 sat just before most sites open, so a simulation
  // opened reading "0 running" at any site whose first case began at 07:15 or later.
  const [targetMinutes, setTargetMinutes] = useState<number>(7 * 60);
  // Set the moment the user drags the time bar, and cleared when the date changes. Without it, every
  // reload after an allocation would yank the bar back to the morning mid-review.
  const userMovedBarRef = useRef(false);
  // "Live" mode: the blue time bar tracks the real current local time-of-day and advances
  // minute-by-minute (in production the board shows today, so this is "now"). Dragging the
  // slider switches to manual scrubbing; "Jump to now" resumes live tracking.
  // In SIMULATION mode (the local, no-AWS stack) the day is synthetic, so there is no
  // meaningful "now" — start with live tracking OFF, which leaves the blue bar parked on the day's
  // first case start (see the effect below). Deployed boards keep live "now" tracking.
  const [liveNow, setLiveNow] = useState<boolean>(!isLocalMode());
  const isLiveBar = liveNow;
  // "Who's free?" mode — grabbing the time bar parks it (glowing purple) and swaps the bullpen for a
  // time-scoped availability panel. Turned off by "Jump to now" / the panel's back button.
  const [whosFree, setWhosFree] = useState<boolean>(false);
  // Draw-case mode — a two-click add-on-case drawing tool that works without the global test-mode flag.
  const [drawMode, setDrawMode] = useState<boolean>(false);
  // Inline case naming: the just-drawn schedule whose name is being typed right on the block.
  const [namingScheduleId, setNamingScheduleId] = useState<string | null>(null);
  const [namingText, setNamingText] = useState<string>('');
  // When a freshly-created case's id isn't returned, match it after the reload to enter inline naming.
  const [pendingNameMatch, setPendingNameMatch] = useState<{ operatingRoomId: string; startUtc: string; endUtc: string } | null>(null);
  useEffect(() => {
    if (!liveNow || isLocalMode()) return; // never chase the wall clock in simulation mode
    const sync = () => {
      const now = new Date();
      const mins = now.getHours() * 60 + now.getMinutes();
      // Functional update returns the same value when the minute hasn't changed, so React
      // skips the re-render — the bar only moves when the clock actually ticks over a minute.
      setTargetMinutes(prev => (prev === mins ? prev : mins));
    };
    sync(); // snap to the current time immediately
    const id = window.setInterval(sync, 30000); // re-check twice a minute; harmless no-op between ticks
    return () => window.clearInterval(id);
  }, [liveNow]);
  // A new day deserves a fresh anchor, so a date change re-arms the auto-park.
  useEffect(() => { userMovedBarRef.current = false; }, [selectedDate]);
  // Park the bar on the day's FIRST CASE START once the cases land (Braden 2026-10-10). Simulation
  // board only: a deployed board's bar is the live clock, and must stay put when "Who's free?" turns
  // live tracking off. A user scrub wins until the date changes. See shouldAutoParkTimeBar.
  useEffect(() => {
    if (!shouldAutoParkTimeBar({ localMode: isLocalMode(), liveNow, userMovedBar: userMovedBarRef.current })) return;
    // Cases STARTING on selectedDate only -- last night's carried-in case would otherwise park at 00:00.
    const first = firstCaseStartOnDay(schedules, selectedDate);
    // No cases -> leave the 07:00 placeholder rather than parking at midnight.
    if (first !== null) setTargetMinutes(first);
  }, [schedules, liveNow, selectedDate]);
  // Test mode for creating schedules (controlled by environment variable)
  const testMode = isTestModeEnabled();
  const [scheduleSelection, setScheduleSelection] = useState<{
    operatingRoomId: string | null;
    startMinutes: number | null;
    endMinutes: number | null;
  }>({ operatingRoomId: null, startMinutes: null, endMinutes: null });
  const [isCreateScheduleModalOpen, setIsCreateScheduleModalOpen] = useState<boolean>(false);
  const [newScheduleData, setNewScheduleData] = useState<any>({});
  const [isCreatingShift, setIsCreatingShift] = useState<boolean>(false);
  const [allocationProgress, setAllocationProgress] = useState<string>('');
  const [isEditingSchedule, setIsEditingSchedule] = useState<boolean>(false);
  const [editSurgeryType, setEditSurgeryType] = useState<string>('');
  const [isUpdatingSchedule, setIsUpdatingSchedule] = useState<boolean>(false);
  const [isDeletingSchedule, setIsDeletingSchedule] = useState<boolean>(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState<boolean>(false);
  // SYMPHONY ONLY (Braden 2026-09-30): "Don't use greedy at all. We're only using symphony from this point
  // forward... you can actually take the button on the screen off where you can choose your algorithms."
  // So there is no picker and no stored preference — the board runs Symphony. The greedy V2 / V2-Morning /
  // CP-SAT orchestrators stay in the repo for reference (and remain reachable by a direct API call), but
  // nothing in the product selects them, and the `oc-board-algorithm` preference is no longer written.
  const algorithm = 'symphony' as const;
  const pollingIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  // Track scroll containers per hospital to auto-center on slider change
  const boardRefs = useRef<Record<string, HTMLDivElement | null>>({});
  // Mobile-specific state
  // LOCATION filter — which sites the board renders AND which sites the case-load totals cover. A Set,
  // so any combination of hospitals and surgery centres can be picked. EMPTY means "all locations", so a
  // fresh board and a cleared dropdown behave identically.
  const [selectedHospitalIds, setSelectedHospitalIds] = useState<ReadonlySet<string>>(loadSelectedHospitalIds);
  useEffect(() => { saveSelectedHospitalIds(selectedHospitalIds); }, [selectedHospitalIds]);
  // The saved filter outlives the sites it names (a deleted site, another account on this browser). An
  // unknown id would leave the board rendering NO sites, so drop unknown ids once hospitals load.
  useEffect(() => {
    setSelectedHospitalIds(prev => pruneUnknownHospitalIds(prev, hospitals.map(h => h.hospitalId)));
  }, [hospitals]);
  const [locationPickerOpen, setLocationPickerOpen] = useState(false);
  const siteSelected = useCallback(
    (hospitalId: string) => selectedHospitalIds.size === 0 || selectedHospitalIds.has(hospitalId),
    [selectedHospitalIds],
  );
  const [showShiftsCard, setShowShiftsCard] = useState<boolean>(false);
  // View mode: 'active' shows only rooms with schedules, 'all' shows all rooms
  const [viewMode, setViewMode] = useState<'active' | 'all'>(loadViewMode);
  useEffect(() => { saveViewMode(viewMode); }, [viewMode]);
  // Hospitals manually force-included into the active view (so empty ORs are surfaced for new schedule drawing)
  const [forcedActiveHospitalIds, setForcedActiveHospitalIds] = useState<Set<string>>(loadForcedHospitalIds);
  useEffect(() => { saveForcedHospitalIds(forcedActiveHospitalIds); }, [forcedActiveHospitalIds]);
  // Side bar open/collapsed state (Sites on the left, Bullpen on the right), remembered across
  // refreshes as undated board preferences -- see boardPrefs.ts.
  const [isSidebarOpen, setIsSidebarOpen] = useState<boolean>(loadSitesOpen);
  useEffect(() => { saveSitesOpen(isSidebarOpen); }, [isSidebarOpen]);
  const [isBullpenOpen, setIsBullpenOpen] = useState<boolean>(loadBullpenOpen);
  useEffect(() => { saveBullpenOpen(isBullpenOpen); }, [isBullpenOpen]);
  // ...and the width each one was dragged to (SidebarResizeHandle on the gutter beside it).
  const [sitesWidth, setSitesWidth] = useState<number>(loadSitesWidth);
  useEffect(() => { saveSitesWidth(sitesWidth); }, [sitesWidth]);
  const [bullpenWidth, setBullpenWidth] = useState<number>(loadBullpenWidth);
  useEffect(() => { saveBullpenWidth(bullpenWidth); }, [bullpenWidth]);
  const [isResizingSidebar, setIsResizingSidebar] = useState(false);
  // Dragging a bar wider stops where the board would drop below MIN_BOARD_WIDTH (boardPrefs.ts).
  const boardLayoutRef = useRef<HTMLDivElement>(null);
  const sitesRange = { ...SITES_WIDTH, max: maxSidebarWidth(SITES_WIDTH, boardLayoutRef.current?.clientWidth ?? 0, isBullpenOpen ? bullpenWidth : null) };
  const bullpenRange = { ...BULLPEN_WIDTH, max: maxSidebarWidth(BULLPEN_WIDTH, boardLayoutRef.current?.clientWidth ?? 0, isSidebarOpen ? sitesWidth : null) };
  // Which view is shown in the right shifts sidebar (only one at a time).
  const [shiftsView, setShiftsView] = useState<'available' | 'allocated' | 'all'>(loadShiftsView);
  useEffect(() => { saveShiftsView(shiftsView); }, [shiftsView]);

  // Generate hour labels from midnight to 11 PM (full day in local time)
  const timeSlots: string[] = [];
  for (let hour = 0; hour <= 23; hour++) {
    timeSlots.push(`${hour.toString().padStart(2, '0')}:00`);
  }

  const timelineWidthPx = timeSlots.length * 100; // 100px per hour

  // A new day starts with a clean slate, as the old per-date reload did (it cleared the error before fetching).
  useEffect(() => { setError(''); }, [selectedDate]);
  // The day's core data could not be fetched (and nothing is cached for it).
  useEffect(() => {
    if (!day.coreError) return;
    setError('Failed to load data');
    console.error('Error loading data:', day.coreError);
  }, [day.coreError]);
  useEffect(() => {
    if (day.allocationsError) console.warn('Could not load allocation data:', day.allocationsError);
  }, [day.allocationsError]);

  // Cleanup polling on unmount
  useEffect(() => {
    return () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
      }
    };
  }, []);

  // Diagnostic: Check for orphaned schedules (schedules without matching operating rooms).
  // Gated on `!loading && allocationsLoaded` so it never fires while the day's data is still arriving.
  useEffect(() => {
    if (loading || !allocationsLoaded) return;
    if (schedules.length === 0 || operatingRooms.length === 0) return;

    const orphanedSchedules = schedules.filter(schedule => {
      const room = operatingRooms.find(r => r.operatingRoomId === schedule.operatingRoomId);
      return !room;
    });

    if (orphanedSchedules.length > 0) {
      console.warn('⚠️ Orphaned schedules detected (schedules reference missing operating rooms):', orphanedSchedules);
      console.warn('Operating Room IDs missing:', [...new Set(orphanedSchedules.map(s => s.operatingRoomId))]);
      setError(`Warning: ${orphanedSchedules.length} schedule(s) reference operating rooms that are not in the system. These schedules are not visible on the board.`);
    }
  }, [schedules, operatingRooms, loading, allocationsLoaded]);

  /** Revalidate the day on screen now (after a write whose effect the board must show). Clears a stale error. */
  const reloadDay = async () => {
    setError('');
    await refetchDay();
  };

  const getEmployeeName = (employeeId: string): string => {
    const employee = employees.find(emp => emp.employeeId === employeeId);
    return employee ? `${employee.firstName} ${employee.lastName}` : employeeId;
  };

  const getHospitalName = (hospitalId: string): string => {
    if (hospitalId === 'missing-hospital') {
      return '⚠️ Missing Operating Rooms (Data Integrity Issue)';
    }
    const hospital = hospitals.find(hosp => hosp.hospitalId === hospitalId);
    return hospital ? hospital.name : hospitalId;
  };

  // Each hospital's Board color (stored on its card, or a stable default from the id).
  const getHospitalColor = (hospitalId: string): string => {
    if (hospitalId === 'missing-hospital') return '#7a8699';
    return colorForHospital(hospitals.find(hosp => hosp.hospitalId === hospitalId));
  };

  const getOperatingRoomName = (operatingRoomId: string): string => {
    const room = operatingRooms.find(room => room.operatingRoomId === operatingRoomId);
    return room ? room.name : operatingRoomId;
  };

  const getOperatingRoomCredential = (operatingRoomId: string): string | null => {
    const room = operatingRooms.find(room => room.operatingRoomId === operatingRoomId);
    return room ? (room.credential || null) : null;
  };

  const getOperatingRoomHospitalId = (operatingRoomId: string): string | null => {
    const room = operatingRooms.find(room => room.operatingRoomId === operatingRoomId);
    return room ? room.hospitalId : null;
  };

  const getSchedulesForRoom = (operatingRoomId: string): OperatingRoomSchedule[] => {
    return schedules
      .filter(s => s.operatingRoomId === operatingRoomId)
      .sort((a, b) => new Date(a.startTimeUtc).getTime() - new Date(b.startTimeUtc).getTime());
  };

  // B5: every board read of "a person's shift today" resolves to their EARLIEST shift (earliestShiftByEmployee),
  // so nameplate colors, window labels, the bullpen, and the provider card all agree when someone has two
  // overlapping shift rows (a carried-in night shift + today's day shift). Previously getEmployeeShift returned
  // the API's FIRST match and shiftByEmployeeId the LAST, so the same person could read differently in different places.
  const earliestShiftByEmp = earliestShiftByEmployee<any>(shifts);
  const getEmployeeShift = (employeeId: string) => earliestShiftByEmp.get(employeeId);

  const formatTime = (timeUtc: string): string => {
    return utcToLocalTime(timeUtc);
  };

  // Helper function to determine if allocations have been run for the day
  const hasPersistedAllocations = () => {
    return allocationsLoaded && allocations.length > 0;
  };
  // NEED MD / NEED CRNA are prompts about what the ALGORITHM left uncovered, so they appear only once it has run
  // (every allocation it saves carries an `algorithm` tag; a hand-placed or pinned one does not). Braden 2026-09-16:
  // "if I pin something don't automatically turn every other slot as a need md or need crna. Just let the nameplate
  // be pinned and let everything else stay gray." A manual mark (slotMarks) still forces NEED or NONE either way.
  const hasAlgorithmAllocations = () => allocationsLoaded && allocations.some((a: any) => !!a.algorithm);

  // Helper function to determine card state
  const getCardState = (schedule: OperatingRoomSchedule) => {
    const allocation = allocations.find(a => a.operatingRoomScheduleId === schedule.scheduleId) || null;
    const manualAllocation = manualAllocations.find(ma => ma.scheduleId === schedule.scheduleId) || null;
    
    // Rule lives in boardViewHelpers.boardCardState so it can be unit-tested: staffing drives the colour,
    // the pin is drawn as a corner dot instead of recolouring the block. Coverage is minute by minute, so one block
    // can stripe green and red; the class follows the case's first minute and the stripes carry the rest.
    const segments = caseCoverage(schedule, manualAllocation, allocation);
    const state = boardCardState({ hasPersistedAllocations: hasPersistedAllocations(), manualAllocation, allocation, covered: segments[0]?.covered ?? true });
    const hasDoctor = ((manualAllocation || allocation)?.allocatedDoctorIds?.length || 0) > 0;
    const splitBackground = (state === 'success' || state === 'incomplete') && hasDoctor ? coverageSegmentsBackground(segments) : undefined;
    return { state, splitBackground };
  };

  // Set (true/false) or clear (null) one flag on a case. Local, per-day; never touches an allocation.
  const setCaseFlag = (scheduleId: string, kind: CaseMarkerKind, value: boolean | null) => {
    setCaseMarkers(setCaseMarker(selectedDate, scheduleId, kind, value));
  };

  const handleScheduleClick = (schedule: OperatingRoomSchedule) => {
    setSelectedSchedule(schedule);
    // Non-admins (read-only users) must never reach the editable/pin manual-allocation
    // modal. They can still VIEW allocation details when allocations exist; if no
    // allocations exist yet there is nothing read-only to show, so this is a no-op.
    if (!isAdmin) {
      if (hasPersistedAllocations()) {
        setIsViewModalOpen(true);
      } else {
        setSelectedSchedule(null);
      }
      return;
    }
    if (isModifying) {
      setIsModalOpen(true);
    } else if (!hasPersistedAllocations()) {
      // Pre-run pin flow: no allocations exist yet, so clicking an (empty)
      // schedule opens the manual-allocation modal in pin mode (isModifying=false).
      // Pins are held in memory until the user runs the allocation algorithm.
      setIsModalOpen(true);
    } else {
      // Board already allocated. Clicking any schedule — allocated OR not (e.g. a freshly drawn
      // add-on case) — opens the details view, which offers Edit Case Name / Delete plus an opt-in
      // "Modify Allocation" / "Pin & re-run". We no longer force the pin modal (and its list of who
      // is un/available at this time) onto an unallocated schedule.
      setIsViewModalOpen(true);
    }
  };

  const handleModifyClick = () => {
    if (!selectedSchedule) return;
    
    // Convert persisted allocation to manual allocation for editing
    const allocation = allocations.find(a => a.operatingRoomScheduleId === selectedSchedule.scheduleId);
    if (allocation) {
      const manualAlloc: ManualAllocation = {
        scheduleId: selectedSchedule.scheduleId,
        allocatedNurseIds: allocation.allocatedNurseIds || [],
        allocatedDoctorIds: allocation.allocatedDoctorIds || []
      };
      
      // Add to modified allocations
      setModifiedAllocations(prev => {
        const newMap = new Map(prev);
        newMap.set(selectedSchedule.scheduleId, manualAlloc);
        return newMap;
      });
    }
    
    // Switch to modification mode and open manual allocation modal
    setIsModifying(true);
    setIsViewModalOpen(false);
    setIsModalOpen(true);
  };

  // "Pin & re-run" entry from the view modal: open the pin editor (pre-run, NOT
  // modify mode) for an already-allocated schedule. The modal pre-fills from the
  // current allocation via getPinModalPrefill; on Save the pin lands in the
  // in-memory manualAllocations and the next Allocate re-runs with it kept fixed.
  const handlePinAndRerunClick = () => {
    setIsViewModalOpen(false);
    setIsModalOpen(true);
  };

  // Prefill source for the pin modal. In modify mode it reads the modify map; in the
  // pre-run pin flow it reads an already-staged in-memory pin, falling back to the
  // schedule's current persisted allocation so "Pin & re-run" starts from the staff
  // already on the board. (Logic lives in resolvePinPrefill so it can be unit tested.)
  const getPinModalPrefill = (): ManualAllocation | undefined =>
    resolvePinPrefill({
      scheduleId: selectedSchedule?.scheduleId,
      isModifying,
      modifiedAllocations,
      manualAllocations,
      persistedAllocations: allocations,
    });

  const handleClearPins = () => {
    setManualAllocations([]);
  };

  const handleModalClose = () => {
    // If the user was in modify mode, discard any in-progress modifications so the board
    // reverts to its original (still-allocated) state. The Save path runs API persistence
    // and onReloadAllocations() before this close, so clearing the local map here is also
    // correct on save — by then the persisted state is the authoritative source.
    const cleanup = computeStateAfterModalClose({ isModifying });
    if (cleanup) {
      setModifiedAllocations(cleanup.modifiedAllocations as Map<string, ManualAllocation>);
      setIsModifying(cleanup.isModifying);
    }
    setIsModalOpen(false);
    setSelectedSchedule(null);
  };

  const handleViewModalClose = () => {
    setIsViewModalOpen(false);
    setSelectedSchedule(null);
    setIsEditingSchedule(false);
    setEditSurgeryType('');
    setShowDeleteConfirm(false);
  };

  const handleEditScheduleClick = () => {
    if (!selectedSchedule) return;
    setEditSurgeryType(selectedSchedule.surgeryType);
    setIsEditingSchedule(true);
  };

  const handleCancelEditSchedule = () => {
    setIsEditingSchedule(false);
    setEditSurgeryType('');
  };

  const handleSaveScheduleEdit = async () => {
    if (!selectedSchedule || !editSurgeryType.trim()) return;
    setIsUpdatingSchedule(true);
    try {
      await updateOperatingRoomSchedule(selectedSchedule.scheduleId, { surgeryType: editSurgeryType.trim() });
      setToast({ type: 'success', text: 'Schedule updated successfully' });
      handleViewModalClose();
      await reloadDay();
    } catch (error: any) {
      setToast({ type: 'error', text: error.response?.data?.error || 'Failed to update schedule' });
    } finally {
      setIsUpdatingSchedule(false);
    }
  };

  const handleDeleteSchedule = async () => {
    if (!selectedSchedule) return;
    setIsDeletingSchedule(true);
    try {
      await deleteOperatingRoomSchedule(selectedSchedule.scheduleId);
      setToast({ type: 'success', text: 'Schedule deleted successfully' });
      handleViewModalClose();
      await reloadDay();
    } catch (error: any) {
      setToast({ type: 'error', text: error.response?.data?.error || 'Failed to delete schedule' });
    } finally {
      setIsDeletingSchedule(false);
    }
  };

  const handleManualAllocationSave = (allocation: ManualAllocation) => {
    if (isModifying) {
      // Update modified allocations
      setModifiedAllocations(prev => {
        const newMap = new Map(prev);
        newMap.set(allocation.scheduleId, allocation);
        return newMap;
      });
    } else {
      // Original behavior for pre-allocation manual assignments
      setManualAllocations(prev => {
        const existing = prev.find(ma => ma.scheduleId === allocation.scheduleId);
        if (existing) {
          return prev.map(ma => ma.scheduleId === allocation.scheduleId ? allocation : ma);
        } else {
          return [...prev, allocation];
        }
      });
    }
  };

  const handleWipeAllocations = async () => {
    if (!selectedDate) return;
    if (!window.confirm(`Are you sure you want to delete all allocations for ${selectedDate}? This action cannot be undone.`)) {
      return;
    }
    setWiping(true);
    setError('');
    try {
      const result = await wipeAllocations(selectedDate, localDateToUtcStart(selectedDate), localDateToUtcEnd(selectedDate));
      // A wipe = reset to bullpen. Clear the per-day board overlays too, so stale names don't linger — ESPECIALLY
      // float CRNAs, which are browser-only and MERGE on re-allocate (so they'd otherwise survive both the wipe and
      // the next allocation). Re-allocating then refills the float slots fresh. Same overlays a fresh sim clears;
      // the move-log, Off/Meeting dispositions, and site-locks (person-level) are intentionally kept.
      clearFloatCrnas(selectedDate); setFloatCrnasState(loadFloatCrnas(selectedDate));
      clearRoomTransfers(selectedDate); setRoomTransfers(loadRoomTransfers(selectedDate));
      clearNurseTransfers(selectedDate); setNurseTransfers(loadNurseTransfers(selectedDate));
      clearSlotMarks(selectedDate); setSlotMarks(loadSlotMarks(selectedDate));
      // PINS DIE WITH THE DAY (Braden 2026-09-20): "if I pin something, leave it there for that run. If I
      // wipe the run, don't leave the pins. I would have to re-pin." Pins are in-memory state, so without this
      // they survived the wipe INVISIBLY — the pin dot needs hasPersistedAllocations(), which is false after a
      // wipe — and were then silently reapplied by the next Allocate.
      setManualAllocations([]);
      // The Undo stack holds ABSOLUTE prior doctor/nurse ids from the allocation we just deleted. Replaying one
      // after a wipe would write a superseded assignment onto a rebuilt day, so the history dies with the day.
      clearUndo(selectedDate); setUndoStack(loadUndoStack(selectedDate));
      setToast({ type: 'success', text: result.message });
      await reloadDay();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to wipe allocations');
      console.error('Error wiping allocations:', err);
    } finally {
      setWiping(false);
    }
  };


  const pollForCompletion = (jobId: string, isModifyFlow: boolean) => {
    setAllocationProgress('Solver is running...');
    let pollCount = 0;
    const maxPolls = 80; // ~4 minutes at 3s intervals
    // The run belongs to the date it was started on. The job can outlast the runner's stay on that date, so every
    // write below targets runDate explicitly, and on-screen state is only touched while runDate is still shown
    // (boardViewHelpers.planAllocationCompletion).
    const runDate = selectedDate;
    const runRoomCasesOf = roomCasesOf;

    pollingIntervalRef.current = setInterval(async () => {
      pollCount++;
      try {
        const jobStatus = await getAllocationJobStatus(jobId);

        if (jobStatus.status === 'COMPLETED') {
          if (pollingIntervalRef.current) clearInterval(pollingIntervalRef.current);
          pollingIntervalRef.current = null;

          if (jobStatus.result?.success) {
            const plan = planAllocationCompletion({ runDate, currentDate: selectedDateRef.current, isModifyFlow });
            const showing = () => planAllocationCompletion({ runDate, currentDate: selectedDateRef.current, isModifyFlow }).updateDisplayedDay;
            setToast({ type: 'success', text: plan.toast });
            if (isModifyFlow) {
              setModifiedAllocations(new Map());
              setIsModifying(false);
            } else {
              setManualAllocations([]);
            }
            // Refresh the RUN date's cached data (the allocator rewrote its allocations). If it is the day on
            // screen it re-renders; if not, the cache is simply fresh when the runner goes back to it.
            await Promise.all([
              queryClient.invalidateQueries({ queryKey: queryKeys.shiftsForDay(plan.refreshDate) }),
              queryClient.invalidateQueries({ queryKey: queryKeys.schedulesForDay(plan.refreshDate) }),
              queryClient.invalidateQueries({ queryKey: queryKeys.allocationsForDay(plan.refreshDate) }),
            ]);
            // Float CRNAs (Phase 2): Symphony returns which CRNAs it assigned to each site's float slots (the
            // runner's pre-assigned ones are included). Write them into the per-day float overlay so the board's
            // float boxes show the auto-filled picks alongside anything hand-assigned. Only on a fresh run.
            if (plan.rebuildOverlays) {
              // Re-allocate REFRESHES the float slots (Braden: floats fresh on each re-allocate) — clear the prior
              // float overlay FIRST, then write Symphony's new picks, instead of merging stale names into it.
              // Symphony's floatCrnaAssignments already re-includes any manual pre-assignments (passed in as
              // floatPreassignments and honored), so nothing hand-placed is lost by the clear.
              clearFloatCrnas(runDate);
              // A fresh allocation REPLACES the day, so the prior Undo entries now describe assignments that no
              // longer exist. Keeping them would let one click write a superseded doctor back onto a new plan.
              clearUndo(runDate);
              const floats = (jobStatus.result as any)?.floatCrnaAssignments as { hospitalId: string; crnaIds: string[] }[] | undefined;
              if (Array.isArray(floats)) for (const f of floats) applyFloatAssignments(runDate, f.hospitalId, f.crnaIds || []);
              // An OB room with nothing scheduled keeps a CRNA for an add-on C-section: Symphony returns it keyed by
              // room (there is no case to allocate against), and the board paints it on that room's plate.
              clearObHold(runDate);
              const obHolds = (jobStatus.result as any)?.obHoldCrnas as { operatingRoomId: string; crnaIds: string[] }[] | undefined;
              if (Array.isArray(obHolds)) for (const h of obHolds) applyObHold(runDate, h.operatingRoomId, h.crnaIds || []);
              // A room Symphony could not staff because NOBODY WORKING is credentialed for it reads NO CRED, not
              // NEED MD — the fix is credentials, not another phone call (Braden 2026-09-18).
              const noCredForRun = applyNoCred(runDate, (jobStatus.result as any)?.unstaffedRooms);
              // Symphony's handoffs PARTWAY THROUGH a case (Braden 2026-09-14): the reliever goes in that case's relief box
              // with the handoff time, replacing the previous run's handoffs. Reliefs placed by hand stay. Read and
              // written for runDate from storage (not from on-screen state, which may be another day's).
              const handoffs = (jobStatus.result as any)?.reliefHandoffs as ReliefHandoff[] | undefined;
              const reliefForRun = applyReliefHandoffs(loadReliefOverlay(runDate), Array.isArray(handoffs) ? handoffs : [], runRoomCasesOf);
              saveReliefOverlay(runDate, reliefForRun);
              // Mirror onto the screen only while the run's day is the one shown; otherwise the per-date reload
              // effects pick all of this up from storage when the runner returns to runDate.
              if (showing()) {
                setUndoStack(loadUndoStack(runDate));
                setFloatCrnasState(loadFloatCrnas(runDate));
                setObHold(loadObHold(runDate));
                setNoCred(noCredForRun);
                setReliefOverlay(reliefForRun);
              }
            }
            // (grading) Snapshot what the algorithm just produced as the BASELINE for this day — the reference
            // the runner's later manual edits are graded against. A fresh baseline resets the day, so clear the
            // local move log too. Only on a fresh run, never a modify pass. Best-effort: never block the board.
            if (plan.captureBaseline) {
              try {
                const sd = localDateToUtcStart(runDate);
                const ed = localDateToUtcEnd(runDate);
                const fresh = await searchAllocationsByWindow(sd, ed);
                await saveGradingBaseline(runDate, algorithm, fresh.allocations || []);
                const clearedLog = clearMoveLog(runDate);
                if (showing()) setMoveLog(clearedLog);
              } catch (e) { console.error('grading baseline capture failed', e); }
            }
          } else {
            setError(jobStatus.result?.errors?.[0] || 'Allocation completed but produced no results');
          }
          setAllocating(false);
          setAllocationProgress('');
        } else if (jobStatus.status === 'FAILED') {
          if (pollingIntervalRef.current) clearInterval(pollingIntervalRef.current);
          pollingIntervalRef.current = null;
          setError(jobStatus.error || 'Allocation job failed');
          setAllocating(false);
          setAllocationProgress('');
        } else if (pollCount >= maxPolls) {
          if (pollingIntervalRef.current) clearInterval(pollingIntervalRef.current);
          pollingIntervalRef.current = null;
          setError('Allocation timed out. The solver may still be running - try refreshing the page.');
          setAllocating(false);
          setAllocationProgress('');
        }
      } catch (err) {
        console.error('Error polling job status:', err);
        // Don't stop polling on transient errors
      }
    }, 3000);
  };

  const handleAllocate = async () => {
    if (!selectedDate) return;
    // Pin-on-top re-run: the board already has allocations and the user staged pins
    // (pre-run flow, not modify). Re-running keeps the pins and recomputes the rest,
    // replacing the current board — confirm before discarding the old allocations.
    if (!isModifying && hasPersistedAllocations() && manualAllocations.length > 0) {
      const ok = window.confirm(
        'This re-runs the allocation algorithm and REPLACES the current board.\n\n' +
        'Your pinned schedules are kept exactly as set; every other schedule is recomputed. Continue?'
      );
      if (!ok) return;
    }
    setAllocating(true);
    setError('');
    setAllocationProgress('Starting Symphony (site-driven ratio staffing)...');
    try {
      const startDateTime = localDateToUtcStart(selectedDate);
      const endDateTime = localDateToUtcEnd(selectedDate);

      let manualAllocsToSend: ManualAllocation[] = manualAllocations;

      // If modifying, we need to wipe existing allocations first, then reallocate
      if (isModifying && modifiedAllocations.size > 0) {
        await wipeAllocations(selectedDate, startDateTime, endDateTime);
        manualAllocsToSend = Array.from(modifiedAllocations.values());
      }

      const { jobId } = await startAllocation({
        startTimeUtc: startDateTime,
        endTimeUtc: endDateTime,
        manualAllocations: manualAllocsToSend,
        algorithm,
        // Hard site locks set on the Anesthesiologists / CRNA tabs (per day): confine those providers to their
        // chosen site. Only Symphony honors them; harmless to other algorithms (they ignore the field).
        siteLocks: loadSiteLocks(selectedDate),
        // Float CRNAs the runner pre-assigned on the board (hospitalId -> CRNA ids). Symphony holds these out of
        // rooms and fills the remaining float slots itself; other algorithms ignore the field.
        floatPreassignments: (() => {
          const fc = loadFloatCrnas(selectedDate);
          const out: Record<string, string[]> = {};
          for (const [hid, arr] of Object.entries(fc)) { const ids = arr.filter((x): x is string => !!x); if (ids.length) out[hid] = ids; }
          return out;
        })(),
        // Per-day float SLOT COUNTS (hospitalId -> count) for THIS date, so Symphony auto-fills the right number
        // of floats for the day being allocated — never another day's. Sent as the larger of the configured
        // count and however many the board actually shows (a drag-created slot bumps the overlay length), so the
        // backend fills exactly what the runner sees. Replaces the old global Hospital.floatCrnaSlots read.
        floatSlotsByHospital: (() => {
          const cfg = loadFloatSlots(selectedDate);
          const fc = loadFloatCrnas(selectedDate);
          const out: Record<string, number> = {};
          for (const hid of new Set([...Object.keys(cfg), ...Object.keys(fc)])) {
            const n = Math.max(cfg[hid] || 0, (fc[hid] || []).length);
            if (n > 0) out[hid] = n;
          }
          return out;
        })(),
        // Providers the runner marked "Off" (sent home) — Symphony treats them as HARD-unavailable, so a re-run
        // never re-staffs someone the board shows as sent home. Read fresh from the shared oc-anes disposition store.
        offEmployeeIds: (() => {
          try {
            const raw = localStorage.getItem(`oc-anes-${selectedDate}`);
            const p = raw ? JSON.parse(raw) : {};
            return Object.keys(p.status || {}).filter((id: string) => normalizeDoctorStatuses(p.status[id]).includes('off'));
          } catch { return []; }
        })(),
        // Cases flagged pediatric on the board (the ⚑ marker). Manual-only — we deliberately don't
        // collect or persist patient age from customer data (see boardViewHelpers.effectiveCaseFlags),
        // so there is no auto-detection fallback here.
        // Symphony prefers a pediatric anesthesiologist credentialed there for a flagged room as a TIEBREAK
        // under the pecking order (never a jump ahead of it). The flag does not change the supervision ratio:
        // 1:3 applies only at a pediatric site (Braden 2026-09-13). See SYMPHONY_PEDIATRIC_CASE_MARKER_ANSWERS.md.
        // Only Symphony reads this field.
        pediatricScheduleIds: (() => {
          const marks = loadCaseMarkers(selectedDate);
          return schedules.filter(s => effectiveCaseFlags(s, marks).pediatric.value).map(s => s.scheduleId);
        })(),
        // The pecking order as the runner has it on the Anesthesiologists tab (drag-reordered numbers win over the shift
        // data), so Symphony's waterfall follows the order the board shows. Only Symphony reads this field.
        peckingOrderOverride,
        // Send the DST-correct US Central offset for the board's selected date. Cases and shifts are
        // GENERATED in Central (simulate pins to America/Chicago) and the hospitals operate in Central, so
        // the backend's early-morning rules must reason in Central too — NOT the browser's own timezone.
        // Using the browser offset here made allocation disagree with the generated data on any non-Central
        // machine (shifting which cases count as "early morning"). This mirrors simulateDay's offset source.
        timezone: getCentralOffsetStringForDate(selectedDate),
      });

      pollForCompletion(jobId, isModifying && modifiedAllocations.size > 0);

    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to start allocation');
      console.error('Error starting allocation:', err);
      setAllocating(false);
      setAllocationProgress('');
    }
  };

  // Publish the current day for grading: freeze the edited board + the move log as a snapshot to grade
  // side-by-side against the Symphony baseline captured when Allocate was last run. Re-publishing overwrites.
  const handlePublishDay = async () => {
    if (!selectedDate) return;
    const ok = window.confirm(
      `Publish ${selectedDate} for grading?\n\n` +
      `This freezes the current board (${allocations.length} allocations) and your ${moveLog.length} logged move${moveLog.length === 1 ? '' : 's'} as a snapshot to grade side-by-side against Symphony. You can keep editing afterward — the snapshot won't change until you publish again.`
    );
    if (!ok) return;
    setPublishing(true);
    try {
      const res = await publishGradingDay(selectedDate, allocations, moveLog);
      setToast({ type: 'success', text: res?.hasBaseline
        ? `Published ${selectedDate} for grading — ${res.moveCount} move${res.moveCount === 1 ? '' : 's'}, ready to grade vs Symphony.`
        : `Published ${selectedDate}, but no Symphony baseline was captured — re-run Symphony first for a true side-by-side.` });
    } catch (e: any) {
      setError(e?.response?.data?.error || 'Failed to publish day for grading');
    } finally {
      setPublishing(false);
    }
  };

  // Group operating rooms by hospital
  const operatingRoomsByHospital = operatingRooms.reduce((acc, room) => {
    if (!acc[room.hospitalId]) {
      acc[room.hospitalId] = [];
    }
    acc[room.hospitalId].push(room);
    return acc;
  }, {} as Record<string, OperatingRoom[]>);

  // Diagnostic: Create phantom operating rooms for orphaned schedules
  const orphanedSchedules = schedules.filter(schedule => {
    const room = operatingRooms.find(r => r.operatingRoomId === schedule.operatingRoomId);
    return !room;
  });

  if (orphanedSchedules.length > 0) {
    const orphanedRoomIds = new Set(orphanedSchedules.map(s => s.operatingRoomId));
    orphanedRoomIds.forEach(roomId => {
      // Create a phantom operating room for missing rooms
      const phantomRoom: OperatingRoom = {
        operatingRoomId: roomId,
        roomNumber: 'MISSING',
        name: `⚠️ Missing OR (${roomId.substring(0, 8)}...)`,
        hospitalId: 'missing-hospital'
      };
      
      if (!operatingRoomsByHospital['missing-hospital']) {
        operatingRoomsByHospital['missing-hospital'] = [];
      }
      operatingRoomsByHospital['missing-hospital'].push(phantomRoom);
    });
  }

  // Compute left position (in px) for the vertical indicator within the scrollable content
  // Layout: first column is 220px, then 24 time columns each 100px wide (full day).
  // The column widths come from boardViewHelpers so this stays in lockstep with the board table.
  const getIndicatorLeftPx = useCallback((minutes: number) => {
    const minMinutes = 0; // 00:00 (midnight local time)
    const maxMinutes = 24 * 60; // 24:00 (end of day)
    const clamped = Math.max(minMinutes, Math.min(maxMinutes, minutes));
    const hourColumnsPx = (clamped / 60) * BOARD_HOUR_COLUMN_PX;
    const left = BOARD_OR_COLUMN_PX + hourColumnsPx; // include operating room column width
    return left;
  }, []);

  const formatSliderTime = (minutes: number) => {
    const h = Math.floor(minutes / 60);
    const m = minutes % 60;
    return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
  };

  const setBoardRef = (hospitalId: string) => (el: HTMLDivElement | null) => {
    boardRefs.current[hospitalId] = el;
  };

  const autoCenterOnTime = useCallback((minutes: number) => {
    const left = getIndicatorLeftPx(minutes);
    Object.values(boardRefs.current).forEach((container) => {
      if (!container) return;
      const viewportWidth = container.clientWidth;
      const targetScrollLeft = Math.max(0, left - viewportWidth / 2);
      container.scrollTo({ left: targetScrollLeft, behavior: 'smooth' });
    });
  }, [getIndicatorLeftPx]);

  const onSliderChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = Number(e.target.value);
    setLiveNow(false); // manual scrub — stop tracking the live clock
    userMovedBarRef.current = true;   // a scrub wins over the auto-park until the date changes
    setTargetMinutes(value);
    autoCenterOnTime(value);
  };

  // Leave "Who's free?" mode: return to the bullpen, and (in production) resume live "now" tracking.
  const exitWhosFree = () => {
    setWhosFree(false);
    if (!isLocalMode()) setLiveNow(true);
  };

  // Enter "Who's free?" mode from the master time slider's purple pill: stop live tracking, swap the
  // bullpen for the availability panel, and let the existing slider scrub the target time. This replaced
  // the old board-bar grab knob, whose per-scroll repositioning (top = 42px + boardScrollTop) forced a
  // full BoardView re-render on every scroll tick — the source of the scroll lag.
  const enterWhosFree = () => {
    setLiveNow(false);
    setWhosFree(true);
    setIsBullpenOpen(true);
    setShiftsView('available');
  };

  // Initial board scroll: position the time indicator (default 07:00) near the left so the
  // first cases of the day are visible to the right, instead of opening at 00:00 (empty morning).
  const scrollBoardsToTime = useCallback((minutes: number) => {
    const left = getIndicatorLeftPx(minutes); // includes the 220px sticky OR column
    Object.values(boardRefs.current).forEach((container) => {
      if (!container) return;
      // Put the indicator ~15% in from the left edge of the timeline area.
      const targetScrollLeft = Math.max(0, left - 220 - container.clientWidth * 0.15);
      container.scrollTo({ left: targetScrollLeft });
    });
  }, [getIndicatorLeftPx]);

  // Re-run the initial scroll whenever a fresh day of data finishes loading (not on slider drags).
  const targetMinutesRef = useRef(targetMinutes);
  targetMinutesRef.current = targetMinutes;
  useEffect(() => {
    if (loading) return;
    const t = setTimeout(() => scrollBoardsToTime(targetMinutesRef.current), 80);
    return () => clearTimeout(t);
  }, [loading, selectedDate, schedules.length, scrollBoardsToTime]);

  const handleTimelineClick = (operatingRoomId: string, clickedMinutes: number) => {
    if (!(testMode || drawMode) || !isAdmin) return;

    // If this is a new selection or different OR
    if (!scheduleSelection.operatingRoomId || scheduleSelection.operatingRoomId !== operatingRoomId) {
      setScheduleSelection({
        operatingRoomId,
        startMinutes: clickedMinutes,
        endMinutes: null
      });
    } else if (scheduleSelection.startMinutes !== null && scheduleSelection.endMinutes === null) {
      // Second click - complete the range
      const start = Math.min(scheduleSelection.startMinutes, clickedMinutes);
      const end = Math.max(scheduleSelection.startMinutes, clickedMinutes);
      if (end - start < 5) return; // ignore an accidental double-click on (almost) the same spot

      // Draw tool: create the case now and let the user type its name right on the block.
      // Legacy test mode keeps the old fill-out-a-form modal.
      if (drawMode) {
        createCaseInline(operatingRoomId, start, end);
      } else {
        setScheduleSelection({ operatingRoomId, startMinutes: start, endMinutes: end });
        openCreateScheduleModal(operatingRoomId, start, end);
      }
    }
  };

  const openCreateScheduleModal = (operatingRoomId: string, startMinutes: number, endMinutes: number) => {
    if (!selectedDate) return;
    
    // Convert minutes since midnight to local datetime string
    const startHours = Math.floor(startMinutes / 60);
    const startMins = startMinutes % 60;
    const endHours = Math.floor(endMinutes / 60);
    const endMins = endMinutes % 60;
    
    const startLocal = `${selectedDate}T${String(startHours).padStart(2, '0')}:${String(startMins).padStart(2, '0')}:00`;
    const endLocal = `${selectedDate}T${String(endHours).padStart(2, '0')}:${String(endMins).padStart(2, '0')}:00`;
    
    // Store both local display string and UTC for API call
    setNewScheduleData({
      operatingRoomId,
      startTimeLocal: startLocal,
      endTimeLocal: endLocal,
      surgeryType: 'New Surgery'
    });
    
    setIsCreateScheduleModalOpen(true);
  };

  const handleCreateSchedule = async () => {
    try {
      // Convert local times to UTC for the API call
      const startUtc = new Date(newScheduleData.startTimeLocal).toISOString();
      const endUtc = new Date(newScheduleData.endTimeLocal).toISOString();
      
      await createOperatingRoomSchedule({
        operatingRoomId: newScheduleData.operatingRoomId,
        startTimeUtc: startUtc,
        endTimeUtc: endUtc,
        surgeryType: newScheduleData.surgeryType
      });
      
      setIsCreateScheduleModalOpen(false);
      setScheduleSelection({ operatingRoomId: null, startMinutes: null, endMinutes: null });
      // Reload data to show new schedule (silent to preserve scroll position)
      await reloadDay();
      setToast({ type: 'success', text: 'Schedule created successfully!' });
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to create schedule');
      console.error('Error creating schedule:', err);
    }
  };

  const cancelScheduleCreation = () => {
    setIsCreateScheduleModalOpen(false);
    setIsCreatingShift(false);
    setScheduleSelection({ operatingRoomId: null, startMinutes: null, endMinutes: null });
  };

  // Draw-tool second click: create the add-on case immediately (no modal), then drop straight into
  // inline naming on the new block. Times use the same local→UTC conversion as the create modal so a
  // drawn case lands exactly where it was drawn. Name defaults to "New case" until the user types one.
  const createCaseInline = async (operatingRoomId: string, startMinutes: number, endMinutes: number) => {
    if (!selectedDate) return;
    const fmt = (mins: number) => `${selectedDate}T${String(Math.floor(mins / 60)).padStart(2, '0')}:${String(mins % 60).padStart(2, '0')}:00`;
    const startUtc = new Date(fmt(startMinutes)).toISOString();
    const endUtc = new Date(fmt(endMinutes)).toISOString();
    setScheduleSelection({ operatingRoomId: null, startMinutes: null, endMinutes: null });
    try {
      const created: any = await createOperatingRoomSchedule({ operatingRoomId, startTimeUtc: startUtc, endTimeUtc: endUtc, surgeryType: 'New case' });
      await reloadDay();
      const newId = created?.scheduleId || created?.schedule?.scheduleId;
      if (newId) { setNamingScheduleId(newId); setNamingText(''); }
      else { setPendingNameMatch({ operatingRoomId, startUtc, endUtc }); } // resolve after reload (effect below)
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to create case');
    }
  };

  // Save the inline case name (Enter/blur). Empty stays "New case". Guarded so blur-after-Enter no-ops.
  const commitCaseName = async () => {
    const id = namingScheduleId;
    if (!id) return;
    const name = namingText.trim() || 'New case';
    setNamingScheduleId(null);
    setNamingText('');
    try {
      await updateOperatingRoomSchedule(id, { surgeryType: name });
      await reloadDay();
    } catch (err: any) {
      setToast({ type: 'error', text: err.response?.data?.error || 'Failed to name case' });
    }
  };
  const cancelCaseNaming = () => { setNamingScheduleId(null); setNamingText(''); };

  // After a drawn case reloads, find it (room + start/end within a minute) and enter inline naming.
  useEffect(() => {
    if (!pendingNameMatch) return;
    const m = pendingNameMatch;
    const match = schedules.find(s => s.operatingRoomId === m.operatingRoomId
      && Math.abs(new Date(s.startTimeUtc).getTime() - new Date(m.startUtc).getTime()) < 60000
      && Math.abs(new Date(s.endTimeUtc).getTime() - new Date(m.endUtc).getTime()) < 60000);
    if (match) { setNamingScheduleId(match.scheduleId); setNamingText(''); setPendingNameMatch(null); }
  }, [schedules, pendingNameMatch]);

  const scrollToRoom = useCallback((operatingRoomId: string) => {
    const row = document.querySelector(`[data-room-id="${operatingRoomId}"]`);
    if (row) {
      row.scrollIntoView({ behavior: 'smooth', block: 'center' });
      row.classList.add('highlight-flash');
      setTimeout(() => row.classList.remove('highlight-flash'), 1500);
    }
  }, []);

  const handleSidebarCreateSchedule = useCallback((operatingRoomId: string) => {
    if (!isAdmin) return;
    openCreateScheduleModal(operatingRoomId, 8 * 60, 9 * 60);
  }, [selectedDate, isAdmin]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleToggleForcedHospital = useCallback((hospitalId: string) => {
    setForcedActiveHospitalIds(prev => toggleHospitalIdInSet(prev, hospitalId));
  }, []);

  const handleCreateShift = async () => {
    try {
      // Convert local times to UTC for the API call
      const startUtc = new Date(newScheduleData.startTimeLocal).toISOString();
      const endUtc = new Date(newScheduleData.endTimeLocal).toISOString();
      
      // Get employee type from selected employee
      const selectedEmployee = employees.find(e => e.employeeId === newScheduleData.employeeId);
      if (!selectedEmployee) {
        setError('Please select an employee');
        return;
      }
      
      await createShift({
        employeeId: newScheduleData.employeeId,
        startTimeUtc: startUtc,
        endTimeUtc: endUtc,
        employeeType: selectedEmployee.employeeType,
        order: newScheduleData.order
      });
      
      setIsCreateScheduleModalOpen(false);
      setIsCreatingShift(false);
      setScheduleSelection({ operatingRoomId: null, startMinutes: null, endMinutes: null });
      setToast({ type: 'success', text: 'Shift created successfully!' });
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to create shift');
      console.error('Error creating shift:', err);
    }
  };

  // Build helper maps
  const employeeMap = new Map(employees.map(e => [e.employeeId, e]));
  const scheduleMap = new Map(schedules.map(s => [s.scheduleId, s]));
  // Label rooms by NAME first (same as the board's room cell uses), so a room whose `roomNumber` differs from its
  // `name` (e.g. name "MC03" but roomNumber "OR-2") shows the SAME label on the card's room synopsis as on the board.
  const roomLabelById = new Map(operatingRooms.map((r: any) => [r.operatingRoomId, r.name || r.roomNumber || r.operatingRoomId]));
  // --- Move-log helpers (passive journal of manual board moves) ---
  const nameOf = (id: string) => { const e = employeeMap.get(id); return e ? `${e.firstName} ${e.lastName}` : 'someone'; };
  const logMove = (entry: Omit<MoveLogEntry, 'ts'>) => setMoveLog(appendMove(selectedDate, entry));
  const fmtLogTime = (iso: string) => { try { return new Date(iso).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }); } catch { return ''; } };
  const moveIcon = (k: MoveLogEntry['kind']) => k === 'place' ? '➕' : k === 'remove' ? '➖' : k === 'transfer' ? '🔁' : k === 'lock' ? '🔒' : '🔓';
  // Lookups for the hover tooltip — the EARLIEST shift per employee (earliestShiftByEmp, defined above), so the
  // tooltip agrees with the nameplate, the bullpen and the card for someone with two overlapping shift rows (B5).
  const shiftByEmployeeId = earliestShiftByEmp;
  const hospitalNameById = new Map(hospitals.map(h => [h.hospitalId, h.name]));
  // Current site names (Hospitals & ORs page) — the person card filters "Credentialed sites" to these.
  const hospitalSiteNames = hospitals.map(h => h.name);

  // ---- Editable site coin + room transfer (local, per-day, reversible) ----
  // Coin options: one colored acronym badge per hospital.
  const siteAcronym = (name?: string): string =>
    (name || '').replace(/[^A-Za-z0-9 ]/g, ' ').split(/\s+/).filter(Boolean).map(w => w[0].toUpperCase()).join('').slice(0, 5) || '?';
  const siteLockOptions = hospitals.map(h => ({ hospitalId: h.hospitalId, name: h.name, acronym: siteAcronym(h.name), color: colorForHospital(h) }));
  // Warn before hard-locking someone to a site they're NOT credentialed for (would leave them unstaffed).
  const canWorkAtSite = (empId: string, hospitalId: string): boolean => {
    const creds: string[] = ((employeeMap.get(empId) as any)?.credentials) || [];
    if (!creds.length) return true;                                     // no credential list -> eligible everywhere
    const hName = hospitalNameById.get(hospitalId);
    return !!hName && creds.includes(hName);
  };
  const setBoardSiteLock = (empId: string, hospitalId: string | null) => {
    if (hospitalId && !canWorkAtSite(empId, hospitalId)) {
      const emp = employeeMap.get(empId);
      const who = emp ? `${emp.firstName} ${emp.lastName}` : 'This provider';
      const hName = hospitalNameById.get(hospitalId) || 'that site';
      if (!window.confirm(`${who} is not credentialed at ${hName}, so a hard lock there would leave them UNSTAFFED when you next run Allocate. Lock them anyway?`)) return;
    }
    setSiteLocks(setSiteLock(selectedDate, empId, hospitalId));
    const who = nameOf(empId);
    if (hospitalId) logMove({ kind: 'lock', who, to: hospitalNameById.get(hospitalId) || 'a site', detail: `Locked ${who} to ${hospitalNameById.get(hospitalId) || 'a site'}` });
    else logMove({ kind: 'unlock', who, detail: `Cleared ${who}'s site lock` });
  };
  // ---- Float CRNA slots (site-level, per-day, reversible) ----
  // Pre-assign a CRNA into one of a site's float slots. A float belongs to the SITE (no room), so it's keyed
  // by hospitalId. The person is pulled out of any other float slot and any relief assignment so they're never
  // double-counted (they'll also drop out of the bullpen via the exclude set below).
  const assignFloatCrna = (hospitalId: string, index: number, empId: string) => {
    if (!isCredentialedAt(empId, hospitalId)) { window.alert(`${nameOf(empId)} is not credentialed at ${getHospitalName(hospitalId)}, so they can't float there.`); return; }
    clearFloatCrnaForEmployee(selectedDate, empId);
    clearReliefForEmployee(empId);
    setFloatCrnasState(persistFloatCrna(selectedDate, hospitalId, index, empId));
    setFloatPicker(null);
    const who = nameOf(empId);
    const site = hospitalNameById.get(hospitalId) || 'a site';
    logMove({ kind: 'place', who, slot: 'Float CRNA', to: site, detail: `Placed ${who} as a float CRNA at ${site}` });
  };
  const clearFloatCrna = (hospitalId: string, index: number) => {
    const prev = floatCrnaAt(floatCrnas, hospitalId, index);
    setFloatCrnasState(persistFloatCrna(selectedDate, hospitalId, index, null));
    if (prev) {
      const who = nameOf(prev);
      const site = hospitalNameById.get(hospitalId) || 'a site';
      logMove({ kind: 'remove', who, slot: 'Float CRNA', from: site, detail: `Removed ${who} from float CRNA at ${site}` });
    }
  };
  // Drag-to-float: a CRNA dragged onto a site's float drop zone is floated there. Fill the first EMPTY float
  // box (a configured slot or a previously drag-created one); if every box is full, APPEND a new slot — this
  // is what lets a drag "create a spot" even when the site started with zero float slots. See assignFloatCrna.
  const floatDropCrna = (hospitalId: string, empId: string) => {
    const backendSlots = Math.max(0, floatSlotsAt(floatSlotMap, hospitalId));
    const cap = Math.max(backendSlots, floatCrnas[hospitalId]?.length ?? 0);
    let index = 0;
    while (index < cap && floatCrnaAt(floatCrnas, hospitalId, index)) index++;
    assignFloatCrna(hospitalId, index, empId);
  };
  // Schedule ids (cases) for a room, and for a doctor's EFFECTIVE rooms — the unit a doctor edit acts on.
  const scheduleIdsForRoom = (operatingRoomId: string): string[] =>
    allocations.filter((a: any) => scheduleMap.get(a.operatingRoomScheduleId)?.operatingRoomId === operatingRoomId).map((a: any) => a.operatingRoomScheduleId);
  const scheduleIdsForDoctor = (docId: string): string[] =>
    allocations.filter((a: any) => effectiveDoctorIds(a).includes(docId)).map((a: any) => a.operatingRoomScheduleId);
  // Option B: a move/swap REWRITES the allocation (persisted via saveAllocationPayload, no overlay) so every view
  // agrees, records an Undo entry, and clears any stale doctor overlay on the touched rooms. Guarded —
  // buildDoctorEditPayload returns null when a write would erase the day.
  /**
   * Commit a persisted board edit safely: re-read the window, refuse if a schedule THIS edit touches changed
   * server-side (another runner got there first), otherwise rebase the edit onto fresh state and write — so a
   * concurrent edit to some other room survives instead of being rewritten away by our full-window PUT.
   * Local side effects (overlay clears, Undo entry, Move Log) run only after the write lands.
   * Returns the post-write allocations on success, null when nothing was written.
   */
  const commitEdit = async (
    edits: { scheduleId: string }[],
    rebuild: (source: any[], loaded: boolean) => { payload: any[]; nextAllocations: any[]; undo: any[] } | null,
    snapshotOverride?: GuardableAllocation[],
    conflictMessage?: string,
  ): Promise<{ undo: any[]; after: any[] } | null> => {
    if (!isAdmin) return null;
    const res = rebuild(allocations, allocationsLoaded);
    if (!res) return null;
    const startDateTime = localDateToUtcStart(selectedDate);
    const endDateTime = localDateToUtcEnd(selectedDate);
    const outcome = await commitAllocationEdit({
      snapshot: snapshotOverride && snapshotOverride.length ? snapshotOverride : (allocations as unknown as GuardableAllocation[]),
      editedIds: editedScheduleIds(edits),
      fetchFresh: async () => ((await searchAllocationsByWindow(startDateTime, endDateTime)).allocations || []) as unknown as GuardableAllocation[],
      rebuild: (fresh) => rebuild(fresh as any[], true),
      put: (payload) => updateAllocations(payload, startDateTime, endDateTime),
    });
    if (!outcome.ok) {
      if (outcome.reason === 'conflict' && conflictMessage) setError(conflictMessage);
      else if (outcome.message) setError(outcome.message);
      if (outcome.reason === 'error') console.error('Failed to save allocation change:', (outcome as any).error);
      return null;
    }
    setError('');
    let after: any[] = res.nextAllocations;
    try { after = (await searchAllocationsByWindow(startDateTime, endDateTime)).allocations || []; } catch { /* write landed */ }
    setAllocations(after);
    return { undo: res.undo, after };
  };

  const persistDoctorEdits = async (edits: DoctorEdit[], undoLabel: string, log: Omit<MoveLogEntry, 'ts'>) => {
    const committed = await commitEdit(edits, (source, loaded) => buildDoctorEditPayload(source, edits, loaded));
    if (!committed) return;
    let nextTransfers = roomTransfers;
    for (const u of committed.undo) { const rid = scheduleMap.get(u.scheduleId)?.operatingRoomId; if (rid) nextTransfers = setRoomTransfer(selectedDate, rid, null); }
    setRoomTransfers(nextTransfers);
    const touched = editedScheduleIds(edits);
    setUndoStack(pushUndo(selectedDate, {
      label: undoLabel, kind: 'doctor', changes: committed.undo,
      expected: (committed.after as unknown as GuardableAllocation[]).filter(a => touched.includes(a.operatingRoomScheduleId)),
    }));
    logMove(log);
  };
  const undoLastMove = async () => {
    const { entry, stack } = popUndo(selectedDate);
    if (!entry) return;
    const edits = entry.kind === 'nurse'
      ? nurseEditsFromUndo(entry.changes as NurseUndoChange[])
      : editsFromUndo(entry.changes as DoctorUndoChange[]);
    // `changes` are ABSOLUTE prior ids, so replaying blindly onto a day that has moved on (wipe, re-allocate,
    // another runner) overwrites rather than undoes. `expected` records what the move left behind; if the server
    // no longer matches, the guard refuses and the entry stays on the stack.
    const committed = await commitEdit(
      edits as { scheduleId: string }[],
      (source, loaded) => (entry.kind === 'nurse'
        ? buildNurseEditPayload(source, edits as any, loaded)
        : buildDoctorEditPayload(source, edits as any, loaded)),
      entry.expected,
      `That move can no longer be undone — ${entry.label} has been superseded by a newer change. Reload to see the latest board.`,
    );
    if (!committed) return;   // entry stays on the stack so the runner can retry after reloading
    setUndoStack(stack);
    logMove({ kind: 'transfer', detail: `Undid: ${entry.label}` });
  };
  // CRNA move/swap now PERSISTS the resulting nurse assignment (like the doctor moves) rather than only writing
  // the oc-nurse-transfers overlay — so a hand-tuned CRNA in-time waterfall no longer reverts on save. Reuses the
  // nurseRoomMoves rules (single-room invariant) to compute the end state, flushes it to allocatedNurseIds, clears
  // the overlay for the touched rooms, and records an Undo entry (kind 'nurse').
  const persistNurseMove = async (newOverlay: Record<string, string>, undoLabel: string, log: Omit<MoveLogEntry, 'ts'>) => {
    const editsFor = (source: any[]) => {
      const allocs = source.map((a: any) => ({ scheduleId: a.operatingRoomScheduleId, operatingRoomId: scheduleMap.get(a.operatingRoomScheduleId)?.operatingRoomId, allocatedNurseIds: a.allocatedNurseIds || [] }));
      return nurseEditsForOverlay(newOverlay, allocs, isCrnaId);
    };
    const committed = await commitEdit(editsFor(allocations), (source, loaded) => buildNurseEditPayload(source, editsFor(source), loaded));
    if (!committed) return;
    const cleared = { ...nurseTransfers };
    for (const rid of Object.keys(newOverlay)) delete cleared[rid];
    applyNurseTransfers(cleared);
    const touched = editedScheduleIds(editsFor(allocations));
    setUndoStack(pushUndo(selectedDate, {
      label: undoLabel, kind: 'nurse', changes: committed.undo,
      expected: (committed.after as unknown as GuardableAllocation[]).filter(a => touched.includes(a.operatingRoomScheduleId)),
    }));
    logMove(log);
  };
  const moveRoom = (operatingRoomId: string, toDoctorId: string) => {
    if (blockMoveIfUncredentialed(toDoctorId, [operatingRoomId])) return;
    const roomLabel = roomLabelById.get(operatingRoomId) || 'a room';
    const toName = nameOf(toDoctorId);
    const fromName = [...new Set(scheduleIdsForRoom(operatingRoomId).flatMap((sid: string) =>
      (allocations.find((al: any) => al.operatingRoomScheduleId === sid)?.allocatedDoctorIds || []).filter((id: string) => id !== toDoctorId)))].map(nameOf).join(', ');
    persistDoctorEdits(editsForRooms(scheduleIdsForRoom(operatingRoomId), toDoctorId),
      `Handed ${roomLabel} to ${toName}`,
      { kind: 'transfer', who: toName, room: roomLabel, from: fromName || undefined, to: toName, detail: `Handed ${roomLabel}${fromName ? ` from ${fromName}` : ''} to ${toName}` });
  };
  // Hand EVERY room this anesthesiologist currently owns to another doctor (the person card's "Transfer all").
  const moveAllRooms = (fromDoctorId: string, toDoctorId: string) => {
    if (fromDoctorId === toDoctorId) return;
    const scheds = scheduleIdsForDoctor(fromDoctorId);
    if (!scheds.length) return;
    if (blockMoveIfUncredentialed(toDoctorId, roomsOfScheds(scheds))) return;
    const labels = [...new Set(scheds.map((sid: string) => scheduleMap.get(sid)?.operatingRoomId).filter(Boolean).map((rid: any) => roomLabelById.get(rid) || 'a room'))];
    const toName = nameOf(toDoctorId), fromName = nameOf(fromDoctorId);
    persistDoctorEdits(editsForRooms(scheds, toDoctorId),
      `Transferred all of ${fromName}'s rooms to ${toName}`,
      { kind: 'transfer', who: toName, room: labels.join(', '), from: fromName || undefined, to: toName, detail: `Handed all ${labels.length} room${labels.length > 1 ? 's' : ''} (${labels.join(', ')}) from ${fromName} to ${toName}` });
  };
  // EXCHANGE all room assignments between two anesthesiologists (the person card's "Swap all"): A's rooms go to B
  // and B's to A. Snapshot BOTH schedule sets before editing (they're disjoint, so order doesn't matter).
  const swapAllRooms = (docAId: string, docBId: string) => {
    if (docAId === docBId) return;
    const aScheds = scheduleIdsForDoctor(docAId), bScheds = scheduleIdsForDoctor(docBId);
    if (!aScheds.length && !bScheds.length) return;
    if (blockMoveIfUncredentialed(docBId, roomsOfScheds(aScheds)) || blockMoveIfUncredentialed(docAId, roomsOfScheds(bScheds))) return;
    const aName = nameOf(docAId), bName = nameOf(docBId);
    persistDoctorEdits([...editsForRooms(aScheds, docBId), ...editsForRooms(bScheds, docAId)],
      `Swapped all rooms between ${aName} and ${bName}`,
      { kind: 'transfer', who: `${aName} ⇄ ${bName}`, room: `${aScheds.length + bScheds.length} rooms`, from: aName, to: bName, detail: `Swapped all room assignments between ${aName} and ${bName}` });
  };
  // Apply the room-transfer overlay: an allocation whose ROOM was moved reports the target doctor instead of the
  // allocated one. Everything that reads a room's doctor (nameplate, supervision, in-time) goes through here.
  const effectiveDoctorIds = (a: any): string[] => {
    const sched = scheduleMap.get(a.operatingRoomScheduleId);
    return effectiveDoctorIdsForRoom(a.allocatedDoctorIds || [], sched?.operatingRoomId, roomTransfers);
  };
  // Apply the NURSE-transfer overlay: an allocation whose ROOM had its CRNA moved reports the target CRNA instead
  // of the allocated one (any student on the case is preserved). The CRNA analogue of effectiveDoctorIds —
  // everything that reads a room's CRNA (nameplate, bullpen, concurrency, supervision, the CRNA card) goes here.
  const isCrnaId = (id: string): boolean => employeeMap.get(id)?.employeeType === 'nurse';
  const effectiveNurseIds = (a: any): string[] => {
    const sched = scheduleMap.get(a.operatingRoomScheduleId);
    return effectiveNurseIdsForRoom(a.allocatedNurseIds || [], sched?.operatingRoomId, nurseTransfers, isCrnaId);
  };
  // GREEN while every role the case needs has someone PRESENT, RED while one does not — minute by minute, so a case
  // turns red when someone goes home and green again once relief is arranged (Braden 2026-09-13). Roles: an
  // anesthesiologist (the allocation's doctor, a relief supervisor, or a doctor gone in-room) and, in a supervised
  // room, someone in the room (the CRNA/student, or whoever was assigned as their relief). Who counts as present —
  // shift window, pecking order, sent home / called back — is decided in caseCoverage.ts and offTimes.ts.
  const hmToMsOnSelectedDate = (hm: string) => new Date(localDateTimeToUtc(`${selectedDate}T${hm}:00`)).getTime();
  const caseCoverage = (schedule: any, manualAllocation: any, allocation: any): CoverageSegment[] => {
    const nurseIds: string[] = manualAllocation ? (manualAllocation.allocatedNurseIds || []) : (allocation ? effectiveNurseIds(allocation) : []);
    const docIds: string[] = manualAllocation ? (manualAllocation.allocatedDoctorIds || []) : (allocation ? effectiveDoctorIds(allocation) : []);
    // Relief carries forward: a reliever assigned on this case, or on an EARLIER case in the same room, is still in
    // that room for this one. Relief also takes OWNERSHIP of its role from the handoff (Braden 2026-09-14): the team
    // that started the case is not counted for that role after it, so the case never goes back to them.
    const roomSchedules = getSchedulesForRoom(schedule.operatingRoomId);
    const caseStartMs = new Date(schedule.startTimeUtc).getTime(), caseEndMs = new Date(schedule.endTimeUtc).getTime();
    const roles = caseRoleHolders({
      caseStartMs, caseEndMs, doctorIds: docIds, nurseIds,
      ...caseReliefs(schedule.scheduleId, roomSchedules), presenceFor: presenceForEmployee,
    });
    return caseCoverageSegments(caseStartMs, caseEndMs, roles, presenceForEmployee);
  };
  // SHORT PREP (Braden 2026-09-14): whoever OPENS a room with less than the report-in lead gets a red outline on their
  // name block, so a float knows to help them set up. Arrival = a manually typed in-time if there is one, else a shift
  // worker's shift start. A pecking-order provider's in-time is derived FROM their first room, so they always get the
  // full lead unless an in-time was typed in. Shown only while the plates show the room's first case.
  const shortPrepFor = (roomId: string, scheduleId: string | undefined, empId: string): boolean => {
    const first = getSchedulesForRoom(roomId)[0];
    if (!first || first.scheduleId !== scheduleId) return false;
    const manual = inTimeByEmployee[empId];
    const sh: any = shiftByEmployeeId.get(empId);
    const pecking = effectivePeckingOrder(peckingOrderOverride, empId, sh?.order) != null;
    const arrivalMs = manual ? hmToMsOnSelectedDate(manual) : (!pecking && sh?.startTimeUtc ? Date.parse(sh.startTimeUtc) : undefined);
    return shortPrepMinutes(Date.parse(first.startTimeUtc), arrivalMs, effectiveCaseFlags(first, caseMarkers).nerveBlock.value) !== null;
  };
  // The RAW (pre-overlay) CRNA holder of each room — the other half of what the overlay rules need to keep a
  // CRNA single-room. `alloc` lets a caller pass an in-flight payload (a drop/remove edits the raw allocations
  // before recomputing the overlay), so the rules always see the raw state they are about to be paired with.
  const rawCrnaIdsByRoom = (alloc: any[] = allocations): RawCrnaByRoom => {
    const out: RawCrnaByRoom = {};
    for (const a of alloc) {
      const rid = scheduleMap.get(a.operatingRoomScheduleId || a.scheduleId)?.operatingRoomId;
      if (!rid) continue;
      const crnas = (a.allocatedNurseIds || []).filter(isCrnaId);
      out[rid] = [...new Set([...(out[rid] || []), ...crnas])];
    }
    return out;
  };
  // Persist a whole overlay map computed by the nurseRoomMoves rules and reflect it in state.
  const applyNurseTransfers = (next: Record<string, string>) => setNurseTransfers(saveNurseTransfers(selectedDate, next));

  // Resolve the staff (and their source) currently attached to a schedule, for the hover tooltip.
  const getScheduleHoverDetails = (schedule: OperatingRoomSchedule) => {
    const allocation = allocations.find(a => a.operatingRoomScheduleId === schedule.scheduleId) || null;
    const manualAllocation = manualAllocations.find(ma => ma.scheduleId === schedule.scheduleId) || null;

    let source: 'allocated' | 'manual' | 'none' = 'none';
    let doctorIds: string[] = [];
    let nurseIds: string[] = [];
    if (hasPersistedAllocations() && allocation) {
      source = 'allocated';
      // Room-transfer overlay so the hover tooltip names the same doctor + CRNA the nameplate shows.
      doctorIds = effectiveDoctorIds(allocation);
      nurseIds = effectiveNurseIds(allocation);
    } else if (manualAllocation) {
      source = 'manual';
      doctorIds = manualAllocation.allocatedDoctorIds || [];
      nurseIds = manualAllocation.allocatedNurseIds || [];
    }

    return buildScheduleHoverDetails(
      schedule,
      doctorIds,
      nurseIds,
      source,
      employeeMap,
      shiftByEmployeeId,
      hospitalNameById,
      getEmployeeTypeDisplayName
    );
  };

  // Target instant in UTC based on selectedDate + targetMinutes (local)
  const targetTimeUtc = (() => {
    if (!selectedDate) return '';
    const hh = String(Math.floor(targetMinutes / 60)).padStart(2, '0');
    const mm = String(targetMinutes % 60).padStart(2, '0');
    const local = `${selectedDate}T${hh}:${mm}:00`;
    return localDateTimeToUtc(local);
  })();

  // Doctor(s) + CRNA(s) covering this room at the time indicator — drives the staffing
  // nameplates shown under the OR name. Coverage is STICKY: the assigned team stays on the
  // nameplate no matter where the blue bar is dragged — during a case, in a turnover gap
  // between cases, or before the first case — and only clears once the room's LAST case has
  // ended (their work is done, so they fall into the bullpen). Manual drag-off is separate
  // (handleRemoveStaff), so a team can also be pulled to the bullpen by hand at any time.
  // Doctors the chief marked "Off" (sent home) on the Anesthesiologists tab are removed from the board
  // ENTIRELY — no room nameplate and not in the bullpen. Their rooms fall back to needing a doctor.
  const offDoctorIds = new Set(Object.keys(statusByEmployee).filter(id => (statusByEmployee[id] || []).includes('off')));

  // One person's presence today (shift window, pecking order all day, minus sent-home spans) — shared by the case green/red
  // and the name plates.
  const presenceForEmployee = (id: string) => presenceIntervals(shiftByEmployeeId.get(id), awayIntervals(offLogByEmployee[id], offDoctorIds.has(id), hmToMsOnSelectedDate));
  // The reliefs in force on a case (assigned on it, or carried in from an earlier case in the room), each with the handoff
  // time Symphony recorded on the case it was assigned on — none means the reliever takes over when they arrive.
  const caseReliefs = (scheduleId: string, roomSchedules: { scheduleId: string; startTimeUtc: string }[]): { doctorRelief?: CaseRelief; inRoomRelief?: CaseRelief & { isDoctor: boolean } } => {
    const handoffMsOf = (from: Record<string, string> | undefined, sid: string): number | undefined => {
      const ms = from?.[sid] ? Date.parse(from[sid]) : NaN;
      return Number.isFinite(ms) ? ms : undefined;
    };
    const sup = carriedReliefEntry(scheduleId, roomSchedules, reliefOverlay.reliefSup);
    const inRoom = carriedReliefEntry(scheduleId, roomSchedules, reliefOverlay.inRoom);
    return {
      doctorRelief: sup ? { id: sup.id, handoffMs: handoffMsOf(reliefOverlay.reliefSupFrom, sup.fromScheduleId) } : undefined,
      inRoomRelief: inRoom ? { id: inRoom.id, isDoctor: employeeMap.get(inRoom.id)?.employeeType === 'doctor', handoffMs: handoffMsOf(reliefOverlay.inRoomFrom, inRoom.fromScheduleId) } : undefined,
    };
  };

  const getActiveStaffForRoom = (operatingRoomId: string) => {
    const roomSchedules = getSchedulesForRoom(operatingRoomId);
    if (roomSchedules.length === 0) return null;

    const targetTime = new Date(targetTimeUtc).getTime();

    // Once the bar passes the room's last case end, every case is done -> bullpen (nameplate clears).
    const lastEnd = Math.max(...roomSchedules.map(s => new Date(s.endTimeUtc).getTime()));
    if (targetTime >= lastEnd) return null;

    // Governing case = the one running now; else the most recent case that has already started
    // (keeps the outgoing team on the room through a gap); else the first case (arriving team,
    // before the day's first case begins). This is what keeps the team visible at any bar time.
    let activeSchedule = roomSchedules.find(s => {
      const start = new Date(s.startTimeUtc).getTime();
      const end = new Date(s.endTimeUtc).getTime();
      return targetTime >= start && targetTime < end;
    });
    if (!activeSchedule) {
      const started = roomSchedules.filter(s => new Date(s.startTimeUtc).getTime() <= targetTime);
      activeSchedule = started.length ? started[started.length - 1] : roomSchedules[0];
    }
    if (!activeSchedule) return null;

    // Return the active scheduleId even when nothing is allocated yet, so an empty slot is still
    // a valid drop target (a drop creates the first allocation for this schedule).
    let allocation = allocations.find(a => a.operatingRoomScheduleId === activeSchedule!.scheduleId);

    // Carried-over coverage: if this case has no explicit staffing, assume the team from the most
    // recent earlier case in the SAME room continues covering it, and flag it as inherited (green).
    // We only carry a team forward (and only turn later cases green) when that earlier case is a
    // COMPLETE team — a doctor paired with a CRNA/student, OR a doctor explicitly marked "solo in a
    // room" (the fuchsia solo override). A lone supervising doctor with no CRNA yet is treated as
    // still-in-progress, so it does NOT green out the rest of the room.
    let inherited = false;
    const hasStaff = (a?: Allocation) => !!a && ((a.allocatedDoctorIds || []).length > 0 || (a.allocatedNurseIds || []).length > 0);
    const isCompleteTeam = (a?: Allocation) => {
      if (!a) return false;
      const docs = a.allocatedDoctorIds || [];
      if (docs.length === 0) return false;
      if ((a.allocatedNurseIds || []).length > 0) return true;                   // doc + CRNA/student pair
      const ov = a.colorOverrides || {};
      return docs.some(id => matchesPaletteColor('docSolo', ov[id]));             // doc explicitly marked solo
    };
    if (!hasStaff(allocation)) {
      const idx = roomSchedules.findIndex(s => s.scheduleId === activeSchedule!.scheduleId);
      for (let i = idx - 1; i >= 0; i--) {
        const prev = allocations.find(a => a.operatingRoomScheduleId === roomSchedules[i].scheduleId);
        if (isCompleteTeam(prev)) { allocation = prev; inherited = true; break; }
      }
    }

    const toPlate = (id: string) => {
      const emp = employeeMap.get(id);
      return {
        id,
        lastName: emp ? emp.lastName : id,
        fullName: emp ? `${emp.firstName} ${emp.lastName}` : id,
        employeeType: emp ? emp.employeeType : '',
        studentCompetency: emp ? (emp as any).studentCompetency : undefined,
      };
    };
    // Whoever HOLDS the case at the bar's time (Braden 2026-09-14: "show whoever has the case at that time"): once a
    // relief has taken over, the plate shows the reliever instead of the team that started the case (roleHoldersAt).
    const holders = roleHoldersAt({
      atMs: targetTime,
      caseStartMs: new Date(activeSchedule.startTimeUtc).getTime(),
      doctorIds: allocation ? effectiveDoctorIds(allocation) : [],
      nurseIds: allocation ? effectiveNurseIds(allocation) : [],
      ...caseReliefs(activeSchedule.scheduleId, roomSchedules),
      presenceFor: presenceForEmployee,
    });
    return {
      scheduleId: activeSchedule.scheduleId,
      inherited,
      // Inherited display is an assumption (not a real allocation on this case): no color overrides.
      colorOverrides: (inherited ? {} : (allocation?.colorOverrides || {})) as Record<string, string>,
      doctors: holders.doctorIds.filter((id: string) => !offDoctorIds.has(id)).map(toPlate),
      nurses: holders.nurseIds.map(toPlate),
      // A relief now ON the plate: its relief box stays empty, and dragging the plate off clears the relief.
      doctorTakenOver: holders.doctorTakenOver,
      inRoomTakenOver: holders.inRoomTakenOver,
      reliefOf: holders.reliefOf,
    };
  };

  // Snapshot the current allocations as an editable PUT payload, preserving per-placement color overrides.
  type AllocPayload = { scheduleId: string; allocatedNurseIds: string[]; allocatedDoctorIds: string[]; colorOverrides: Record<string, string> };
  const currentAllocationPayload = (): AllocPayload[] => allocations.map((a: any) => ({
    scheduleId: a.operatingRoomScheduleId,
    allocatedNurseIds: [...(a.allocatedNurseIds || [])],
    allocatedDoctorIds: [...(a.allocatedDoctorIds || [])],
    colorOverrides: { ...(a.colorOverrides || {}) },
  }));

  // Manually pre-programming a schedule (drag/click place or remove) also records it as a PIN, so the
  // "Allocate Staff" button stays enabled and the algorithm keeps these fixed while it fills the rest.
  // Empty schedules drop their pin. (Skipped in modify mode, which uses its own map.)
  const upsertManualPin = (scheduleId: string, allocatedNurseIds: string[], allocatedDoctorIds: string[]) => {
    if (isModifying) return;
    setManualAllocations(prev => {
      const rest = prev.filter(ma => ma.scheduleId !== scheduleId);
      if (allocatedNurseIds.length === 0 && allocatedDoctorIds.length === 0) return rest;
      return [...rest, { scheduleId, allocatedNurseIds: [...allocatedNurseIds], allocatedDoctorIds: [...allocatedDoctorIds] }];
    });
  };

  // Persist a bullpen drop: set the given slot on the active schedule's allocation, save the whole
  // window via PUT /allocations, then reload so it survives a refresh.
  // Credential guard (Braden 8/29): a provider may only be placed in a room at a hospital they are CREDENTIALED
  // for. A credential is a hospitalId (migrated) or a hospital name (legacy) — resolve both exactly like
  // credentialCoinsFor. Unknown hospital -> don't block (benefit of the doubt).
  // The rule itself (incl. the no-credentials-on-file escape) lives in boardViewHelpers so it is unit-testable and
  // so the board and the ManualAllocationModal picker cannot drift apart. Here we only supply the resolver.
  const isCredentialedAt = (employeeId: string, hospitalId: string | null | undefined): boolean =>
    isCredentialedAtHospital(((employeeMap.get(employeeId) as any)?.credentials), hospitalId,
      c => (hospObjById.get(c) ?? hospObjByName.get(c))?.hospitalId);
  // Reject a manual placement of an UNCREDENTIALED provider into a room's hospital; returns true if it was blocked.
  const blockIfUncredentialed = (employeeId: string, operatingRoomId: string | null | undefined): boolean => {
    const hid = operatingRoomId ? getOperatingRoomHospitalId(operatingRoomId) : null;
    if (hid && !isCredentialedAt(employeeId, hid)) {
      window.alert(`${nameOf(employeeId)} is not credentialed at ${getHospitalName(hid)}, so they can't be placed in ${roomLabelById.get(operatingRoomId!) || 'that room'}.`);
      return true;
    }
    return false;
  };
  // Reject a room-transfer/swap that would hand `employeeId` any room at a hospital they are not credentialed for.
  const blockMoveIfUncredentialed = (employeeId: string, operatingRoomIds: (string | undefined)[]): boolean => {
    for (const rid of operatingRoomIds) {
      const hid = rid ? getOperatingRoomHospitalId(rid) : null;
      if (hid && !isCredentialedAt(employeeId, hid)) {
        window.alert(`${nameOf(employeeId)} is not credentialed at ${getHospitalName(hid)}, so this transfer to ${roomLabelById.get(rid!) || 'that room'} was blocked.`);
        return true;
      }
    }
    return false;
  };
  const roomsOfScheds = (schedIds: string[]): (string | undefined)[] => schedIds.map(sid => scheduleMap.get(sid)?.operatingRoomId);
  // The cases a single-room provider (CRNA/student) must be pulled off to be placed on `scheduleId`: only the
  // ones running at the same time. The rule itself lives in boardViewHelpers so it is unit-testable.
  const msOf = (v?: string) => { const t = v ? new Date(v).getTime() : NaN; return Number.isNaN(t) ? undefined : t; };
  const vacatedScheduleIds = (
    targetScheduleId: string,
    employeeId: string,
    payload: { scheduleId: string; allocatedNurseIds: string[] }[],
  ): Set<string> => {
    const target = scheduleMap.get(targetScheduleId);
    const held = payload
      .filter(p => p.scheduleId !== targetScheduleId && p.allocatedNurseIds.includes(employeeId))
      .map(p => {
        const s = scheduleMap.get(p.scheduleId);
        return { scheduleId: p.scheduleId, startMs: msOf(s?.startTimeUtc), endMs: msOf(s?.endTimeUtc) };
      });
    return scheduleIdsToVacateForSingleRoomPlacement(
      { startMs: msOf(target?.startTimeUtc), endMs: msOf(target?.endTimeUtc) },
      held,
    );
  };
  const handleDropStaff = async (scheduleId: string, slotKind: 'doctor' | 'crna' | 'student', employeeId: string) => {
    if (blockIfUncredentialed(employeeId, scheduleMap.get(scheduleId)?.operatingRoomId)) return;
    const payload = currentAllocationPayload();
    let entry = payload.find(p => p.scheduleId === scheduleId);
    if (!entry) { entry = { scheduleId, allocatedNurseIds: [], allocatedDoctorIds: [], colorOverrides: {} }; payload.push(entry); }
    // (move-log) snapshot the target slot's prior occupant + this person's source room BEFORE we mutate.
    const _sched = scheduleMap.get(scheduleId);
    // Placing a person clears any manual NEED/NONE mark on that room's slot (it's filled now).
    if (_sched && (slotKind === 'doctor' || slotKind === 'crna')) setSlotMarks(setSlotMark(selectedDate, _sched.operatingRoomId, slotKind, null));
    const _roomLabel = _sched ? (roomLabelById.get(_sched.operatingRoomId) || 'a room') : 'a room';
    const _slotLabel = slotKind === 'doctor' ? 'anesthesiologist' : slotKind === 'crna' ? 'CRNA' : 'student';
    let _priorOnSlot = '';
    let _sourceRoom = '';
    if (slotKind === 'doctor') {
      _priorOnSlot = entry.allocatedDoctorIds.filter(id => id !== employeeId).map(nameOf).join(', ');
    } else {
      const _isStudent = (id: string) => (employeeMap.get(id)?.employeeType || '').startsWith('nurse-student');
      const _sameKind = (id: string) => slotKind === 'student' ? _isStudent(id) : employeeMap.get(id)?.employeeType === 'nurse';
      _priorOnSlot = entry.allocatedNurseIds.filter(id => id !== employeeId && _sameKind(id)).map(nameOf).join(', ');
      const _src = payload.find(p => p.scheduleId !== scheduleId && p.allocatedNurseIds.includes(employeeId));
      const _srcSched = _src ? scheduleMap.get(_src.scheduleId) : undefined;
      _sourceRoom = _srcSched ? (roomLabelById.get(_srcSched.operatingRoomId) || '') : '';
    }
    if (slotKind === 'doctor') {
      // Anesthesiologists are additive across rooms. The cap is about SIGNATURES: each solo room and
      // each distinct CRNA/student they supervise is one signature (a CRNA who follows to two rooms is
      // still one). Only warn if adding THIS room would put them over DOCTOR_MAX_ROOMS signatures — but
      // count ONLY the rooms they'd be on AT THIS MOMENT: rooms currently going, or starting within the
      // hour of this room's start. Rooms later in the day (that they'll relieve into) are NOT counted, and
      // a physical room's many cases count once (group by room), so we don't inflate a solo doctor to 7.
      const alreadyOnThisRoom = entry.allocatedDoctorIds.includes(employeeId);
      if (!alreadyOnThisRoom && _sched) {
        const placedStartMs = new Date(_sched.startTimeUtc).getTime();
        // Ask the SHARED math, at the instant this room starts. This used to be an inline re-implementation
        // that read RAW allocatedDoctorIds/allocatedNurseIds — so on a board with room or nurse transfers it
        // could quote a number no other surface showed. It now goes through the same resolvers as the person
        // card and the empty-slot picker, with the room being placed passed in as an extra room (its pending
        // nurse list exists only in the in-flight payload, not yet in `allocations`).
        const newSignatures = signaturesAtMoment(employeeId, allocations as any, placedStartMs, {
          roomIdOf: (sid) => scheduleMap.get(sid)?.operatingRoomId,
          startMsOf: (sid) => { const s = scheduleMap.get(sid)?.startTimeUtc; return s ? new Date(s).getTime() : undefined; },
          endMsOf: (sid) => { const e = scheduleMap.get(sid)?.endTimeUtc; return e ? new Date(e).getTime() : undefined; },
          isNurseLike: (id) => { const t = employeeMap.get(id)?.employeeType || ''; return t === 'nurse' || t.startsWith('nurse-student'); },
          // The placed room comes in via extraRooms, so skip its existing allocation to avoid counting it twice.
          doctorIdsOf: (a: any) => (a.operatingRoomScheduleId === scheduleId ? [] : effectiveDoctorIds(a)),
          nurseIdsOf: (a) => effectiveNurseIds(a as any),
          extraRooms: [{
            roomId: _sched.operatingRoomId,
            startMs: placedStartMs,
            endMs: new Date(_sched.endTimeUtc).getTime(),
            nurseIds: entry.allocatedNurseIds,
          }],
        });
        // Compare against THIS doctor's own supervision ratio, not the blanket 4. A doctor configured to
        // supervise at 1:2 is over their cap at 3 rooms, and warning only past 4 let the board silently
        // over-assign them — while the scorecard, the nameplate dots and the empty-slot picker were all
        // already reading maxConcurrentNurseCoverageRatio. DOCTOR_MAX_ROOMS stays the default for a doctor
        // with no configured ratio.
        const emp = employeeMap.get(employeeId);
        const cap = (emp as any)?.maxConcurrentNurseCoverageRatio ?? DOCTOR_MAX_ROOMS;
        if (newSignatures > cap) {
          const name = emp ? `${emp.firstName} ${emp.lastName}` : 'This anesthesiologist';
          if (!window.confirm(`${name} would be running ${newSignatures} rooms at once (rooms going now or starting within the hour). That is more than their usual ${cap}. Continue?`)) {
            return;
          }
        }
      }
      entry.allocatedDoctorIds = [employeeId];
    } else if (slotKind === 'crna') {
      // CRNAs are single-room: remove from any CONCURRENT room (so a room-to-room drag MOVES them), then place.
      // Scoped to overlapping cases on purpose — a blanket strip would unstaff the morning room a
      // returning-later CRNA is still covering when the runner drags them into their afternoon room.
      const vacate = vacatedScheduleIds(scheduleId, employeeId, payload);
      payload.forEach(p => { if (vacate.has(p.scheduleId)) p.allocatedNurseIds = p.allocatedNurseIds.filter(id => id !== employeeId); });
      entry.allocatedNurseIds = [...entry.allocatedNurseIds.filter(id => employeeMap.get(id)?.employeeType !== 'nurse'), employeeId];
      // A direct board drop is authoritative over the card's nurse-transfer overlay: clear any overlay on this
      // room so effectiveNurseIds shows the just-dropped CRNA, not a prior card-move target that would mask it.
      // Clearing only THIS room's key is not enough — the raw strip above cannot reach a room the CRNA held via
      // the overlay, so that stale entry would leave them in two ORs. dropNurseOnRoom releases them by VALUE
      // too, against the post-strip raw state.
      if (_sched) applyNurseTransfers(dropNurseOnRoom(nurseTransfers, rawCrnaIdsByRoom(payload), _sched.operatingRoomId, employeeId));
    } else {
      // Students are single-room too: remove from any concurrent room, then place (keeping any CRNA here).
      const vacate = vacatedScheduleIds(scheduleId, employeeId, payload);
      payload.forEach(p => { if (vacate.has(p.scheduleId)) p.allocatedNurseIds = p.allocatedNurseIds.filter(id => id !== employeeId); });
      entry.allocatedNurseIds = [...entry.allocatedNurseIds.filter(id => !(employeeMap.get(id)?.employeeType || '').startsWith('nurse-student')), employeeId];
    }
    upsertManualPin(scheduleId, entry.allocatedNurseIds, entry.allocatedDoctorIds);
    const _detail = slotKind === 'doctor'
      ? `Put ${nameOf(employeeId)} on ${_roomLabel}${_priorOnSlot ? ` (was ${_priorOnSlot})` : ''}`
      : `Moved ${nameOf(employeeId)} (${_slotLabel}) to ${_roomLabel}${_sourceRoom ? ` from ${_sourceRoom}` : (_priorOnSlot ? ` (replaced ${_priorOnSlot})` : '')}`;
    logMove({ kind: 'place', who: nameOf(employeeId), room: _roomLabel, slot: _slotLabel, from: _sourceRoom || _priorOnSlot || undefined, detail: _detail });
    // Placed into a real slot → they're no longer "relief" anywhere (e.g. dragging a relief plate into a NEED box).
    clearReliefForEmployee(employeeId);
    await saveAllocationPayload(payload);
  };

  // Remove one person from one room (drag them off onto the bullpen). Doctors on other rooms stay
  // there; CRNAs/students free up entirely and return to the bullpen.
  const handleRemoveStaff = async (fromScheduleId: string, employeeId: string) => {
    const payload = currentAllocationPayload();
    // A CRNA/doctor is allocated to EVERY case in a room, so dragging their plate off must free them from ALL of
    // this room's cases — otherwise they stay on the other cases and never return to the bullpen. We only touch
    // THIS room's schedules, so a doctor supervising other rooms keeps those.
    const fromSched = scheduleMap.get(fromScheduleId);
    const roomId = fromSched?.operatingRoomId;
    // If this room's CRNA is on it only via the card's nurse-transfer overlay, dragging that plate off must clear
    // the overlay too — otherwise the overlaid CRNA isn't in the raw allocation, so the raw removal below is a
    // no-op and the plate would stay on the board. Reverting the room to its underlying allocation is only safe
    // when that allocated CRNA is not seated elsewhere by the overlay: after a swap (which writes TWO entries)
    // it is, and an unconditional revert put them in both rooms. removeNurseFromRoom vacates instead in that case.
    const _rawByRoom = rawCrnaIdsByRoom(payload);
    if (roomId && isCrnaId(employeeId)) applyNurseTransfers(removeNurseFromRoom(nurseTransfers, _rawByRoom, roomId, employeeId));
    const targetIds = roomId
      ? new Set(getSchedulesForRoom(roomId).map((s: any) => s.scheduleId))
      : new Set([fromScheduleId]);
    // Presence must be judged on the EFFECTIVE nurse ids, not the raw payload: a CRNA holding this room only via
    // the overlay is absent from allocatedNurseIds, so a raw-only test silently skipped the Move Log entry for a
    // removal that visibly changed the board — and that log is what publishGradingDay ships to the grading archive.
    const _wasPresent = payload.some(e => targetIds.has(e.scheduleId) && (
      effectiveNurseIdsForRoom(e.allocatedNurseIds, roomId, nurseTransfers, isCrnaId).includes(employeeId)
      || e.allocatedDoctorIds.includes(employeeId)));
    payload.forEach(entry => {
      if (!targetIds.has(entry.scheduleId)) return;
      entry.allocatedNurseIds = entry.allocatedNurseIds.filter(id => id !== employeeId);
      entry.allocatedDoctorIds = entry.allocatedDoctorIds.filter(id => id !== employeeId);
      delete entry.colorOverrides[employeeId];
      upsertManualPin(entry.scheduleId, entry.allocatedNurseIds, entry.allocatedDoctorIds);
    });
    if (_wasPresent) {
      const _roomLabel = roomId ? (roomLabelById.get(roomId) || 'a room') : 'a room';
      logMove({ kind: 'remove', who: nameOf(employeeId), room: _roomLabel, detail: `Took ${nameOf(employeeId)} off ${_roomLabel}` });
    }
    await saveAllocationPayload(payload);
  };

  // Set (or clear, when color is null) a nameplate color override for a person.
  // Anesthesiologists can cover multiple rooms, so their color is applied to EVERY room they're
  // assigned to (and, via overrideColorByEmployee below, their bullpen brick) so it stays consistent.
  // CRNAs/students are single-room, so their override stays on just that room.
  const handleRecolor = async (scheduleId: string, employeeId: string, color: string | null) => {
    const payload = currentAllocationPayload();
    const isDoctor = employeeMap.get(employeeId)?.employeeType === 'doctor';
    if (isDoctor) {
      let touched = false;
      payload.forEach(p => {
        if (p.allocatedDoctorIds.includes(employeeId)) {
          if (color) p.colorOverrides[employeeId] = color; else delete p.colorOverrides[employeeId];
          touched = true;
        }
      });
      if (!touched) return;
    } else {
      const entry = payload.find(p => p.scheduleId === scheduleId);
      if (!entry) return;
      if (color) entry.colorOverrides[employeeId] = color; else delete entry.colorOverrides[employeeId];
    }
    await saveAllocationPayload(payload);
  };

  // Persist a full-window allocation payload and reload so it survives a refresh.
  const saveAllocationPayload = async (payload: AllocPayload[]) => {
    const startDateTime = localDateToUtcStart(selectedDate);
    const endDateTime = localDateToUtcEnd(selectedDate);
    try {
      await updateAllocations(payload, startDateTime, endDateTime);
      const fresh = await searchAllocationsByWindow(startDateTime, endDateTime);
      setAllocations(fresh.allocations || []);
    } catch (err) {
      console.error('Failed to save allocation change:', err);
      setError('Failed to save allocation change');
    }
  };

  const leftPxFromUtc = (utcIso: string) => {
    // Convert UTC to local time for display positioning
    // Position relative to midnight (00:00 local time)
    const dt = new Date(utcIso);
    const minutes = dt.getHours() * 60 + dt.getMinutes();
    return (minutes / 60) * 100; // 100px per hour from midnight
  };

  const widthPxFromUtcRange = (startUtc: string, endUtc: string) => {
    const s = new Date(startUtc).getTime();
    const e = new Date(endUtc).getTime();
    const hours = Math.max(0, (e - s) / (1000 * 60 * 60));
    return hours * 100; // 100px per hour
  };

  // Compute employees allocated at target time
  const busyDoctorsSet = new Set<string>();
  const busyNonDoctorsSet = new Set<string>();
  try {
    const t = new Date(targetTimeUtc).getTime();
    allocations.forEach(a => {
      const sched = scheduleMap.get(a.operatingRoomScheduleId);
      if (!sched) return;
      const s = new Date(sched.startTimeUtc).getTime();
      const e = new Date(sched.endTimeUtc).getTime();
      if (t >= s && t < e) {
        // Room-transfer overlay: a moved room counts its TARGET doctor as busy, not the original.
        const doctorIds = effectiveDoctorIds(a);
        doctorIds.forEach((id: string) => {
          const emp = employeeMap.get(id);
          if (emp && emp.employeeType === 'doctor') busyDoctorsSet.add(id);
        });
        // Nurse-transfer overlay: a moved room counts its TARGET CRNA as busy, not the original.
        const nurseIds = effectiveNurseIds(a);
        nurseIds.forEach((id: string) => {
          const emp = employeeMap.get(id);
          if (emp && (emp.employeeType === 'nurse' || emp.employeeType === 'nurse-student-junior' || emp.employeeType === 'nurse-student-senior')) {
            busyNonDoctorsSet.add(id);
          }
        });
      }
    });
  } catch {}

  // Supervising-vs-solo COLOR must match what the board actually DISPLAYS: the sticky governing team per room
  // (getActiveStaffForRoom), not just cases literally running at the scrubber instant — otherwise a doctor shown
  // supervising a CRNA between/around cases would render solo (fuchsia). Count each displayed doctor's
  // displayed CRNAs across all rooms, so any doctor shown next to a CRNA reads as supervising (light-blue).
  const displayedSupervision = new Map<string, Set<string>>();
  try {
    operatingRooms.forEach(or => {
      const active = getActiveStaffForRoom(or.operatingRoomId);
      if (!active || active.nurses.length === 0) return;
      const nurseIds = active.nurses.map(n => n.id);
      active.doctors.forEach(doc => {
        if (!displayedSupervision.has(doc.id)) displayedSupervision.set(doc.id, new Set());
        const set = displayedSupervision.get(doc.id)!;
        nurseIds.forEach(nid => set.add(nid));
      });
    });
  } catch {}
  const supervisedCrnaCount = (doctorId: string): number => displayedSupervision.get(doctorId)?.size || 0;

  // Day-level supervisor set: a doctor who shares a room with a CRNA/student in ANY persisted allocation
  // is a SUPERVISOR for the day. A supervisor physically cannot be "solo" in an extra room they merely
  // hold the signature on (they're running other rooms concurrently), so those empty rooms must read
  // "NEED CRNA" — NOT a finished "NONE" — even when the algorithm tagged the room doctor-only. Only a
  // doctor who supervises no one all day is a genuine solo (1:1) doctor whose empty room reads NONE.
  const daySupervisorDoctorIds = daySupervisingDoctorIds(allocations as any);

  // Bullpen exclusion: anyone used for ANY purpose leaves the bullpen. CRNAs/students are single-room;
  // an anesthesiologist also drops out as soon as they're placed in even one room (solo or supervising).
  // To add an already-placed anesthesiologist to more rooms, drag their nameplate room-to-room (additive).
  const staffedEmployeeIds = new Set<string>();
  allocations.forEach((a: any) => {
    // Transfer overlays: a moved room staffs its TARGET doctor/CRNA (who leaves the bullpen); the original
    // drops back into the bullpen if this was their only room.
    effectiveNurseIds(a).forEach((id: string) => staffedEmployeeIds.add(id));
    effectiveDoctorIds(a).forEach((id: string) => staffedEmployeeIds.add(id));
  });
  // Held on an empty OB room for an add-on C-section — they are committed, not free.
  for (const id of obHoldEmployeeIds(obHold)) staffedEmployeeIds.add(id);

  // ---- Live relief ----
  // Only relief whose schedule STILL EXISTS on today's board counts as "using" a person. A relief entry
  // keyed by a scheduleId that a re-simulation has replaced (schedules get fresh random UUIDs) renders on
  // no room, so it must NOT linger in these sets and hide that person from the bullpen. Without this filter,
  // re-simulating a day "ghost-reserves" the CRNAs/doctors who were relief on the previous sim — they vanish
  // from the bullpen despite having no allocation. (The relief overlay is intentionally kept across sims so
  // a same-day replan survives; the scheduleId check is what makes keeping it safe.)
  const liveScheduleIds = new Set<string>(schedules.map(s => s.scheduleId));
  const liveReliefValues = (m: Record<string, string>): string[] =>
    Object.entries(m).filter(([sid, empId]) => liveScheduleIds.has(sid) && !!empId).map(([, empId]) => empId); // '' = a cleared carry
  // Anesthesiologists currently marked "in a room" somewhere -> their supervised rooms need a relief box.
  const inRoomDoctorIds = new Set<string>(liveReliefValues(reliefOverlay.inRoom));
  // Everyone currently assigned as relief (above a doctor or a CRNA) — they're "used", so drop them from the bullpen.
  const reliefEmployeeIds = new Set<string>([...liveReliefValues(reliefOverlay.inRoom), ...liveReliefValues(reliefOverlay.reliefSup)]);

  // Manual nameplate color overrides are stored per-allocation; collapse them into one
  // employeeId -> color lookup so the bullpen brick can show the same color the board rooms do.
  const overrideColorByEmployee = new Map<string, string>();
  allocations.forEach((a: any) => {
    if (a.colorOverrides) {
      Object.entries(a.colorOverrides).forEach(([id, c]) => { if (typeof c === 'string') overrideColorByEmployee.set(id, c); });
    }
  });

  // Each employee's assigned rooms with their case times (for concurrency + multi-start detection).
  const scheduleTimeMap = new Map(schedules.map(s => [s.scheduleId, s]));
  const roomsByEmployee = new Map<string, { startMs: number; endMs: number; startMin: number }[]>();
  allocations.forEach((a: any) => {
    const sched = scheduleTimeMap.get(a.operatingRoomScheduleId);
    if (!sched) return;
    const startMs = new Date(sched.startTimeUtc).getTime();
    const endMs = new Date(sched.endTimeUtc).getTime();
    const hm = utcToLocalTime(sched.startTimeUtc); // "HH:MM" local
    const startMin = hm ? ((parseInt(hm.slice(0, 2), 10) % 24) * 60 + parseInt(hm.slice(3, 5), 10)) : 0;
    const add = (id: string) => { if (!roomsByEmployee.has(id)) roomsByEmployee.set(id, []); roomsByEmployee.get(id)!.push({ startMs, endMs, startMin }); };
    // Transfer overlays: the room's interval counts toward its TARGET doctor/CRNA's concurrency, not the original's.
    effectiveDoctorIds(a).forEach(add);
    effectiveNurseIds(a).forEach(add);
  });

  // Max number of an employee's rooms that overlap at any instant (concurrent supervision load).
  // Sequential rooms (a short early case, then a later one) never overlap -> counts as 1.
  const maxConcurrentRooms = (id: string): number => {
    const rooms = roomsByEmployee.get(id) || [];
    if (rooms.length <= 1) return rooms.length;
    const points = rooms.flatMap(r => [{ t: r.startMs, d: 1 }, { t: r.endMs, d: -1 }]);
    points.sort((a, b) => a.t - b.t || a.d - b.d); // process ends before starts at the same instant
    let cur = 0, max = 0;
    for (const p of points) { cur += p.d; if (cur > max) max = cur; }
    return max;
  };

  // The "at the moment" supervision load used everywhere a signature count is shown (the person card's
  // dots, the empty-slot picker's open capacity): the doctor's PEAK concurrent signatures — rooms going
  // now or starting within the hour, grouped by PHYSICAL room (many cases in one room count once) with
  // same-CRNA-follows collapsing to 1. A doctor who merely relieves into afternoon rooms is NOT shown as
  // running them all at once. Delegates to the SHARED math (supervisionUtils) so the count is identical
  // whether opened from the Board or the Anesthesiologists tab, and matches the drop warning.
  const peakConcurrentSignaturesFor = (doctorId: string): number => peakConcurrentSignatures(doctorId, allocations as any, {
    roomIdOf: (schedId) => scheduleMap.get(schedId)?.operatingRoomId,
    startMsOf: (schedId) => { const s = scheduleMap.get(schedId)?.startTimeUtc; return s ? new Date(s).getTime() : undefined; },
    endMsOf: (schedId) => { const e = scheduleMap.get(schedId)?.endTimeUtc; return e ? new Date(e).getTime() : undefined; },
    isNurseLike: (id) => { const t = employeeMap.get(id)?.employeeType || ''; return t === 'nurse' || t.startsWith('nurse-student'); },
    doctorIdsOf: (a) => effectiveDoctorIds(a as any),
    nurseIdsOf: (a) => effectiveNurseIds(a as any),
  });

  // Rule 8 (reuse): a provider is "multi-start" when they cover an early (<= 09:30 local start) room B
  // for which they were genuinely FREE >= 45 min beforehand — i.e. ALL their other early rooms ended at
  // least 45 min before B started. That's the sequential-reuse signature (a team finishing a short early
  // room and picking up a later one). A normal concurrent supervised group (overlapping/closely-staggered
  // rooms) never satisfies this, so it isn't flagged. Shown in a distinct color.
  const EARLY_START_MAX_MIN = 9 * 60 + 30;
  const REUSE_GAP_MS = 45 * 60000;
  const multiStartEmployeeIds = new Set<string>();
  roomsByEmployee.forEach((rooms, id) => {
    const early = rooms.filter(r => r.startMin <= EARLY_START_MAX_MIN);
    if (early.length < 2) return;
    for (const B of early) {
      const others = early.filter(r => r !== B);
      if (others.length > 0 && others.every(o => o.endMs <= B.startMs - REUSE_GAP_MS)) { multiStartEmployeeIds.add(id); break; }
    }
  });

  // Live supervision tally for an anesthesiologist: how many rooms they cover, by pairing type,
  // so the person card can show open "signatures" (CRNA cap, concurrency-based) and student ratios.
  const supervisionFor = (doctorId: string) => {
    let totalRooms = 0, crnaRooms = 0, greenStudentRooms = 0, otherStudentRooms = 0, soloRooms = 0;
    allocations.forEach((a: any) => {
      if (!effectiveDoctorIds(a).includes(doctorId)) return;
      totalRooms++;
      const nurseIds: string[] = effectiveNurseIds(a);
      const hasCrna = nurseIds.some(id => employeeMap.get(id)?.employeeType === 'nurse');
      const studentIds = nurseIds.filter(id => (employeeMap.get(id)?.employeeType || '').startsWith('nurse-student'));
      const hasGreen = studentIds.some(id => (employeeMap.get(id) as any)?.studentCompetency === 'green');
      if (hasCrna) crnaRooms++;
      else if (hasGreen) greenStudentRooms++;
      else if (studentIds.length) otherStudentRooms++;
      else soloRooms++;
    });
    // The card's dot count is the "at the moment" load: the doctor's PEAK concurrent signatures (rooms
    // going now or starting within the hour), matching the drop warning. Rooms later in the day that they
    // only relieve into are NOT counted, so a doctor covering staggered/relief rooms isn't shown as running
    // them all at once.
    const signaturesUsed = peakConcurrentSignaturesFor(doctorId);
    const concurrentRooms = maxConcurrentRooms(doctorId);
    return { totalRooms, crnaRooms, greenStudentRooms, otherStudentRooms, soloRooms, concurrentRooms, signaturesUsed };
  };

  // The rooms a doctor ended up covering (one entry per room, earliest first): room label, its earliest case
  // start, the case types in it, and whether it has a CRNA (supervising) or not (solo). Feeds the person card's
  // derived report-in time + room synopsis.
  const assignedRoomsFor = (doctorId: string) => {
    const byRoom = new Map<string, { starts: string[]; types: Set<string>; hasCrna: boolean; cases: { startTimeUtc: string; surgeryType: string }[] }>();
    allocations.forEach((a: any) => {
      if (!effectiveDoctorIds(a).includes(doctorId)) return;
      const sched = scheduleMap.get(a.operatingRoomScheduleId);
      if (!sched) return;
      const rid = sched.operatingRoomId;
      const e = byRoom.get(rid) || { starts: [], types: new Set<string>(), hasCrna: false, cases: [] };
      e.starts.push(sched.startTimeUtc);
      if (sched.surgeryType) e.types.add(sched.surgeryType);
      e.cases.push({ startTimeUtc: sched.startTimeUtc, surgeryType: sched.surgeryType || '' });
      if (effectiveNurseIds(a).length > 0) e.hasCrna = true;
      byRoom.set(rid, e);
    });
    return [...byRoom.entries()].map(([rid, e]) => ({
      operatingRoomId: rid,
      roomLabel: roomLabelById.get(rid) || rid,
      startTimeUtc: e.starts.slice().sort()[0],
      surgeryTypes: [...e.types],
      hasCrna: e.hasCrna,
      cases: e.cases.slice().sort((x, y) => x.startTimeUtc.localeCompare(y.startTimeUtc)),
    })).sort((a, b) => a.startTimeUtc.localeCompare(b.startTimeUtc));
  };

  // Candidates offered when an empty slot is clicked.
  //   • Anesthesiologists are scoped to the room's HOSPITAL: only doctors already working THAT site (they have
  //     a room there, or are locked there) are offered — the runner doesn't want to see doctors from other
  //     sites. Full signatures are INCLUDED (a site doctor can be deliberately overloaded onto an uncovered
  //     room, e.g. clicking NEED MD); the hint shows remaining capacity, or "full". If a site has no doctors
  //     yet, fall back to all working doctors so the picker is never a dead end.
  //   • CRNAs/students: those working today who aren't already staffed somewhere.
  const shiftEmployeeIds = new Set(shifts.map((s: any) => s.employeeId));
  const roomHospitalById = new Map<string, string>(operatingRooms.map((r: any) => [r.operatingRoomId, r.hospitalId]));
  // doctorId -> set of hospitalIds the doctor is "at" today (has a room there, honoring transfers; or locked there).
  const doctorHospitals = new Map<string, Set<string>>();
  const addDoctorHospital = (did: string, hid?: string) => {
    if (!hid) return;
    let s = doctorHospitals.get(did);
    if (!s) { s = new Set<string>(); doctorHospitals.set(did, s); }
    s.add(hid);
  };
  allocations.forEach((a: any) => {
    const sched = scheduleMap.get(a.operatingRoomScheduleId);
    const hid = sched ? roomHospitalById.get(sched.operatingRoomId) : undefined;
    effectiveDoctorIds(a).forEach((did: string) => addDoctorHospital(did, hid));
  });
  Object.entries(siteLocks).forEach(([empId, hid]) => addDoctorHospital(empId, hid || undefined));

  // Colored site "coins" for the person card: the site(s) a doctor is AT (next to their name), and their
  // credentialed real sites (colored like the board), matching the Anesthesiologists tab.
  const hospObjById = new Map<string, any>(hospitals.map((h: any) => [h.hospitalId, h]));
  const hospObjByName = new Map<string, any>(hospitals.map((h: any) => [h.name, h]));
  const coinForHid = (hid: string) => { const h = hospObjById.get(hid); const name = h?.name || hospitalNameById.get(hid) || ''; return { hospitalId: hid, name, acronym: siteAcronym(name), color: colorForHospital(h) }; };
  const atSitesFor = (did: string) => [...(doctorHospitals.get(did) || [])].map(coinForHid);

  // ---- Site-scoped transfer/swap targets ----
  // A doctor is "at" a site if they have an (effective) room there OR are hard-locked there. Every move/swap
  // target is limited to doctors at the room's site (per-room) or at ALL of the doctor's sites (move/swap all).
  const doctorsBySite = new Map<string, Set<string>>();
  doctorHospitals.forEach((hids, did) => hids.forEach(hid => {
    if (!doctorsBySite.has(hid)) doctorsBySite.set(hid, new Set());
    doctorsBySite.get(hid)!.add(did);
  }));
  Object.entries(siteLocks).forEach(([empId, hid]) => {
    if (employeeMap.get(empId)?.employeeType !== 'doctor') return;
    if (!doctorsBySite.has(hid)) doctorsBySite.set(hid, new Set());
    doctorsBySite.get(hid)!.add(empId);
  });
  // Board column order for hospitals (first-seen in the operating-rooms list) — used to order the CRNA card's
  // site-grouped swap candidates and the doctor transfer dropdown the SAME way the board's columns are ordered.
  const boardSiteOrder = hospitalOrderFromRooms(operatingRooms);
  // CRNA analogue of doctorHospitals / doctorsBySite: which site(s) each CRNA is at today (an effective room
  // there, or a hard lock), so the CRNA card can group its swap candidates by site with a "Not yet assigned" tail.
  const nurseHospitals = new Map<string, Set<string>>();
  const addNurseHospital = (nid: string, hid?: string) => {
    if (!hid) return;
    let s = nurseHospitals.get(nid);
    if (!s) { s = new Set<string>(); nurseHospitals.set(nid, s); }
    s.add(hid);
  };
  allocations.forEach((a: any) => {
    const sched = scheduleMap.get(a.operatingRoomScheduleId);
    const hid = sched ? roomHospitalById.get(sched.operatingRoomId) : undefined;
    effectiveNurseIds(a).forEach((nid: string) => { if (isCrnaId(nid)) addNurseHospital(nid, hid); });
  });
  const crnasBySite = new Map<string, Set<string>>();
  nurseHospitals.forEach((hids, nid) => hids.forEach(hid => {
    if (!crnasBySite.has(hid)) crnasBySite.set(hid, new Set());
    crnasBySite.get(hid)!.add(nid);
  }));
  Object.entries(siteLocks).forEach(([empId, hid]) => {
    if (employeeMap.get(empId)?.employeeType !== 'nurse') return;
    if (!crnasBySite.has(hid)) crnasBySite.set(hid, new Set());
    crnasBySite.get(hid)!.add(empId);
  });
  const toCandidateList = (ids: string[], excludeId: string) => ids
    .filter(d => d !== excludeId)
    .map(d => ({ id: d, name: nameOf(d) }))
    .sort((a, b) => a.name.localeCompare(b.name));
  // Manual room transfers target ANY AVAILABLE anesthesiologist — everyone on shift today who isn't marked
  // off, whether or not they already hold a room. This is what lets a doctor's rooms be handed to an available
  // (bullpen) doc — e.g. a fixed-shift doctor — not just to doctors already placed at the site. The transfer
  // itself is a pure overlay (room -> target doctor id), so any doctor is a valid target.
  const transferTargetDoctorIds = employees
    .filter((e: any) => e.employeeType === 'doctor' && shiftEmployeeIds.has(e.employeeId) && !offDoctorIds.has(e.employeeId))
    .map((e: any) => e.employeeId);
  // CRNA swap targets: any available CRNA on shift today who isn't marked off — whether or not they already hold
  // a room (an unplaced/bullpen CRNA is a valid target, exactly like the doctor path). The transfer is a pure
  // overlay (room -> target CRNA), so any CRNA is valid. Students are excluded (employeeType 'nurse' only) — a
  // swap trades one CRNA for another.
  const transferTargetCrnaIds = employees
    .filter((e: any) => e.employeeType === 'nurse' && shiftEmployeeIds.has(e.employeeId) && !offDoctorIds.has(e.employeeId))
    .map((e: any) => e.employeeId);
  // Targets to hand/swap ONE room, or ALL of a doctor's rooms: any available anesthesiologist (minus self).
  const roomCandidatesFor = (_operatingRoomId: string, excludeId: string) => toCandidateList(transferTargetDoctorIds, excludeId);
  const allCandidatesFor = (docId: string) => toCandidateList(transferTargetDoctorIds, docId);
  // Swap ONE room with another doctor: hand this room to B, and take B's EARLIEST room at the SAME site in
  // return (a clean 1-for-1 trade). If B has no room there (locked-only), it degrades to a plain move.
  const swapRoom = (operatingRoomId: string, fromDoctorId: string, withDoctorId: string) => {
    if (fromDoctorId === withDoctorId) return;
    if (blockMoveIfUncredentialed(withDoctorId, [operatingRoomId])) return;
    const hid = getOperatingRoomHospitalId(operatingRoomId);
    const bRoomStart = new Map<string, string>();
    allocations.forEach((a: any) => {
      if (!effectiveDoctorIds(a).includes(withDoctorId)) return;
      const sched = scheduleMap.get(a.operatingRoomScheduleId);
      const rid = sched?.operatingRoomId;
      if (!rid || rid === operatingRoomId || getOperatingRoomHospitalId(rid) !== hid) return;
      const st = sched?.startTimeUtc || '';
      if (!bRoomStart.has(rid) || st < bRoomStart.get(rid)!) bRoomStart.set(rid, st);
    });
    const bEarliest = [...bRoomStart.entries()].sort((x, y) => x[1].localeCompare(y[1]))[0]?.[0];
    const roomLabel = roomLabelById.get(operatingRoomId) || 'a room';
    const bLabel = bEarliest ? (roomLabelById.get(bEarliest) || 'a room') : '';
    const fromName = nameOf(fromDoctorId), toName = nameOf(withDoctorId);
    const edits = editsForRooms(scheduleIdsForRoom(operatingRoomId), withDoctorId);
    if (bEarliest) edits.push(...editsForRooms(scheduleIdsForRoom(bEarliest), fromDoctorId));
    persistDoctorEdits(edits,
      bEarliest ? `Swapped ${roomLabel} for ${bLabel}` : `Handed ${roomLabel} to ${toName}`,
      { kind: 'transfer', who: `${fromName} ⇄ ${toName}`, room: roomLabel, from: fromName, to: toName, detail: bEarliest ? `Swapped ${roomLabel} (${fromName}) for ${bLabel} (${toName})` : `Handed ${roomLabel} to ${toName}` });
  };

  // ---- CRNA rooms + move/swap (a CRNA is single-room; reuse the room-transfer overlay to hand their room off) ----
  // The report-in the provider card shows (Braden 2026-09-14): 30 min before a room they open (45 for a nerve block),
  // or when they take a room over — at a case start, or partway through a case at its handoff time. Same rule as the tabs.
  const boardTakeovers = takeoverScheduleIds(allocations as any[], sid => scheduleMap.get(sid)?.operatingRoomId,
    sid => { const st = scheduleMap.get(sid)?.startTimeUtc; return st ? Date.parse(st) : undefined; });
  const reportInUtcFor = (id: string, isDoc: boolean): string | undefined => {
    const cases: { startMs: number; nerveBlock: boolean; takingOver: boolean }[] = [];
    allocations.forEach((a: any) => {
      if (!(isDoc ? effectiveDoctorIds(a) : effectiveNurseIds(a)).includes(id)) return;
      const sched = scheduleMap.get(a.operatingRoomScheduleId);
      if (sched) cases.push({ startMs: Date.parse(sched.startTimeUtc), nerveBlock: effectiveCaseFlags(sched, caseMarkers).nerveBlock.value, takingOver: boardTakeovers.has(a.operatingRoomScheduleId) });
    });
    // Taking over partway through a case: in at the handoff time recorded for it.
    for (const h of handoffTimesFor(reliefOverlay, id)) if (scheduleMap.has(h.scheduleId)) cases.push({ startMs: h.ms, nerveBlock: false, takingOver: true });
    const inMs = reportInMs(cases);
    return inMs === undefined ? undefined : new Date(inMs).toISOString();
  };
  const assignedRoomsForNurse = (nurseId: string) => {
    const byRoom = new Map<string, { starts: string[]; types: Set<string>; hasCrna: boolean; cases: { startTimeUtc: string; surgeryType: string }[] }>();
    allocations.forEach((a: any) => {
      if (!effectiveNurseIds(a).includes(nurseId)) return;
      const sched = scheduleMap.get(a.operatingRoomScheduleId);
      if (!sched) return;
      const rid = sched.operatingRoomId;
      const e = byRoom.get(rid) || { starts: [], types: new Set<string>(), hasCrna: false, cases: [] };
      e.starts.push(sched.startTimeUtc);
      if (sched.surgeryType) e.types.add(sched.surgeryType);
      e.cases.push({ startTimeUtc: sched.startTimeUtc, surgeryType: sched.surgeryType || '' });
      e.hasCrna = true;
      byRoom.set(rid, e);
    });
    return [...byRoom.entries()].map(([rid, e]) => ({
      operatingRoomId: rid, roomLabel: roomLabelById.get(rid) || rid, startTimeUtc: e.starts.slice().sort()[0],
      surgeryTypes: [...e.types], hasCrna: e.hasCrna, cases: e.cases.slice().sort((x, y) => x.startTimeUtc.localeCompare(y.startTimeUtc)),
    })).sort((a, b) => a.startTimeUtc.localeCompare(b.startTimeUtc));
  };
  const crnaRoomsFor = (nurseId: string): Set<string> => {
    const s = new Set<string>();
    allocations.forEach((a: any) => { if (effectiveNurseIds(a).includes(nurseId)) { const rid = scheduleMap.get(a.operatingRoomScheduleId)?.operatingRoomId; if (rid) s.add(rid); } });
    return s;
  };
  // Same rooms, ordered by first case — "transfer all" hands over the EARLIEST room, so the pick is stable
  // rather than dependent on allocation iteration order.
  const crnaRoomsForInBoardOrder = (nurseId: string): string[] => {
    const startByRoom = new Map<string, string>();
    allocations.forEach((a: any) => {
      if (!effectiveNurseIds(a).includes(nurseId)) return;
      const sched = scheduleMap.get(a.operatingRoomScheduleId);
      const rid = sched?.operatingRoomId;
      if (!rid) return;
      const st = sched?.startTimeUtc || '';
      if (!startByRoom.has(rid) || st < startByRoom.get(rid)!) startByRoom.set(rid, st);
    });
    return [...startByRoom.entries()].sort((x, y) => x[1].localeCompare(y[1]) || x[0].localeCompare(y[0])).map(([rid]) => rid);
  };
  // Available-CRNA swap candidates for the CRNA card: EVERY CRNA on shift today (minus this one). The reported
  // gap was that this listed DOCTORS narrowed to a site-intersection; the CRNA card must offer all CRNAs, just
  // like the doctor card offers all doctors.
  const crnaCandidatesFor = (nurseId: string) => toCandidateList(transferTargetCrnaIds, nurseId);
  // Move ONE room's CRNA to another CRNA (writes the nurse-transfer overlay). Mirrors moveRoom for doctors.
  const moveNurseRoom = (operatingRoomId: string, toNurseId: string) => {
    if (blockMoveIfUncredentialed(toNurseId, [operatingRoomId])) return;
    const roomLabel = roomLabelById.get(operatingRoomId) || 'a room';
    const toName = nameOf(toNurseId);
    const fromIds = new Set<string>();
    getSchedulesForRoom(operatingRoomId).forEach((s: any) => {
      const a = allocations.find((al: any) => al.operatingRoomScheduleId === s.scheduleId);
      (a?.allocatedNurseIds || []).forEach((id: string) => { if (id !== toNurseId && isCrnaId(id)) fromIds.add(id); });
    });
    const fromName = [...fromIds].map(nameOf).join(', ');
    // The candidate list deliberately includes CRNAs who already hold a room, so the target must be RELEASED
    // from theirs before being seated here — a CRNA is single-room, and writing only this room's entry cloned
    // them into two ORs (the hazard swapNurseRoom guards against and this one-way path did not).
    const overlay = moveNurseToRoom(nurseTransfers, rawCrnaIdsByRoom(), operatingRoomId, toNurseId);
    persistNurseMove(overlay, `Handed ${roomLabel} CRNA to ${toName}`,
      { kind: 'transfer', who: toName, room: roomLabel, from: fromName || undefined, to: toName, detail: `Handed ${roomLabel} CRNA${fromName ? ` from ${fromName}` : ''} to ${toName}` });
  };
  // Swap ONE room's CRNA with another CRNA: this room -> nurse B, and B's earliest room -> this CRNA. A CRNA is
  // single-room, so this is a clean 1-for-1 trade — and UNLIKE the doctor swap it must trade B's room back
  // regardless of site: a doctor supervises many rooms so a cross-site "swap" that degrades to a one-way move is
  // harmless, but a CRNA is single-room, so a one-way move onto a placed CRNA would double-book them into two ORs.
  // It only degrades to a one-way move when B genuinely holds NO room (a bullpen CRNA), which is correct.
  const swapNurseRoom = (operatingRoomId: string, fromNurseId: string, withNurseId: string) => {
    if (fromNurseId === withNurseId) return;
    if (blockMoveIfUncredentialed(withNurseId, [operatingRoomId])) return;
    const bRoomStart = new Map<string, string>();
    allocations.forEach((a: any) => {
      if (!effectiveNurseIds(a).includes(withNurseId)) return;
      const sched = scheduleMap.get(a.operatingRoomScheduleId);
      const rid = sched?.operatingRoomId;
      if (!rid || rid === operatingRoomId) return;
      const st = sched?.startTimeUtc || '';
      if (!bRoomStart.has(rid) || st < bRoomStart.get(rid)!) bRoomStart.set(rid, st);
    });
    const bEarliest = [...bRoomStart.entries()].sort((x, y) => x[1].localeCompare(y[1]))[0]?.[0];
    // The return leg seats fromNurse into B's room — block it too if fromNurse isn't credentialed there.
    if (bEarliest && blockMoveIfUncredentialed(fromNurseId, [bEarliest])) return;
    const overlay = swapNurseRooms(nurseTransfers, rawCrnaIdsByRoom(), operatingRoomId, fromNurseId, withNurseId, bEarliest);
    const roomLabel = roomLabelById.get(operatingRoomId) || 'a room';
    const bLabel = bEarliest ? (roomLabelById.get(bEarliest) || 'a room') : '';
    const fromName = nameOf(fromNurseId), toName = nameOf(withNurseId);
    persistNurseMove(overlay, bEarliest ? `Swapped ${roomLabel} CRNA for ${bLabel}` : `Handed ${roomLabel} CRNA to ${toName}`,
      { kind: 'transfer', who: `${fromName} ⇄ ${toName}`, room: roomLabel, from: fromName, to: toName, detail: bEarliest ? `Swapped ${roomLabel} CRNA (${fromName}) for ${bLabel} (${toName})` : `Handed ${roomLabel} CRNA to ${toName}` });
  };
  const crnaRoomTransferFor = (nurseId: string) => ({
    candidates: crnaCandidatesFor(nurseId),
    // Group by the site(s) each CRNA is at (with a "Not yet assigned" tail), ordered like the board's columns.
    candidateGroups: groupTransferCandidates(transferTargetCrnaIds.filter((t: string) => t !== nurseId), crnasBySite, nameOf, (hid: string) => hospitalNameById.get(hid) || '', boardSiteOrder, (hid: string) => { const cc = coinForHid(hid); return { color: cc.color, acronym: cc.acronym }; }, (cid: string) => peckingOrderOverride[cid] ?? (shiftByEmployeeId.get(cid)?.order ?? null)),
    candidatesForRoom: (_rid: string) => crnaCandidatesFor(nurseId),
    onMove: (rid: string, toId: string) => moveNurseRoom(rid, toId),
    // "Transfer all" cannot loop moveNurseRoom: each call would release the target from the room the previous
    // call just gave them, so only the last room would stick. A CRNA is single-room, so the target takes the
    // earliest room and any others keep their occupant (see moveNurseToRooms).
    onMoveAll: (toId: string) => {
      const rids = crnaRoomsForInBoardOrder(nurseId);
      if (!rids.length) return;
      applyNurseTransfers(moveNurseToRooms(nurseTransfers, rawCrnaIdsByRoom(), rids, toId));
      const roomLabel = roomLabelById.get(rids[0]) || 'a room';
      logMove({ kind: 'transfer', who: nameOf(toId), room: roomLabel, from: nameOf(nurseId), to: nameOf(toId), detail: `Handed ${roomLabel} CRNA from ${nameOf(nurseId)} to ${nameOf(toId)}` });
    },
    onSwapAll: (otherId: string) => { const r = [...crnaRoomsFor(nurseId)][0]; if (r) swapNurseRoom(r, nurseId, otherId); },
    onSwapRoom: (rid: string, otherId: string) => swapNurseRoom(rid, nurseId, otherId),
  });

  // ---- Relief picker: available providers grouped like the bullpen (pecking order, then shift window) ----
  // Each row is shown in the person's nameplate color: an override wins, else supervising(blue)/solo(orange)
  // for a doctor and the shift color for a CRNA.
  const colorForPicker = (id: string): string => {
    const ov = overrideColorByEmployee.get(id);
    if (ov) return ov;
    return employeeMap.get(id)?.employeeType === 'nurse'
      ? crnaNameplateColor(getEmployeeShift(id))
      : doctorNameplateColor(getEmployeeShift(id), supervisedCrnaCount(id));
  };
  const shiftWindowLabelFor = (empId: string): string => {
    const s = getEmployeeShift(empId);
    if (!s?.startTimeUtc || !s?.endTimeUtc) return 'No shift';
    const f = (utc: string) => new Date(utc).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' }).replace(/\s/g, '').toLowerCase();
    return `${f(s.startTimeUtc)}–${f(s.endTimeUtc)}`;
  };
  const lastNameOf = (empId: string) => (employeeMap.get(empId)?.lastName || '');
  const groupProvidersForPicker = (empIds: string[], sectionPrefix?: string): ReliefPickerGroup[] => {
    const withOrder: { id: string; ord: number }[] = [];
    const byWindow = new Map<string, string[]>();
    const winStartMs = new Map<string, number>();
    empIds.forEach(eid => {
      const ord = peckingOrderOverride[eid] ?? (getEmployeeShift(eid)?.order ?? null);
      if (ord !== null && ord !== undefined) { withOrder.push({ id: eid, ord }); return; }
      const lbl = shiftWindowLabelFor(eid);
      if (!byWindow.has(lbl)) { byWindow.set(lbl, []); const s = getEmployeeShift(eid); winStartMs.set(lbl, s?.startTimeUtc ? new Date(s.startTimeUtc).getTime() : Infinity); }
      byWindow.get(lbl)!.push(eid);
    });
    const pre = sectionPrefix ? `${sectionPrefix} · ` : '';
    const groups: ReliefPickerGroup[] = [];
    if (withOrder.length) groups.push({ label: `${pre}Pecking order`, items: withOrder.sort((a, b) => a.ord - b.ord).map(x => ({ id: x.id, name: nameOf(x.id), note: `#${x.ord}`, color: colorForPicker(x.id) })) });
    [...byWindow.entries()].sort((a, b) => (winStartMs.get(a[0])! - winStartMs.get(b[0])!)).forEach(([lbl, ids]) => {
      groups.push({ label: `${pre}${lbl}`, items: ids.sort((a, b) => lastNameOf(a).localeCompare(lastNameOf(b))).map(id => ({ id, name: nameOf(id), color: colorForPicker(id) })) });
    });
    return groups;
  };
  const availableDoctorIds = () => employees.filter((e: any) => e.employeeType === 'doctor' && shiftByEmployeeId.has(e.employeeId)).map((e: any) => e.employeeId);
  const availableCrnaIds = () => employees.filter((e: any) => e.employeeType === 'nurse' && shiftByEmployeeId.has(e.employeeId)).map((e: any) => e.employeeId);
  // Doctor card: any available doctor. CRNA card: all available doctors (MD section) + all available CRNAs.
  const reliefDoctorGroups = (excludeId: string) => groupProvidersForPicker(availableDoctorIds().filter((id: string) => id !== excludeId));
  const reliefCrnaGroups = (excludeId: string) => [
    ...groupProvidersForPicker(availableDoctorIds(), 'MD'),
    ...groupProvidersForPicker(availableCrnaIds().filter((id: string) => id !== excludeId), 'CRNA'),
  ];
  // C1 phase 2: a credential may be a hospitalId (migrated) OR a hospital name (legacy) — resolve id first, then name.
  const credentialCoinsFor = (emp: any) => (emp?.credentials || []).map((c: string) => hospObjById.get(c) ?? hospObjByName.get(c)).filter(Boolean).map((h: any) => ({ hospitalId: h.hospitalId, name: h.name, acronym: siteAcronym(h.name), color: colorForHospital(h) }));

  // The shift shown ON a provider card: their earliest shift for the day, carrying the pecking-order number
  // that is actually in force (a manual reorder on the Anesthesiologists tab beats the stored order), and
  // withheld entirely unless BOTH ends are present — the card would otherwise print "Invalid Date".
  const cardShiftFor = (id: string) => {
    const s = earliestShiftByEmp.get(id);
    return providerCardShift(s, effectivePeckingOrder(peckingOrderOverride, id, s?.order));
  };

  // ---- The provider card (EmployeeDetailsPopover) -------------------------------------------------------
  // ONE builder for every provider card on this page — the room nameplates and the bullpen bricks — so the
  // two can never drift apart. `room` is the placement the card was opened FROM; omit it (the bullpen) and
  // every room-relative section falls away: supervision scorecard, assigned rooms + room transfer, and the
  // per-placement recolor. That is behaviour-preserving, not a shortcut: the bullpen only lists people who
  // are in NO effective allocation (BoardView excludes `staffedEmployeeIds`), and all three of those fields
  // are derived from allocations, so they are empty for anyone the bullpen can show. Skipping them also
  // keeps ~100 bricks from each building a full room-transfer candidate list on every render.
  //
  // `boardActivityCoin` mirrors what RoomNamePlates computes for its own plates, so the bullpen renders the
  // identical header. NOTE that this coin header HIDES the site-lock block and the "Set color" row inside
  // EmployeeDetailsPopover, so on a doctor/CRNA card the site-lock below is deliberately dormant; students
  // (no coin header) do get it.
  type ProviderCardRoom = {
    staff: ReturnType<typeof getActiveStaffForRoom>;
    inherited: boolean;
    overrides: Record<string, string>;
  };
  const providerCardFor = (id: string, room?: ProviderCardRoom) => {
    const emp = employeeMap.get(id);
    const isDoc = emp?.employeeType === 'doctor';
    const isCrna = emp?.employeeType === 'nurse';
    const staff = room?.staff;
    // The room this card was opened from (for the CRNA site coin — a CRNA is single-room).
    const currentRoomHid = staff ? getOperatingRoomHospitalId(scheduleMap.get(staff.scheduleId)?.operatingRoomId || '') : null;
    // Site coin(s) for where this person IS today. Placed: their room(s). In the bullpen: the site the chief
    // hard-locked them to if there is one (a lock is a statement about today, and the coin header is the only
    // place a bullpen card can show it), else their home site so the coin never renders blank.
    const lockedHid = siteLocks[id];
    const atSites = room
      ? (isDoc ? atSitesFor(id) : (isCrna && currentRoomHid ? [coinForHid(currentRoomHid)] : undefined))
      : (lockedHid ? [coinForHid(lockedHid)] : (emp?.homeHospitalId ? [coinForHid(emp.homeHospitalId)] : undefined));
    return {
      employee: emp,
      shift: cardShiftFor(id),
      hospitalName: emp?.homeHospitalId ? hospitalNameById.get(emp.homeHospitalId) : undefined,
      supervision: (room && isDoc) ? supervisionFor(id) : undefined,
      assignedRooms: room ? (isDoc ? assignedRoomsFor(id) : (isCrna ? assignedRoomsForNurse(id) : undefined)) : undefined,
      reportInUtc: room && (isDoc || isCrna) ? reportInUtcFor(id, isDoc) : undefined,
      // Inherited (assumed) placements aren't real allocations, so no recolor.
      recolor: (staff && !room?.inherited) ? { current: room?.overrides[id], onPick: (c: string | null) => handleRecolor(staff.scheduleId, id, c) } : undefined,
      // Editable site coin (anyone) + room transfer (doctors only), both local & reversible.
      siteLock: emp ? { current: siteLocks[id], options: siteLockOptions, onPick: (hid: string | null) => setBoardSiteLock(id, hid) } : undefined,
      roomTransfer: room ? (isDoc ? { candidates: allCandidatesFor(id), candidateGroups: groupTransferCandidates(transferTargetDoctorIds.filter((t: string) => t !== id), doctorsBySite, nameOf, (hid: string) => hospitalNameById.get(hid) || '', boardSiteOrder, (hid: string) => { const cc = coinForHid(hid); return { color: cc.color, acronym: cc.acronym }; }, (cid: string) => peckingOrderOverride[cid] ?? (shiftByEmployeeId.get(cid)?.order ?? null)), candidatesForRoom: (rid: string) => roomCandidatesFor(rid, id), onMove: (roomId: string, toId: string) => moveRoom(roomId, toId), onMoveAll: (toId: string) => moveAllRooms(id, toId), onSwapAll: (otherId: string) => swapAllRooms(id, otherId), onSwapRoom: (roomId: string, otherId: string) => swapRoom(roomId, id, otherId) } : (isCrna ? crnaRoomTransferFor(id) : undefined)) : undefined,
      validSiteNames: hospitalSiteNames,
      atSites,
      // Colored credential coins + the Meeting / Request / Off disposition (shared per-day store).
      credentialSites: emp ? credentialCoinsFor(emp) : undefined,
      statusControl: (isDoc || isCrna)
        ? { statuses: statusByEmployee[id] || [], note: notesByEmployee[id] || '', onToggleStatus: (st: DoctorDayStatus) => toggleAnesStatus(id, st), onSetNote: (n: string) => setAnesNote(id, n),
            offEntries: offLogByEmployee[id], onSetOffTime: (i: number, field: 'off' | 'back', hm: string) => setAnesOffTime(id, i, field, hm) }
        : undefined,
      inherited: room?.inherited ?? false,
      boardActivityCoin: isDoc || isCrna,
    };
  };
  // A bullpen brick's card: the same card, with no placement behind it.
  const bullpenCardFor = (id: string) => providerCardFor(id);

  // ---- Late-staff counter (float row) ----------------------------------------------------------------
  // Two badges per site ("N late MDs" / "N late CRNAs") for end-of-day planning: who is still on this site
  // past 3pm, and who ELSE could be pulled here. "Late" = a shift worker whose shift ends after 3pm local, OR
  // a pecking-order provider who has NOT been sent home (they stay until the board clears). The badge COUNT is
  // the "here now" set (people in a room at this site who are late). Clicking opens a 2×2 popup:
  //   rows  = Here now (in a room here) | Credentialed here & available (elsewhere/bullpen, could move in)
  //   cols  = Pecking order | Shift work
  const lateOutHM = (id: string): string | undefined => { const e = getEmployeeShift(id)?.endTimeUtc; return e ? utcToLocalTime(e) : undefined; };
  const lateTo12h = (hm: string): string => { const [h, m] = hm.split(':').map(Number); if (Number.isNaN(h)) return hm; const ap = h >= 12 ? 'p' : 'a'; const hh = ((h + 11) % 12) + 1; return `${hh}:${String(m).padStart(2, '0')}${ap}`; };
  const lateOrderOf = (id: string): number | null => { const sh = getEmployeeShift(id); const o = effectivePeckingOrder(peckingOrderOverride, id, sh?.order); return o == null ? null : o; };
  const lateShiftEndsAfter3pm = (id: string): boolean => { const hm = lateOutHM(id); if (!hm) return false; const [h, m] = hm.split(':').map(Number); return (h * 60 + m) > 15 * 60; };
  type LatePerson = { id: string; name: string; order: number | null; outHM?: string };
  const lateStaffFor = (hospitalId: string, type: 'doctor' | 'nurse') => {
    const siteName = getHospitalName(hospitalId);
    const inRoomHere = (type === 'doctor' ? doctorsBySite.get(hospitalId) : crnasBySite.get(hospitalId)) || new Set<string>();
    const mk = (id: string): LatePerson => ({ id, name: nameOf(id), order: lateOrderOf(id), outHM: lateOutHM(id) });
    const hereNowPecking: LatePerson[] = [], hereNowShift: LatePerson[] = [];
    inRoomHere.forEach(id => {
      if (offDoctorIds.has(id)) return;                                  // sent home = gone, not "here now"
      if (lateOrderOf(id) != null) hereNowPecking.push(mk(id));          // pecking, not sent home → still here
      else if (lateShiftEndsAfter3pm(id)) hereNowShift.push(mk(id));     // shift worker out after 3pm → late
    });
    // Credentialed here + working today + not sent home + late, but NOT already here (a pull-in candidate).
    const credHereFor = (e: any) => credentialCoinsFor(e).some((c: any) => c.hospitalId === hospitalId) || (e.credentials || []).includes(siteName) || (e.credentials || []).includes(hospitalId);
    const credPecking: LatePerson[] = [], credShift: LatePerson[] = [];
    for (const e of employees) {
      if (e.employeeType !== type) continue;
      const id = e.employeeId;
      if (inRoomHere.has(id) || offDoctorIds.has(id) || !shiftEmployeeIds.has(id)) continue;
      if (!credHereFor(e)) continue;
      if (lateOrderOf(id) != null) credPecking.push(mk(id));
      else if (lateShiftEndsAfter3pm(id)) credShift.push(mk(id));
    }
    const byOrder = (a: LatePerson, b: LatePerson) => (a.order ?? 9999) - (b.order ?? 9999) || a.name.localeCompare(b.name);
    const byName = (a: LatePerson, b: LatePerson) => a.name.localeCompare(b.name);
    hereNowPecking.sort(byOrder); credPecking.sort(byOrder); hereNowShift.sort(byName); credShift.sort(byName);
    return { count: hereNowPecking.length + hereNowShift.length, hereNowPecking, hereNowShift, credPecking, credShift };
  };
  // Toggle a late-staff badge open/closed, capturing the click's screen position (like openTransferPicker)
  // so the popup can render position:fixed and escape its hospital-section's overflow:hidden.
  const POPUP_W = 360;
  const openLateCounter = (e: React.MouseEvent<HTMLButtonElement>, hospitalId: string, type: 'doctor' | 'nurse') => {
    if (lateCounter?.hospitalId === hospitalId && lateCounter?.type === type) { setLateCounter(null); return; }
    const btn = e.currentTarget.getBoundingClientRect();
    let left = btn.right - POPUP_W;
    if (left < 8) left = 8;
    if (left + POPUP_W > window.innerWidth - 8) left = Math.max(8, window.innerWidth - POPUP_W - 8);
    setLateCounter({ hospitalId, type, top: btn.bottom + 6, left });
  };
  const lateBadgeStyle = (count: number, color: string, active: boolean): React.CSSProperties => ({
    fontSize: '0.62em', fontWeight: 800, letterSpacing: '0.02em', textTransform: 'uppercase', padding: '3px 11px',
    borderRadius: '999px', whiteSpace: 'nowrap', cursor: 'pointer', fontFamily: 'var(--font-display)',
    background: active ? color : (count > 0 ? darkenHex(color, 0.15) : 'var(--bg-surface)'),
    color: (active || count > 0) ? '#fff' : 'var(--text-muted)',
    border: `1px solid ${count > 0 ? darkenHex(color, 0.25) : 'var(--border-subtle)'}`,
    textShadow: (active || count > 0) ? '0 1px 1px rgba(0,0,0,0.3)' : 'none',
  });
  const renderLatePopup = (hospitalId: string, type: 'doctor' | 'nurse', data: ReturnType<typeof lateStaffFor>, hospColor: string) => {
    const chip = (b: LatePerson) => {
      const plate = providerCardFor(b.id);
      const inner = (
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: '5px', maxWidth: '100%', padding: '3px 8px', borderRadius: '6px', background: 'var(--bg-surface-raised)', border: '1px solid var(--border-subtle)', cursor: 'pointer', fontSize: '0.82em' }}>
          {b.order != null && <span style={{ fontWeight: 800, color: 'var(--amber)', flexShrink: 0 }} title="Pecking order #">{b.order}</span>}
          <span style={{ fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{b.name}</span>
          {b.order == null && b.outHM && <span style={{ color: 'var(--text-faint)', fontSize: '0.85em', flexShrink: 0 }}>{lateTo12h(b.outHM)}</span>}
        </span>
      );
      return plate.employee
        ? <EmployeeDetailsPopover key={b.id} {...(plate as any)} triggerStyle={{ display: 'block', maxWidth: '100%' }}>{inner}</EmployeeDetailsPopover>
        : <span key={b.id}>{inner}</span>;
    };
    const colOf = (items: LatePerson[]) => items.length === 0
      ? <div style={{ fontSize: '0.78em', color: 'var(--text-faint)', fontStyle: 'italic', padding: '2px 0' }}>None</div>
      : <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', marginTop: '2px' }}>{items.map(chip)}</div>;
    const colHdr = (t: string) => <div style={{ fontSize: '0.62em', fontWeight: 800, letterSpacing: '0.4px', textTransform: 'uppercase', color: 'var(--text-faint)' }}>{t}</div>;
    const rowLbl = (t: string) => <div style={{ fontSize: '0.68em', fontWeight: 800, letterSpacing: '0.4px', textTransform: 'uppercase', color: darkenHex(hospColor, 0.2) }}>{t}</div>;
    // Fixed + viewport-clamped (not absolute) — this popup renders inside a hospital-section box that clips
    // overflow, so anchoring off-screen coordinates (captured on click, see openLateCounter) is what actually
    // keeps it visible on a site with only a few OR rows.
    const top = lateCounter?.top ?? 0;
    const left = lateCounter?.left ?? 0;
    const maxHeight = Math.min(440, Math.max(160, window.innerHeight - top - 16));
    return (
      <>
        <div onClick={() => setLateCounter(null)} style={{ position: 'fixed', inset: 0, zIndex: 60 }} />
        <div onClick={e => e.stopPropagation()} style={{ position: 'fixed', top: `${top}px`, left: `${left}px`, zIndex: 61, width: `${POPUP_W}px`, maxWidth: '92vw', maxHeight: `${maxHeight}px`, overflowY: 'auto', background: 'var(--bg-surface)', border: `1px solid ${hospColor}`, borderRadius: '9px', boxShadow: '0 10px 28px rgba(0,0,0,0.4)', padding: '11px 13px', textShadow: 'none' }}>
          <div style={{ fontWeight: 800, fontSize: '0.92em', color: 'var(--text-primary)' }}>Late {type === 'doctor' ? 'MDs' : 'CRNAs'} · {getHospitalName(hospitalId)}</div>
          <div style={{ fontSize: '0.68em', color: 'var(--text-faint)', marginBottom: '8px' }}>Tap a name for their card. Working past 3pm or on pecking order (not sent home).</div>
          {rowLbl('Here now')}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', margin: '4px 0 12px' }}>
            <div>{colHdr('Pecking order')}{colOf(data.hereNowPecking)}</div>
            <div>{colHdr('Shift work')}{colOf(data.hereNowShift)}</div>
          </div>
          {rowLbl('Credentialed here & available')}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginTop: '4px' }}>
            <div>{colHdr('Pecking order')}{colOf(data.credPecking)}</div>
            <div>{colHdr('Shift work')}{colOf(data.credShift)}</div>
          </div>
        </div>
      </>
    );
  };

  // "Who's free?" — providers on the clock at the parked time (targetTimeUtc) who are out of a room.
  // A person is FREE when: (1) not sent home ("off"); (2) on the clock — a shift-worker whose shift
  // window still covers the time, or a pecking-order person who has reported and not been sent home;
  // and (3) not currently in a room (no allocation of theirs spans the time). Grouped like the bullpen.
  const computeWhosFreeColumns = (): FreeColumn[] => {
    const T = new Date(targetTimeUtc).getTime();
    // Each employee's busy intervals from the raw allocations (schedule start/end).
    const intervals = new Map<string, { s: number; e: number }[]>();
    for (const a of allocations as any[]) {
      const sched = scheduleMap.get(a.operatingRoomScheduleId);
      if (!sched) continue;
      const s = new Date(sched.startTimeUtc).getTime();
      const e = new Date(sched.endTimeUtc).getTime();
      for (const id of [...(a.allocatedDoctorIds || []), ...(a.allocatedNurseIds || [])]) {
        if (!intervals.has(id)) intervals.set(id, []);
        intervals.get(id)!.push({ s, e });
      }
    }
    const docs: FreeBody[] = [], crnas: FreeBody[] = [], studs: FreeBody[] = [];
    for (const emp of employees as any[]) {
      const id = emp.employeeId;
      const shift = getEmployeeShift(id);
      if (!shift || !shift.startTimeUtc) continue;          // not working today
      if (offDoctorIds.has(id)) continue;                    // sent home — hidden
      const sStart = new Date(shift.startTimeUtc).getTime();
      const sEnd = shift.endTimeUtc ? new Date(shift.endTimeUtc).getTime() : Infinity;
      const order = effectivePeckingOrder(peckingOrderOverride, id, shift.order);
      const pecking = order !== null && order !== undefined;
      // Shift-worker: must be inside their window. Pecking: reported in and not yet sent home.
      const onClock = pecking ? (T >= sStart) : (T >= sStart && T < sEnd);
      if (!onClock) continue;
      const ivs = intervals.get(id) || [];
      if (ivs.some(iv => iv.s <= T && T < iv.e)) continue;   // still in a room at this time
      const endedBefore = ivs.filter(iv => iv.e <= T).map(iv => iv.e);
      const lastEnd = endedBefore.length ? Math.max(...endedBefore) : null;
      const subtitle = lastEnd ? `room ended ${utcToLocalTime(new Date(lastEnd).toISOString())}` : 'not yet placed';
      const tag = pecking ? `#${order}` : (shift.endTimeUtc ? `til ${utcToLocalTime(shift.endTimeUtc)}` : '');
      const type: string = emp.employeeType;
      const baseColor = type === 'doctor' ? doctorBullpenColor(shift)
        : type === 'nurse' ? crnaNameplateColor(shift)
        : studentColorFor(type, emp.studentCompetency);
      const body: FreeBody = {
        id, lastName: emp.lastName, fullName: `${emp.firstName} ${emp.lastName}`,
        color: overrideColorByEmployee.get(id) || baseColor, tag, subtitle,
        employee: emp, card: bullpenCardFor(id),
      };
      if (type === 'doctor') docs.push(body);
      else if (type === 'nurse') crnas.push(body);
      else if (type.startsWith('nurse-student')) studs.push(body);
    }
    const byName = (a: FreeBody, b: FreeBody) => a.lastName.localeCompare(b.lastName);
    docs.sort(byName); crnas.sort(byName); studs.sort(byName);
    return [
      { title: 'Anesthesiologists', items: docs },
      { title: 'CRNAs', items: crnas },
      { title: 'Students', items: studs },
    ];
  };

  const getSlotCandidates = (slotKind: 'doctor' | 'crna' | 'student', hospitalId?: string) => {
    if (slotKind === 'doctor') {
      const working = employees.filter((e: any) => e.employeeType === 'doctor' && shiftEmployeeIds.has(e.employeeId));
      let pool = working;
      if (hospitalId) {
        const atSite = working.filter((e: any) => doctorHospitals.get(e.employeeId)?.has(hospitalId));
        if (atSite.length) pool = atSite;   // scope to this site; fall back to all working docs if it's empty
      }
      return pool
        // Open capacity is measured "at the moment" (peak concurrent signatures — rooms going now or
        // starting within the hour), not the whole-day total, so a doctor who merely relieves into
        // afternoon rooms still reads as available. Matches the person card and the drop warning.
        .map((e: any) => { const cap = e.maxConcurrentNurseCoverageRatio ?? 4; return { e, open: cap - peakConcurrentSignaturesFor(e.employeeId) }; })
        .sort((a, b) => b.open - a.open || a.e.lastName.localeCompare(b.e.lastName))
        .map(x => ({ id: x.e.employeeId, label: `${x.e.firstName} ${x.e.lastName}`, hint: x.open > 0 ? `${x.open} sig${x.open === 1 ? '' : 's'} open` : 'full' }));
    }
    const matches = slotKind === 'crna'
      ? (t: string) => t === 'nurse'
      : (t: string) => t.startsWith('nurse-student');
    return employees
      .filter((e: any) => matches(e.employeeType) && shiftEmployeeIds.has(e.employeeId) && !staffedEmployeeIds.has(e.employeeId))
      .sort((a: any, b: any) => a.lastName.localeCompare(b.lastName))
      .map((e: any) => ({ id: e.employeeId, label: `${e.firstName} ${e.lastName}` }));
  };

  // Aggregate unmet needs across ALL rooms for the bullpen NEED nameplates: every room still showing a
  // NEED MD / NEED CRNA prompt, with that room's case times. Mirrors the per-room effNeed logic in the render.
  const boardShowGaps = hasAlgorithmAllocations();
  const doctorNeeds: { roomLabel: string; hospital: string; hospitalId: string; time: string }[] = [];
  const crnaNeeds: { roomLabel: string; hospital: string; hospitalId: string; time: string }[] = [];
  // The bullpen badge counts SUPERVISORS needed, not rooms (Braden 2026-09-19): "that may be 2 NEED MD in rooms and
  // then 3 supervisors needed for the other 9 rooms. So would be a total of 5 needed MDs not 11." A room with nobody
  // at all needs its own doctor (1:1); rooms that already have a CRNA share one, so they collapse at the site cap.
  // The per-room NEED MD boxes on the board are unchanged — every short room still shows one.
  const doctorNeedRooms: { hospitalId: string; hasCrna: boolean }[] = [];
  operatingRooms.forEach((room: any) => {
    const st = getActiveStaffForRoom(room.operatingRoomId);
    const rd = st?.doctors[0];
    const rc = st?.nurses.find((n: any) => n.employeeType === 'nurse');
    const rs = st?.nurses.find((n: any) => n.employeeType.startsWith('nurse-student'));
    const inh = st?.inherited || false;
    const dMark = getSlotMark(slotMarks, room.operatingRoomId, 'doctor');
    const cMark = getSlotMark(slotMarks, room.operatingRoomId, 'crna');
    // A doctor-SOLO room (algorithm 'doctor-only') shows a muted "NONE" in its CRNA slot, NOT "NEED CRNA" — a
    // finished solo decision, not a gap. It must not count as an unmet CRNA need in the bullpen either, or the
    // bullpen flags "need CRNA" for rooms whose OR slots correctly read NONE (mirror effNeedCrna in the render).
    const soloSid = st?.scheduleId;
    const soloAlloc: any = soloSid ? allocations.find((a: any) => a.operatingRoomScheduleId === soloSid) : undefined;
    // Mirror the board's solo rule (see render): a doctor supervising no CRNA is alone in the room, so its
    // CRNA slot reads NONE and it isn't an unmet CRNA need. Also honor an explicit doctor-only / override.
    // Mirror the timeline's day-wide solo test (see render, `!daySupervisorDoctorIds.has(d.id)`): a doctor who
    // supervises a CRNA ANYWHERE today is never "solo", so their empty signature rooms stay an unmet CRNA need.
    // Without this guard the bullpen used only the time-local supervisedCrnaCount and dropped all of a
    // supervising doctor's empty rooms the moment their live CRNA count read 0 — so staffing one slot made the
    // bullpen NEED-CRNA boxes vanish while the timeline still showed NEED.
    const soloCommitted = !!rd && !daySupervisorDoctorIds.has(rd.id) && (supervisedCrnaCount(rd.id) === 0 || (!!soloAlloc && (soloAlloc.allocationType === 'doctor-only' || matchesPaletteColor('docSolo', (soloAlloc.colorOverrides || {})[rd.id]))));
    const needDoc = !rd && (dMark === 'need' || (dMark !== 'none' && boardShowGaps && !!st && !inh));
    // Gate on `!!st` (room context), NOT `!!rd`: a fully-empty room (no doctor) is an unmet CRNA need too, so the
    // bullpen shows a NEED-CRNA nameplate for it — mirrors the per-room NEED CRNA on an empty room.
    const needCr = !rc && !rs && (cMark === 'need' || (cMark !== 'none' && boardShowGaps && !!st && !inh && !soloCommitted));
    if (!needDoc && !needCr) return;
    // ONE time, not the room's whole case list: the case that actually needs staffing. That is the
    // room's GOVERNING case at the board's current time (getActiveStaffForRoom's scheduleId) -- so a
    // room whose CRNA was sent home and whose next case is at 13:00 reads "13:00", which is the thing
    // the runner has to fill. Falls back to the room's first case if there is no governing one.
    const neededSchedule = (st?.scheduleId ? scheduleMap.get(st.scheduleId) : undefined)
      || getSchedulesForRoom(room.operatingRoomId)[0];
    const entry = {
      roomLabel: room.name || room.roomNumber || room.operatingRoomId,
      hospital: getHospitalName(room.hospitalId),
      hospitalId: room.hospitalId,
      time: neededSchedule ? utcToLocalTime(neededSchedule.startTimeUtc) : '',
    };
    if (needDoc) {
      doctorNeeds.push(entry);
      doctorNeedRooms.push({ hospitalId: room.hospitalId, hasCrna: !!(rc || rs) });
    }
    if (needCr) crnaNeeds.push(entry);
  });
  const byFirstTime = (a: { time: string }, b: { time: string }) => (a.time || '').localeCompare(b.time || '');
  doctorNeeds.sort(byFirstTime);
  crnaNeeds.sort(byFirstTime);
  // Per SITE, because one anesthesiologist cannot supervise across hospitals. 3 at a children's hospital, else 4 —
  // the same site cap the backend applies (siteSupervisionLimit).
  const doctorNeedCount = supervisorsNeeded(doctorNeedRooms,
    (hid) => ((hospObjById.get(hid) as any)?.isChildrensHospital ? 3 : 4));

  return (
    <>
      {toast && (
        <Toast
          message={toast.text}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}
      <div
        ref={boardLayoutRef}
        className={`board-and-shifts ${isSidebarOpen ? 'sidebar-open' : ''} ${!isBullpenOpen ? 'bullpen-collapsed' : ''} ${isResizingSidebar ? 'resizing' : ''}`}
        style={{ '--sites-width': `${sitesWidth}px`, '--bullpen-width': `${bullpenWidth}px` } as React.CSSProperties}
      >
      {isSidebarOpen && (
        <SidebarResizeHandle label="Sites" side="left" width={sitesWidth} range={sitesRange} onResize={setSitesWidth} onDraggingChange={setIsResizingSidebar} />
      )}
      {isBullpenOpen && (
        <SidebarResizeHandle label="Bullpen" side="right" width={bullpenWidth} range={bullpenRange} onResize={setBullpenWidth} onDraggingChange={setIsResizingSidebar} />
      )}
      <HospitalORSidebar
        hospitals={hospitals}
        operatingRoomsByHospital={operatingRoomsByHospital}
        getSchedulesForRoom={getSchedulesForRoom}
        getHospitalName={getHospitalName}
        onScrollToRoom={scrollToRoom}
        onCreateSchedule={handleSidebarCreateSchedule}
        isOpen={isSidebarOpen}
        onToggle={() => setIsSidebarOpen(prev => !prev)}
        viewMode={viewMode}
        forcedActiveHospitalIds={forcedActiveHospitalIds}
        onToggleForcedHospital={handleToggleForcedHospital}
      />
      {/* Mobile toggle button */}
      <button 
        className="mobile-toggle-btn"
        onClick={() => setShowShiftsCard(!showShiftsCard)}
      >
        {showShiftsCard ? 'Show Board' : 'Show Shifts'}
      </button>

      <div className={`card board-card ${showShiftsCard ? 'mobile-hidden' : ''}`} style={{ minWidth: 0 }}>
        <h1 style={{ fontFamily: 'var(--font-display)', fontWeight: 700 }}>Operating Room Board</h1>
      
      {/* Mobile hospital selector */}
      {/* Mobile site selector — writes the same selectedHospitalIds Set as the LOCATION picker. A
          native <select> can only express "all" or exactly one site; combinations come from the
          LOCATION dropdown on the board. */}
      <div className="mobile-hospital-selector">
        <label htmlFor="hospital-select">Site:</label>
        <select
          id="hospital-select"
          value={selectedHospitalIds.size === 1 ? Array.from(selectedHospitalIds)[0] : ''}
          onChange={(e) => setSelectedHospitalIds(e.target.value ? new Set([e.target.value]) : new Set<string>())}
        >
          <option value="">All Sites</option>
          {hospitals.map(h => (
            <option key={h.hospitalId} value={h.hospitalId}>{h.name}</option>
          ))}
        </select>
      </div>

      <div className="form-group">
        {/* No "Select Date:" caption -- the date input is self-evident, and dropping it pulls the whole
            controls row up by the label's line box plus the global `label { margin-bottom: 8px }`
            (index.css). The caption was the input's only accessible name, so it moves to aria-label
            rather than being lost. .form-group's own margin-bottom is left alone: that is the gap BELOW
            this row and is shared by every form in the app. */}
        <div className="board-controls" style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
          <input
            type="date"
            id="date"
            aria-label="Board date"
            value={selectedDate}
            onChange={(e) => setSelectedDate(e.target.value)}
            className="form-control"
            style={{ width: 'auto' }}
          />
          {/* A day already in the cache is shown at once and re-checked in the background; say so quietly. */}
          <DataFreshnessNote refreshing={day.refreshing} refreshFailed={day.refreshFailed} onRetry={() => { void day.refetchAll(); }} />
          <select
            value={viewMode}
            onChange={(e) => setViewMode(e.target.value as 'active' | 'all')}
            className="form-control"
            style={{ width: 'auto' }}
          >
            <option value="active">Active</option>
            <option value="all">All</option>
          </select>
          <AdminOnly>
            <button
              onClick={handleAllocate}
              disabled={isAllocateDisabled({
                hasSelectedDate: !!selectedDate,
                allocating,
                loading,
                hasPersistedAllocations: hasPersistedAllocations(),
                isModifying,
                pinCount: manualAllocations.length,
              })}
              className="btn btn-primary"
            >
              {allocating
                ? (allocationProgress || 'Allocating...')
                : isModifying
                  ? 'Apply Modifications'
                  : (hasPersistedAllocations() && manualAllocations.length > 0)
                    ? 'Allocate with pins'
                    : 'Allocate Staff'}
            </button>
            {!isModifying && manualAllocations.length > 0 && hasPersistedAllocations() && (
              <button
                onClick={handleClearPins}
                disabled={allocating}
                className="btn btn-secondary"
              >
                Clear pins
              </button>
            )}
            {isModifying && (
              <button
                onClick={() => {
                  setIsModifying(false);
                  setModifiedAllocations(new Map());
                }}
                className="btn btn-secondary"
              >
                Cancel Modifications
              </button>
            )}
            {/* Wipe / Submit / Draw Case travel together: the toolbar row wraps, and these three read as one
                group, so nowrap keeps them on a single line instead of splitting across rows (Braden 2026-09-30). */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'nowrap' }}>
              <button
                onClick={handleWipeAllocations}
                disabled={!selectedDate || wiping || loading}
                className="btn btn-danger"
              >
                {wiping ? 'Wiping...' : 'Wipe Allocations'}
              </button>
              {/* Submit the schedule: freezes the edited board + move log as a snapshot to grade side-by-side against Symphony. */}
              <button
                onClick={handlePublishDay}
                disabled={!selectedDate || publishing || loading || !hasPersistedAllocations()}
                className="btn btn-success-outline"
                title="Freeze this day (edited board + move log) so it can be graded side-by-side against Symphony"
              >
                {publishing ? 'Submitting...' : 'Submit Schedule'}
              </button>
              {/* Draw an add-on case: click a start time on a room, then an end time, then type the name on the block. */}
              <button
                onClick={() => { setDrawMode(v => !v); setScheduleSelection({ operatingRoomId: null, startMinutes: null, endMinutes: null }); }}
                className={`btn ${drawMode ? 'btn-primary' : 'btn-secondary'}`}
                title="Draw an add-on case: click a start time on a room, then an end time, then type the case name"
              >
                {drawMode ? 'Drawing — click start, then end' : 'Draw Case'}
              </button>
            </div>
          </AdminOnly>
          {/* Undo the last persisted move/swap (Option B: moves rewrite the allocation; this reverses them). */}
          <AdminOnly>
            <button
              onClick={undoLastMove}
              className="btn btn-secondary"
              disabled={undoStack.length === 0}
              title={undoStack.length ? `Undo: ${undoStack[undoStack.length - 1].label}` : 'Nothing to undo'}
              style={{ marginLeft: 'auto' }}
            >
              ↩ Undo{undoStack.length > 1 ? ` (${undoStack.length})` : ''}
            </button>
          </AdminOnly>
          {/* The Move Log button now lives at the very bottom of the bullpen (see the right sidebar). */}
        </div>
      </div>

      <ReadOnlyBanner />

      {/* Move Log — a passive per-day journal of the manual moves made on the board. Floats over the
          board on the right; reviewing it never changes the allocation. */}
      {showMoveLog && (
        <div style={{ position: 'fixed', top: 0, right: 0, bottom: 0, width: 'min(430px, 92vw)', background: 'var(--bg-surface)', borderLeft: '1px solid var(--border-subtle)', boxShadow: '-4px 0 18px rgba(0,0,0,0.28)', zIndex: 1000, display: 'flex', flexDirection: 'column' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 14px', borderBottom: '1px solid var(--border-subtle)' }}>
            <strong style={{ fontSize: '0.95em' }}>Move Log — {selectedDate}</strong>
            <button className="btn btn-secondary" style={{ padding: '2px 10px' }} onClick={() => setShowMoveLog(false)}>Close</button>
          </div>
          <div style={{ flex: 1, overflowY: 'auto' }}>
            {moveLog.length === 0 ? (
              <div style={{ padding: '16px', color: 'var(--text-faint)', fontStyle: 'italic', fontSize: '0.85em', lineHeight: 1.5 }}>
                No manual moves logged yet today. Drag staff onto or off a room, hand a room to another anesthesiologist, or lock someone to a site — each move is recorded here with a timestamp.
              </div>
            ) : (
              [...moveLog].reverse().map((m, i) => (
                <div key={i} style={{ padding: '7px 14px', borderBottom: '1px solid var(--border-subtle)', fontSize: '0.84em', display: 'flex', gap: '9px', alignItems: 'baseline' }}>
                  <span style={{ color: 'var(--text-faint)', whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }}>{fmtLogTime(m.ts)}</span>
                  <span>{moveIcon(m.kind)} {m.detail}</span>
                </div>
              ))
            )}
          </div>
          <div style={{ padding: '10px 14px', borderTop: '1px solid var(--border-subtle)', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '10px' }}>
            <span style={{ fontSize: '0.76em', color: 'var(--text-faint)' }}>{moveLog.length} move{moveLog.length === 1 ? '' : 's'} · saved on this device only</span>
            {moveLog.length > 0 && (
              <button className="btn btn-danger" style={{ padding: '2px 10px' }}
                onClick={() => { if (window.confirm('Clear the move log for this day? This only clears the local journal — it does not change the board.')) setMoveLog(clearMoveLog(selectedDate)); }}>
                Clear log
              </button>
            )}
          </div>
        </div>
      )}

      {/* Master time slider */}
      <div className="form-group" style={{ marginBottom: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
          <label htmlFor="timeSlider" style={{ margin: 0 }}>
            {whosFree ? "Who's free at" : isLiveBar ? 'Now' : 'Target Time'}: <strong style={whosFree ? { color: WHOS_FREE_PURPLE } : undefined}>{formatSliderTime(targetMinutes)}</strong>
          </label>
          {!whosFree && (
            <button
              type="button"
              onClick={enterWhosFree}
              title="See who's on the clock and out of a room — then drag the slider to any time"
              style={{
                display: 'inline-flex', alignItems: 'center', gap: '5px',
                fontSize: '11px', fontWeight: 700, letterSpacing: '0.04em',
                color: WHOS_FREE_PURPLE, background: 'transparent',
                border: `1px solid ${WHOS_FREE_PURPLE}`, borderRadius: '999px',
                padding: '2px 10px', cursor: 'pointer',
              }}
            >
              <span style={{ width: '7px', height: '7px', borderRadius: '50%', background: WHOS_FREE_PURPLE, boxShadow: `0 0 6px ${WHOS_FREE_PURPLE}` }} />
              Who's free?
            </button>
          )}
          {whosFree ? (
            <button
              type="button"
              onClick={exitWhosFree}
              title="Leave who's-free mode and return to the bullpen"
              style={{
                fontSize: '11px', fontWeight: 700, letterSpacing: '0.04em',
                color: WHOS_FREE_PURPLE, background: 'transparent',
                border: `1px solid ${WHOS_FREE_PURPLE}`, borderRadius: '999px',
                padding: '2px 10px', cursor: 'pointer',
              }}
            >
              {isLocalMode() ? '← Back to bullpen' : '● Jump to now'}
            </button>
          ) : isLiveBar ? (
            <span
              title="The blue bar is tracking the current time and advances every minute — drag its knob to check who's free"
              style={{
                display: 'inline-flex', alignItems: 'center', gap: '5px',
                fontSize: '11px', fontWeight: 600, letterSpacing: '0.06em', textTransform: 'uppercase',
                color: 'var(--or-blue-mid)', border: '1px solid var(--or-blue-mid)',
                borderRadius: '999px', padding: '1px 9px',
              }}
            >
              <span style={{ width: '7px', height: '7px', borderRadius: '50%', background: 'var(--or-blue-mid)', boxShadow: '0 0 6px var(--or-blue-mid)' }} />
              Live
            </span>
          ) : !isLocalMode() ? (
            <button
              type="button"
              onClick={() => setLiveNow(true)}
              title="Snap the blue bar back to the current time and resume live tracking"
              style={{
                fontSize: '11px', fontWeight: 600, letterSpacing: '0.04em',
                color: 'var(--or-blue-mid)', background: 'transparent',
                border: '1px solid var(--or-blue-mid)', borderRadius: '999px',
                padding: '2px 10px', cursor: 'pointer',
              }}
            >
              ● Jump to now
            </button>
          ) : null}
        </div>
        <input
          id="timeSlider"
          type="range"
          min={0}
          max={1440}
          step={15}
          value={targetMinutes}
          onChange={onSliderChange}
          style={{ width: '100%', accentColor: whosFree ? WHOS_FREE_PURPLE : undefined }}
        />
      </div>

      {loading && <p>Loading...</p>}
      {error && <div className="alert alert-error">{error}</div>}

      {!loading && !error && allocationsLoaded && allocations.length === 0 && (
        <div className="alert alert-warning">
          <strong>No Allocation Information Available</strong>
          <p>Click the <strong>"Allocate Staff"</strong> button above to generate assignments.</p>
        </div>
      )}

      {!loading && !error && (
        <div className="board-container" data-palette-version={paletteVersion}>
          {/* LOCATION picker: which hospitals and surgery centres the board shows. The per-site counts
              live in each site's float row and load strip, so there is no combined total here. */}
          {(() => {
            const picked = Array.from(selectedHospitalIds);
            const label = picked.length === 0 ? 'All Locations'
              : picked.length === 1 ? getHospitalName(picked[0])
              : `${picked.length} locations`;
            // Layout only -- no panel chrome. This used to be a raised, bordered box because it also held
            // the "N ORs after 3pm" and "peak N concurrent" pills; with those gone it was a grey box around a
            // single dropdown, so the background/border/radius/padding came off and the picker stands alone.
            // (A JSX {/* */} comment cannot sit here -- at the root of a `return (` it parses as a second
            //  sibling expression, which is the syntax error this replaced.)
            return (
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap', marginBottom: '10px' }}>
                <span style={{ position: 'relative', display: 'inline-block' }}>
                  <button type="button" onClick={() => setLocationPickerOpen(o => !o)}
                    title="Pick which hospitals and surgery centres the board shows and these totals cover"
                    style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', padding: '5px 12px', borderRadius: '8px', cursor: 'pointer', background: 'var(--bg-surface)', border: '1px solid var(--border-subtle)', color: 'var(--text-primary)', fontFamily: 'var(--font-display)', fontSize: '0.74em', fontWeight: 700 }}>
                    <span style={{ fontSize: '0.78em', fontWeight: 800, letterSpacing: '0.06em', textTransform: 'uppercase', color: 'var(--text-muted)' }}>Location</span>
                    {label}
                    <span style={{ opacity: 0.7 }}>{locationPickerOpen ? '\u25B2' : '\u25BC'}</span>
                  </button>
                  {locationPickerOpen && (
                    <>
                      <div onClick={() => setLocationPickerOpen(false)} style={{ position: 'fixed', inset: 0, zIndex: 1200 }} />
                      <div style={{ position: 'absolute', top: '100%', left: 0, marginTop: '6px', zIndex: 1201, minWidth: '260px', maxHeight: '320px', overflowY: 'auto', background: 'var(--bg-surface)', border: '1px solid var(--border-subtle)', borderRadius: '10px', boxShadow: '0 10px 30px rgba(0,0,0,0.4)', padding: '6px' }}>
                        <button type="button" onClick={() => setSelectedHospitalIds(new Set<string>())}
                          style={{ display: 'flex', alignItems: 'center', gap: '8px', width: '100%', textAlign: 'left', padding: '7px 10px', border: 'none', borderBottom: '1px solid var(--border-subtle)', background: 'transparent', color: 'var(--text-primary)', cursor: 'pointer', fontSize: '0.8em', fontWeight: 700 }}>
                          <input type="checkbox" readOnly checked={selectedHospitalIds.size === 0} style={{ pointerEvents: 'none' }} />
                          All Locations
                        </button>
                        {hospitals.map(h => (
                          <button type="button" key={h.hospitalId}
                            onClick={() => setSelectedHospitalIds(prev => toggleHospitalIdInSet(prev, h.hospitalId))}
                            style={{ display: 'flex', alignItems: 'center', gap: '8px', width: '100%', textAlign: 'left', padding: '7px 10px', border: 'none', background: 'transparent', color: 'var(--text-primary)', cursor: 'pointer', fontSize: '0.8em' }}>
                            <input type="checkbox" readOnly checked={selectedHospitalIds.has(h.hospitalId)} style={{ pointerEvents: 'none' }} />
                            <span style={{ width: '9px', height: '9px', borderRadius: '2px', background: getHospitalColor(h.hospitalId), flex: '0 0 9px' }} />
                            <span style={{ whiteSpace: 'nowrap' }}>{h.name}</span>
                          </button>
                        ))}
                      </div>
                    </>
                  )}
                </span>
              </div>
            );
          })()}
          {Object.entries(operatingRoomsByHospital).filter(([hospitalId]) =>
            siteSelected(hospitalId)
          ).map(([hospitalId, rooms]) => {
            // Filter rooms by viewMode first
            const filteredRooms = rooms.filter(room => {
              // In 'active' mode, show rooms with schedules OR rooms whose hospital is force-included
              if (viewMode === 'active') {
                // A C-section room with nothing booked still shows when a CRNA is held there for an add-on:
                // somebody is stationed in it, so hiding it would hide the assignment (Braden 2026-09-16).
                const hasSchedules = getSchedulesForRoom(room.operatingRoomId).length > 0
                  || (obHold[room.operatingRoomId] || []).length > 0;
                return shouldShowRoomInActive(room.hospitalId, hasSchedules, forcedActiveHospitalIds);
              }
              // In 'all' mode, show all rooms
              return true;
            });

            // Sort rooms alphabetically by name
            filteredRooms.sort((a, b) => a.name.localeCompare(b.name));

            // Only render if there are filtered rooms
            if (filteredRooms.length === 0) return null;

            // Per-hospital color: base outline + a darker header bar. The inside keeps the normal
            // theme background (day/night) so the cases sit on the usual surface, not a colored tint.
            const hospColor = getHospitalColor(hospitalId);
            const headerColor = darkenHex(hospColor, 0.4);

            // Case-load windows for this site, in local minutes-of-day — the single basis for the LIVE
            // "ORs running" counter, the after-3pm bubble and the load strip below. One entry per case,
            // tagged with its room so every count is of distinct ORs rather than cases. Anchored to
            // selectedDate: the schedule search is overlap-based, so last night's case running into this
            // morning is in `schedules` too, and must not be counted as running tonight (see caseWindow).
            const siteCaseWindows = rooms.flatMap(room =>
              getSchedulesForRoom(room.operatingRoomId)
                .map(s => caseWindow(s.startTimeUtc, s.endTimeUtc, room.operatingRoomId, hospitalId, selectedDate))
                .filter((w): w is CaseWindow => w !== null));
            // LIVE (Braden 2026-10-04): how many ORs are running at this site AT THE BOARD'S CURRENT TIME,
            // so the header counts down through the day — 30 at the 07:00 open, 26 by 09:00, and so on.
            // targetMinutes tracks the live clock while liveNow is on and follows the time bar when the
            // runner scrubs it. This was previously the day's room count, deliberately held static.
            const runningRoomCount = roomsRunningAt(siteCaseWindows, targetMinutes);
            // The DAY's room count (any case today). Still needed for the after-3pm tooltip, which compares
            // against the whole day — against the live count it would read "3 of 0" before the first case.
            const siteDayRoomCount = rooms.filter(room => getSchedulesForRoom(room.operatingRoomId).length > 0).length;
            const siteAfter3pmRooms = roomsRunningAfter(siteCaseWindows, AFTER_HOURS_CUTOFF_MIN);
            const siteLoadBuckets = peakRoomsPerBucket(siteCaseWindows);
            const sitePeakRooms = peakConcurrency(siteLoadBuckets);

            // Anesthesiologist "coins" for the header: one small nameplate per anesthesiologist
            // currently shown in a room at this site, colored exactly like their board nameplate —
            // light blue = supervising CRNA(s), fuchsia = solo in a room, or the shift-end colour
            // (out 3/5/7pm) for a shift worker, or a manual recolor if the user set one. De-duped across
            // rooms and ordered SOLO FIRST, then shift work before pecking order, then by when they
            // leave — so a doctor alone in a room leads the row and the rightmost coin is the last one
            // out (doctorCoinSortKey; Braden 2026-10-04).
            const DOC_STATUS_LABEL: Record<string, string> = {
              [NAMEPLATE_COLORS.docSupervise]: 'Supervising CRNA(s)',
              [NAMEPLATE_COLORS.docSolo]: 'Solo in a room',
              [NAMEPLATE_COLORS.docOut3pm]: 'Out 3pm',
              [NAMEPLATE_COLORS.docOut5pm]: 'Out 5pm',
              [NAMEPLATE_COLORS.docOut7pm]: 'Out 7pm',
            };
            const siteDoctorCoins = (() => {
              const byId = new Map<string, { id: string; label: string; name: string; status?: string; color: string; employee?: ReturnType<typeof employeeMap.get>; supervision?: ReturnType<typeof supervisionFor> }>();
              rooms.forEach(room => {
                const staff = getActiveStaffForRoom(room.operatingRoomId);
                if (!staff) return;
                staff.doctors.forEach(doc => {
                  if (byId.has(doc.id)) return;
                  const color = overrideColorByEmployee.get(doc.id)
                    || doctorNameplateColor(getEmployeeShift(doc.id), supervisedCrnaCount(doc.id));
                  // name + status power the coin's hover; employee + supervision drive the signature dots
                  // (identical to the nameplate hover, via the shared DoctorCoin / SigDots).
                  byId.set(doc.id, { id: doc.id, label: doc.lastName, name: doc.fullName, status: DOC_STATUS_LABEL[color], color, employee: employeeMap.get(doc.id), supervision: supervisionFor(doc.id) });
                });
              });
              const keyFor = (id: string) => {
                const sh = getEmployeeShift(id);
                return doctorCoinSortKey(
                  effectivePeckingOrder(peckingOrderOverride, id, sh?.order),
                  sh?.startTimeUtc ? utcToLocalTime(sh.startTimeUtc) : undefined,
                  sh?.endTimeUtc ? utcToLocalTime(sh.endTimeUtc) : undefined,
                  supervisedCrnaCount(id) === 0,   // solo in a room -> leads the row
                );
              };
              return [...byId.values()].sort((a, b) => {
                const ka = keyFor(a.id), kb = keyFor(b.id);
                // last name only as a tie-break, so equal out-times keep a stable order across renders
                return ka.solo - kb.solo || ka.group - kb.group || ka.key - kb.key || a.label.localeCompare(b.label);
              });
            })();

            // Site-level FLOAT CRNA slots — the Simulate tab sets the per-day count (floatSlotMap); the
            // runner pre-assigns a CRNA into each empty box. Candidates = CRNAs on shift not already used.
            const floatSlots = Math.max(0, floatSlotsAt(floatSlotMap, hospitalId));
            // A drag-created float slot lives only in the per-day overlay (floatCrnas), so the row's real slot
            // count is the larger of the site's configured slots and however many the overlay already holds.
            const effectiveFloatSlots = Math.max(floatSlots, floatCrnas[hospitalId]?.length ?? 0);
            const floatUsedIds = floatCrnaEmployeeIds(floatCrnas);
            const availableFloatCrnaList = effectiveFloatSlots > 0
              ? employees
                  .filter((e: any) => e.employeeType === 'nurse' && shiftEmployeeIds.has(e.employeeId) && !staffedEmployeeIds.has(e.employeeId) && !reliefEmployeeIds.has(e.employeeId) && !floatUsedIds.has(e.employeeId) && !offDoctorIds.has(e.employeeId))
                  .sort((a: any, b: any) => a.lastName.localeCompare(b.lastName))
              : [];

            // Late-staff counters for the float row: who's still here past 3pm (MDs / CRNAs), for planning moves.
            const lateMDs = lateStaffFor(hospitalId, 'doctor');
            const lateCRNAs = lateStaffFor(hospitalId, 'nurse');

            return (
              <div key={hospitalId} className="hospital-section" style={{ border: `2px solid ${hospColor}`, borderRadius: 'var(--radius-lg)', overflow: 'hidden' }}>
                <h2 style={{ margin: 0, fontFamily: 'var(--font-display)', fontWeight: 700, padding: '10px 16px', background: headerColor, color: '#fff', borderBottom: 'none', borderRadius: 0, textShadow: '0 1px 2px rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px', flexWrap: 'wrap' }}>
                  <span style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap', minWidth: 0 }}>
                    <span>{getHospitalName(hospitalId)}</span>
                    {siteDoctorCoins.length > 0 && (
                      <span style={{ display: 'flex', gap: '4px', flexWrap: 'wrap', alignItems: 'center' }}>
                        {siteDoctorCoins.map(doc => (
                          <DoctorCoin key={doc.id} label={doc.label} color={doc.color} name={doc.name} status={doc.status} employee={doc.employee} supervision={doc.supervision} />
                        ))}
                      </span>
                    )}
                  </span>
                  {/* Both counters share the header's right-hand side and the same pill styling
                      (Braden 2026-10-04). "running" is LIVE and moves with the time bar; "after 3pm" is
                      day-wide and does not. */}
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                    {/* TWO numbers, both labelled (Braden 2026-10-10). The live count alone read as the
                        site's size: at 07:19 on 10/10 County Hospital 1 showed "1 OR running" while four
                        ORs ran that day, because only one had opened yet. The live figure keeps the
                        emphasis -- it is the countdown asked for on 10/04 -- and the day figure sits muted
                        beside it so the site's real extent is never hidden. "today", not "total": this is
                        rooms with a case on the SELECTED DATE, not the site's physical room count. */}
                    <span title={isLiveBar
                      ? `${runningRoomCount} of this site's ${siteDayRoomCount} OR(s) are running at ${formatSliderTime(targetMinutes)} (live). ${siteDayRoomCount} run at some point today.`
                      : `${runningRoomCount} of this site's ${siteDayRoomCount} OR(s) are running at ${formatSliderTime(targetMinutes)} — the target time you set, not the live clock. ${siteDayRoomCount} run at some point today.`}
                      style={{ fontSize: '0.7em', fontWeight: 700, background: 'rgba(255,255,255,0.2)', padding: '3px 12px', borderRadius: '999px', whiteSpace: 'nowrap', textShadow: 'none', letterSpacing: '0.02em' }}>
                      {runningRoomCount} running
                      <span style={{ opacity: 0.6, fontWeight: 600 }}> · {siteDayRoomCount} today</span>
                    </span>
                    <span title={`${siteAfter3pmRooms} of this site's ${siteDayRoomCount} OR(s) today have a case going at or past 3:00 PM (or starting at 3:00 PM)`}
                      style={{ fontSize: '0.7em', fontWeight: 700, background: 'rgba(255,255,255,0.2)', padding: '3px 12px', borderRadius: '999px', whiteSpace: 'nowrap', textShadow: 'none', letterSpacing: '0.02em' }}>
                      {siteAfter3pmRooms} {siteAfter3pmRooms === 1 ? 'OR' : 'ORs'} after 3pm
                    </span>
                  </span>
                </h2>

                {/* Float CRNAs — site-level slots under the doctor coins. Empty boxes are click-to-assign
                    (admin); a filled box shows the CRNA's last name with an × to clear. Admins ALWAYS see the
                    row (even at 0 slots) so an available CRNA can be DRAGGED onto the drop zone to float them,
                    which creates a slot on the fly. Per-day, reversible. */}
                {(effectiveFloatSlots > 0 || isAdmin || lateMDs.count > 0 || lateCRNAs.count > 0) && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap', padding: '6px 16px', background: 'var(--bg-surface-raised)', borderBottom: `1px solid ${hospColor}` }}>
                    <span style={{ fontSize: '0.66em', fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em', fontFamily: 'var(--font-display)' }}>Float CRNAs</span>
                    {Array.from({ length: effectiveFloatSlots }).map((_, i) => {
                      const occ = floatCrnaAt(floatCrnas, hospitalId, i);
                      const emp = occ ? employeeMap.get(occ) : undefined;
                      const pickerOpen = floatPicker?.hospitalId === hospitalId && floatPicker?.index === i;
                      if (occ && emp) {
                        return (
                          <span key={i} title={`${emp.firstName} ${emp.lastName} — float CRNA`} style={{ display: 'inline-flex', alignItems: 'center', gap: '5px', fontSize: '0.62em', fontWeight: 700, padding: '2px 5px 2px 10px', borderRadius: '999px', background: crnaNameplateColor(getEmployeeShift(occ)), color: '#fff', whiteSpace: 'nowrap', border: '1px solid rgba(255,255,255,0.45)', textShadow: '0 1px 1px rgba(0,0,0,0.35)' }}>
                            {emp.lastName}
                            {isAdmin && (
                              <button type="button" onClick={() => clearFloatCrna(hospitalId, i)} title="Remove float CRNA" style={{ border: 'none', background: 'rgba(0,0,0,0.28)', color: '#fff', borderRadius: '999px', width: '15px', height: '15px', lineHeight: '13px', cursor: 'pointer', padding: 0, fontSize: '12px' }}>×</button>
                            )}
                          </span>
                        );
                      }
                      return (
                        <span key={i} style={{ position: 'relative', display: 'inline-block' }}>
                          <button type="button" disabled={!isAdmin} onClick={() => setFloatPicker(pickerOpen ? null : { hospitalId, index: i })} title={isAdmin ? 'Assign a float CRNA' : 'Float CRNA (empty)'} style={{ fontSize: '0.62em', fontWeight: 700, padding: '3px 11px', borderRadius: '999px', background: 'transparent', color: 'var(--text-muted)', border: `1px dashed ${hospColor}`, cursor: isAdmin ? 'pointer' : 'default', whiteSpace: 'nowrap' }}>
                            Float CRNA +
                          </button>
                          {pickerOpen && (
                            <>
                              <div onClick={() => setFloatPicker(null)} style={{ position: 'fixed', inset: 0, zIndex: 40 }} />
                              <div style={{ position: 'absolute', top: '100%', left: 0, marginTop: '4px', zIndex: 41, minWidth: '190px', maxHeight: '260px', overflowY: 'auto', background: 'var(--bg-surface)', border: '1px solid var(--border-subtle)', borderRadius: '6px', boxShadow: '0 6px 20px rgba(0,0,0,0.35)' }}>
                                {availableFloatCrnaList.length === 0 ? (
                                  <div style={{ padding: '8px 10px', fontSize: '0.75em', color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>No available CRNAs</div>
                                ) : availableFloatCrnaList.map((c: any) => (
                                  <button type="button" key={c.employeeId} onClick={() => assignFloatCrna(hospitalId, i, c.employeeId)} style={{ display: 'block', width: '100%', textAlign: 'left', padding: '6px 10px', border: 'none', borderBottom: '1px solid var(--border-subtle)', background: 'transparent', color: 'var(--text-primary)', cursor: 'pointer', fontSize: '0.78em', whiteSpace: 'nowrap' }}>
                                    {c.lastName}, {c.firstName}
                                  </button>
                                ))}
                              </div>
                            </>
                          )}
                        </span>
                      );
                    })}
                    {isAdmin && <FloatDropZone hospColor={hospColor} onDropCrna={(empId) => floatDropCrna(hospitalId, empId)} />}
                    {/* Late-staff counters — styled like the header's "N ORs running" pill; click for the 2×2 planner. */}
                    <span style={{ marginLeft: 'auto', display: 'inline-flex', gap: '6px', alignItems: 'center' }}>
                        <span style={{ position: 'relative', display: 'inline-block' }}>
                          <button type="button"
                            title="Anesthesiologists still here past 3pm — click to see who's here and who could move in"
                            onClick={(e) => openLateCounter(e, hospitalId, 'doctor')}
                            style={lateBadgeStyle(lateMDs.count, hospColor, lateCounter?.hospitalId === hospitalId && lateCounter?.type === 'doctor')}>
                            {lateMDs.count} late {lateMDs.count === 1 ? 'MD' : 'MDs'}
                          </button>
                          {lateCounter?.hospitalId === hospitalId && lateCounter?.type === 'doctor' && renderLatePopup(hospitalId, 'doctor', lateMDs, hospColor)}
                        </span>
                        <span style={{ position: 'relative', display: 'inline-block' }}>
                          <button type="button"
                            title="CRNAs still here past 3pm — click to see who's here and who could move in"
                            onClick={(e) => openLateCounter(e, hospitalId, 'nurse')}
                            style={lateBadgeStyle(lateCRNAs.count, hospColor, lateCounter?.hospitalId === hospitalId && lateCounter?.type === 'nurse')}>
                            {lateCRNAs.count} late {lateCRNAs.count === 1 ? 'CRNA' : 'CRNAs'}
                          </button>
                          {lateCounter?.hospitalId === hospitalId && lateCounter?.type === 'nurse' && renderLatePopup(hospitalId, 'nurse', lateCRNAs, hospColor)}
                        </span>
                      </span>
                  </div>
                )}

                <div
                  className="board-table"
                  style={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch', border: 'none', borderRadius: 0 }}
                  ref={setBoardRef(hospitalId)}
                >
                  <div className="board-table-inner" style={{ position: 'relative', minWidth: '1200px' }}>
                    {/* CASE-LOAD STRIP — one cell per HALF HOUR of the ruler, showing the peak number
                        of ROOMS running concurrently in that half hour at this site. It lives inside
                        board-table-inner so it shares the ruler's horizontal scroll and stays locked to
                        the hour columns; the START/FINISH lines above cross it. Cell width is derived from
                        BUCKET_MINUTES against BOARD_HOUR_COLUMN_PX, so the cells stay aligned to the hour
                        columns if either constant ever changes.

                        It is position:sticky at the TOP of .board-table, which is the element that
                        actually scrolls the rooms (overflow-y:auto, max-height:100vh-140px) -- so the
                        strip follows you down the room list just like the hour labels do. Its z-index
                        sits above the sticky thead (6) and the frozen OR column (4). The START/FINISH
                        lines pass behind it, exactly as they already pass behind the hour labels. */}
                    <div style={{ display: 'flex', alignItems: 'stretch', height: `${LOAD_STRIP_HEIGHT_PX}px`, minWidth: '1200px', position: 'sticky', top: 0, zIndex: 8, background: 'var(--bg-surface)', borderBottom: `1px solid ${hospColor}` }}>
                      <div style={{ width: `${BOARD_OR_COLUMN_PX}px`, flex: `0 0 ${BOARD_OR_COLUMN_PX}px`, display: 'flex', flexDirection: 'column', alignItems: 'flex-end', justifyContent: 'center', paddingRight: '8px', lineHeight: 1.2, fontFamily: 'var(--font-display)', whiteSpace: 'nowrap', background: 'var(--bg-surface-raised)' }}>
                        <span style={{ fontSize: '0.56em', fontWeight: 800, letterSpacing: '0.04em', textTransform: 'uppercase', color: 'var(--text-muted)' }}>
                          Concurrent rooms
                        </span>
                        <span style={{ fontSize: '0.58em', fontWeight: 800, color: 'var(--text-primary)' }}>
                          peak {sitePeakRooms} today &rarr;
                        </span>
                      </div>
                      {siteLoadBuckets.map((count, i) => {
                        const band = LOAD_BAND_STYLE[loadBand(count)];
                        const endsAnHour = i % 2 === 1; // the second half-hour's right edge IS the hour line
                        return (
                          <div key={i}
                            title={`${formatSliderTime(i * BUCKET_MINUTES)} — ${count} ${count === 1 ? 'room' : 'rooms'} running`}
                            style={{ width: `${(BOARD_HOUR_COLUMN_PX * BUCKET_MINUTES) / 60}px`, flex: `0 0 ${(BOARD_HOUR_COLUMN_PX * BUCKET_MINUTES) / 60}px`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.6em', fontWeight: 800, fontFamily: 'var(--font-display)', background: band.bg, color: band.fg, borderRight: endsAnHour ? '1px solid rgba(0,0,0,0.28)' : '1px solid rgba(0,0,0,0.10)' }}>
                            {count}
                          </div>
                        );
                      })}
                    </div>
                    {/* Fixed START (07:00) and FINISH (15:00) lines — glowing green and red-orange. */}
                    <div style={{ position: 'absolute', top: 0, bottom: 0, width: '2px', background: '#22c55e', boxShadow: '0 0 8px rgba(34,197,94,0.75)', left: `${getIndicatorLeftPx(7 * 60)}px`, pointerEvents: 'none', zIndex: 2 }} title="Start — 7:00 AM" />
                    <div style={{ position: 'absolute', top: 0, bottom: 0, width: '2px', background: '#f2600c', boxShadow: '0 0 8px rgba(242,96,12,0.75)', left: `${getIndicatorLeftPx(15 * 60)}px`, pointerEvents: 'none', zIndex: 2 }} title="Finish — 3:00 PM" />
                    {/* Time bar — a pointer-transparent visual marker of the live/target time; it glows purple
                        in "Who's free?" mode. It is NOT grabbable: the mode is now entered from the master time
                        slider's purple "Who's free?" pill and scrubbed with that slider. The old draggable knob
                        had to be repositioned by scrollTop on every scroll tick, which re-rendered the whole
                        board and caused the scroll lag — removed. The line stays pointerEvents:'none' so it
                        never steals a click from the case blocks it crosses. */}
                    <div
                      className="time-indicator"
                      title={whosFree ? `Who's free? — ${formatSliderTime(targetMinutes)}` : isLiveBar ? `Now — ${formatSliderTime(targetMinutes)}` : `Target time — ${formatSliderTime(targetMinutes)}`}
                      style={{
                        position: 'absolute',
                        top: 0,
                        bottom: 0,
                        width: '2px',
                        background: whosFree ? WHOS_FREE_PURPLE : 'var(--or-blue-mid)',
                        boxShadow: whosFree ? '0 0 12px rgba(168,85,247,0.95)' : '0 0 8px rgba(74,159,212,0.6)',
                        left: `${getIndicatorLeftPx(targetMinutes)}px`,
                        pointerEvents: 'none',
                        zIndex: whosFree ? 5 : 3
                      }}
                    />
                    <table style={{ width: '100%', minWidth: '1200px', tableLayout: 'fixed' }}>
                      <thead>
                        <tr>
                          <th style={{ width: '220px', whiteSpace: 'nowrap', top: `${LOAD_STRIP_HEIGHT_PX}px` }}>Operating Room</th>
                          {timeSlots.map(timeSlot => (
                            <th key={timeSlot} style={{ width: '100px', whiteSpace: 'nowrap', top: `${LOAD_STRIP_HEIGHT_PX}px` }}>{timeSlot}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {filteredRooms.map(room => (
                        <tr key={room.operatingRoomId} data-room-id={room.operatingRoomId}>
                          <td className="room-cell" style={{ width: '220px', verticalAlign: 'top', padding: '4px 8px' }}>
                            <strong>{room.name}</strong>
                            {(() => {
                              // Three equal, full-width nameplates under the OR name. Empty slots
                              // render as dashed drop targets (for future drag-and-drop placement).
                              const staff = getActiveStaffForRoom(room.operatingRoomId);
                              const d = staff?.doctors[0];
                              const crna = staff?.nurses.find(n => n.employeeType === 'nurse');
                              const student = staff?.nurses.find(n => n.employeeType.startsWith('nurse-student'));
                              const overrides = staff?.colorOverrides || {};
                              const inherited = staff?.inherited || false;
                              // Person-card data — the same builder the bullpen bricks use, told which
                              // placement this card is being opened from so the room-relative sections fill in.
                              const cardFor = (id: string) => providerCardFor(id, { staff, inherited, overrides });
                              // Color priority: inherited green > manual override > multi-start purple
                              // (a team covering 2+ non-overlapping early rooms, Rule 8) > shift-based base.
                              const plateColor = (id: string, base: string) =>
                                inherited ? INHERITED_TEAM_COLOR
                                : (overrides[id] || (multiStartEmployeeIds.has(id) ? NAMEPLATE_COLORS.multiStart : base));
                              // Relief-planning overlay (local): a doctor gone in-room above the CRNA (orange),
                              // and — for a room whose supervisor has gone in-room somewhere — a relief box
                              // (or the assigned relief supervisor) above the supervisor.
                              const sid = staff?.scheduleId;
                              // Relief carries to the room's later cases (caseCoverage.carriedRelief), so each box shows the
                              // reliever still covering this case even when they were assigned on an earlier one.
                              const roomCases = getSchedulesForRoom(room.operatingRoomId);
                              // A relief that has already taken over at the bar's time is ON the plate, so its box stays empty.
                              const inRoomDocId = sid && !staff?.inRoomTakenOver ? carriedRelief(sid, roomCases, reliefOverlay.inRoom) : undefined;
                              const reliefSupId = sid && !staff?.doctorTakenOver ? carriedRelief(sid, roomCases, reliefOverlay.reliefSup) : undefined;
                              // Dragging a reliever's plate off undoes the relief from this case on, like clearing its box.
                              const removeFromPlate = (fromSid: string, empId: string) => {
                                const via = staff?.reliefOf?.[empId];
                                if (via === 'doctor') return clearReliefSupervisor(fromSid);
                                if (via === 'inRoom') return clearInRoom(fromSid);
                                return handleRemoveStaff(fromSid, empId);
                              };
                              // NEED MD / NEED CRNA appear automatically once the board has been staffed
                              // (allocated or hand-placed) — a freshly simulated day stays all blank blue slots.
                              // A manual mark (slotMarks) OVERRIDES the auto behavior: 'need' forces the red
                              // brick even before allocation; 'none' shows a muted "NONE". Marks apply to the
                              // doctor and CRNA slots independently.
                              // An OB room with no cases has no `staff` at all, so the CRNA held there for an add-on
                              // C-section is drawn from the overlay instead (Braden 2026-09-16).
                              const obHeldId = !staff ? (obHold[room.operatingRoomId] || [])[0] : undefined;
                              const obHeldEmp = obHeldId ? employeeMap.get(obHeldId) : undefined;
                              const crnaShown = crna ?? (obHeldEmp
                                ? { id: obHeldId as string, lastName: obHeldEmp.lastName, fullName: `${obHeldEmp.firstName} ${obHeldEmp.lastName}`, employeeType: 'nurse', studentCompetency: undefined }
                                : undefined);
                              const showGaps = hasAlgorithmAllocations();
                              const docMark = getSlotMark(slotMarks, room.operatingRoomId, 'doctor');
                              const crnaMark = getSlotMark(slotMarks, room.operatingRoomId, 'crna');
                              const autoNeedDoctor = showGaps && !!staff && !d && !inherited;
                              // A room the ALGORITHM placed doctor-SOLO (allocationType 'doctor-only') defaults its
                              // CRNA slot to a muted "NONE" instead of a red "NEED CRNA" — a solo decision is a
                              // finished slot, not a gap the runner has to click to dismiss. A hand-placed doctor
                              // (no persisted doctor-only allocation) still prompts NEED. Manual marks override both.
                              const soloByAlgo = !!d && !!sid && allocations.some((a: any) => a.operatingRoomScheduleId === sid && a.allocationType === 'doctor-only');
                              // A doctor alone in a room reads NONE in its CRNA slot, not NEED CRNA. "Alone" =
                              // the doctor supervises no CRNA anywhere (fuchsia solo) — the reliable signal,
                              // since persisted allocations often carry no allocationType. We also honor an
                              // explicit doctor-only allocation or a manual fuchsia "solo" override.
                              // ...but a doctor who supervises a CRNA ANYWHERE that day is a supervisor, never solo:
                              // the room they merely hold a signature on still NEEDS a CRNA (e.g. one supervisor
                              // covering three CRNA rooms plus a fourth open room). So exclude day-supervisors here.
                              const soloCommitted = !!d && !daySupervisorDoctorIds.has(d.id)
                                && (supervisedCrnaCount(d.id) === 0 || soloByAlgo || matchesPaletteColor('docSolo', overrides[d.id]));
                              // NEED CRNA fires when the room has NO CRNA and it isn't a finished solo decision.
                              // Gate on `!!staff` (room/schedule context), NOT `!!d`: a fully-EMPTY room (no doctor
                              // yet) must show BOTH "NEED MD" and "NEED CRNA". soloCommitted requires a doctor, so an
                              // empty room can never read NONE here — it always prompts for a full team.
                              const autoNeedCrna = showGaps && !!staff && !crna && !student && !inherited && !soloCommitted;
                              const autoNoneCrna = showGaps && !!d && !crna && !student && !inherited && soloCommitted;
                              const effNeedDoctor = !d && (docMark === 'need' || (docMark !== 'none' && autoNeedDoctor));
                              const effNoneDoctor = !d && docMark === 'none';
                              const effNeedCrna = !crna && !student && (crnaMark === 'need' || (crnaMark !== 'none' && autoNeedCrna));
                              const effNoneCrna = !crna && !student && (crnaMark === 'none' || (crnaMark !== 'need' && autoNoneCrna));
                              // Relief plates (in-room relief, relief supervisor) get the same not-credentialed flag as the room's own plates.
                              const doctorPlate = (id: string, color: string) => ({ name: employeeMap.get(id)?.lastName || id, title: employeeMap.get(id) ? `${employeeMap.get(id)!.firstName} ${employeeMap.get(id)!.lastName}` : id, color, ...cardFor(id), uncredentialed: !isCredentialedAt(id, getOperatingRoomHospitalId(room.operatingRoomId)) });
                              return (
                                <RoomNamePlates
                                  scheduleId={staff?.scheduleId}
                                  onDropStaff={handleDropStaff}
                                  onRemoveStaff={removeFromPlate}
                                  onDoctorOnCrna={handleDropDoctorInRoom}
                                  onDropReliefSupervisor={handleDropReliefSupervisor}
                                  onClearInRoom={clearInRoom}
                                  onClearReliefSupervisor={clearReliefSupervisor}
                                  getCandidates={(kind) => getSlotCandidates(kind, room.hospitalId)}
                                  inRoomDoctor={inRoomDocId ? doctorPlate(inRoomDocId, employeeMap.get(inRoomDocId)?.employeeType === 'nurse' ? crnaNameplateColor(getEmployeeShift(inRoomDocId)) : NAMEPLATE_COLORS.docSolo) : undefined}
                                  reliefSupervisor={reliefSupId ? doctorPlate(reliefSupId, NAMEPLATE_COLORS.docSupervise) : undefined}
                                  needsReliefSupervisor={!!(d && inRoomDoctorIds.has(d.id))}
                                  doctor={d ? { name: d.lastName, title: d.fullName, color: plateColor(d.id, doctorNameplateColor(getEmployeeShift(d.id), supervisedCrnaCount(d.id))), meeting: !!meetingByEmployee[d.id], request: (statusByEmployee[d.id] || []).includes('request'), ...cardFor(d.id), shortPrep: !staff?.reliefOf?.[d.id] && shortPrepFor(room.operatingRoomId, sid, d.id), uncredentialed: !isCredentialedAt(d.id, getOperatingRoomHospitalId(room.operatingRoomId)), assignRelief: sid ? { groups: reliefDoctorGroups(d.id), current: reliefSupId ? { id: reliefSupId, name: nameOf(reliefSupId) } : undefined, onAssign: (pid: string) => handleDropReliefSupervisor(sid, pid), onClear: () => clearReliefSupervisor(sid) } : undefined } : undefined}
                                  crna={crnaShown ? { name: crnaShown.lastName, title: crnaShown.fullName, color: plateColor(crnaShown.id, crnaNameplateColor(getEmployeeShift(crnaShown.id))), meeting: !!meetingByEmployee[crnaShown.id], request: (statusByEmployee[crnaShown.id] || []).includes('request'), ...cardFor(crnaShown.id), shortPrep: !staff?.reliefOf?.[crnaShown.id] && shortPrepFor(room.operatingRoomId, sid, crnaShown.id), uncredentialed: !isCredentialedAt(crnaShown.id, getOperatingRoomHospitalId(room.operatingRoomId)), assignRelief: sid ? { groups: reliefCrnaGroups(crnaShown.id), current: inRoomDocId ? { id: inRoomDocId, name: nameOf(inRoomDocId) } : undefined, onAssign: (pid: string) => handleDropDoctorInRoom(sid, pid), onClear: () => clearInRoom(sid) } : undefined } : undefined}
                                  student={student ? { name: student.lastName, title: student.fullName, color: plateColor(student.id, studentColorFor(student.employeeType, student.studentCompetency)), ...cardFor(student.id), shortPrep: !staff?.reliefOf?.[student.id] && shortPrepFor(room.operatingRoomId, sid, student.id), uncredentialed: !isCredentialedAt(student.id, getOperatingRoomHospitalId(room.operatingRoomId)) } : undefined}
                                  doctorNoCred={!!noCred[room.operatingRoomId]?.doctor}
                                  crnaNoCred={!!noCred[room.operatingRoomId]?.crna}
                                  needDoctor={effNeedDoctor}
                                  doctorNone={effNoneDoctor}
                                  doctorHasMark={!!docMark}
                                  onSetDoctorMark={(m) => setSlotMarks(setSlotMark(selectedDate, room.operatingRoomId, 'doctor', m))}
                                  needCrna={effNeedCrna}
                                  crnaNone={effNoneCrna}
                                  crnaHasMark={!!crnaMark}
                                  onSetCrnaMark={(m) => setSlotMarks(setSlotMark(selectedDate, room.operatingRoomId, 'crna', m))}
                                />
                              );
                            })()}
                          </td>
                          <td colSpan={timeSlots.length} style={{ padding: 0, height: '40px', verticalAlign: 'top' }}>
                            <div
                              style={{ position: 'relative', width: `${timelineWidthPx}px`, height: '32px', margin: '4px 0', cursor: (testMode || drawMode) ? 'crosshair' : 'default' }}
                              onClick={(e) => {
                                if (!(testMode || drawMode)) return;
                                const rect = e.currentTarget.getBoundingClientRect();
                                const clickX = e.clientX - rect.left;
                                const clickedMinutes = Math.floor((clickX / timelineWidthPx) * 24 * 60);
                                handleTimelineClick(room.operatingRoomId, clickedMinutes);
                              }}
                            >
                              {/* Selection highlight */}
                              {(testMode || drawMode) && scheduleSelection.operatingRoomId === room.operatingRoomId && scheduleSelection.startMinutes !== null && (
                                <div
                                  style={{
                                    position: 'absolute',
                                    left: `${(scheduleSelection.startMinutes / 60) * 100}px`,
                                    width: scheduleSelection.endMinutes !== null 
                                      ? `${((scheduleSelection.endMinutes - scheduleSelection.startMinutes) / 60) * 100}px`
                                      : '2px',
                                    top: 0,
                                    height: '32px',
                                    backgroundColor: scheduleSelection.endMinutes !== null ? 'rgba(59, 130, 246, 0.3)' : 'rgba(59, 130, 246, 0.8)',
                                    border: '2px dashed #3b82f6',
                                    borderRadius: '4px',
                                    zIndex: 10,
                                    pointerEvents: 'none'
                                  }}
                                />
                              )}
                              {getSchedulesForRoom(room.operatingRoomId).map(schedule => {
                                const left = leftPxFromUtc(schedule.startTimeUtc);
                                const width = widthPxFromUtcRange(schedule.startTimeUtc, schedule.endTimeUtc);
                                const clampedLeft = Math.max(0, Math.min(timelineWidthPx, left));
                                const clampedWidth = Math.max(0, Math.min(timelineWidthPx - clampedLeft, width));
                                // Just-drawn case: type its name right on the block (Enter/blur saves, Esc keeps "New case").
                                if (namingScheduleId === schedule.scheduleId) {
                                  return (
                                    <div key={schedule.scheduleId} style={{ position: 'absolute', left: `${clampedLeft}px`, width: `${Math.max(clampedWidth, 96)}px`, top: '2px', height: '28px', zIndex: 20 }}>
                                      <input
                                        autoFocus
                                        value={namingText}
                                        placeholder="Case name…"
                                        onChange={(e) => setNamingText(e.target.value)}
                                        onClick={(e) => e.stopPropagation()}
                                        onKeyDown={(e) => { if (e.key === 'Enter') { commitCaseName(); } else if (e.key === 'Escape') { cancelCaseNaming(); } }}
                                        onBlur={commitCaseName}
                                        style={{ width: '100%', height: '28px', boxSizing: 'border-box', border: `2px solid ${WHOS_FREE_PURPLE}`, borderRadius: '4px', padding: '2px 6px', fontSize: '0.85em', outline: 'none', boxShadow: '0 0 8px rgba(168,85,247,0.6)' }}
                                      />
                                    </div>
                                  );
                                }
                                // Case-marker borders: yellow = nerve block, hot pink = pediatric (≤12). Both can
                                // show at once — yellow border + a pink ring just outside it. Hand-set overrides
                                // (caseMarkers) win over the auto detection.
                                const flags = effectiveCaseFlags(schedule, caseMarkers);
                                const card = getCardState(schedule);
                                const nbOn = flags.nerveBlock.value;
                                const pedsOn = flags.pediatric.value;
                                const markerStyle: React.CSSProperties =
                                  nbOn && pedsOn ? { border: `2px solid ${NAMEPLATE_COLORS.nerveBlockBorder}`, outline: `2px solid ${NAMEPLATE_COLORS.pediatricBorder}`, outlineOffset: '1px' }
                                  : nbOn ? { border: `2px solid ${NAMEPLATE_COLORS.nerveBlockBorder}` }
                                  : pedsOn ? { border: `2px solid ${NAMEPLATE_COLORS.pediatricBorder}` }
                                  : {};
                                return (
                                  <BoardScheduleBlock
                                    key={schedule.scheduleId}
                                    label={schedule.surgeryType}
                                    ariaLabel={`${schedule.surgeryType} - ${formatTime(schedule.startTimeUtc)} to ${formatTime(schedule.endTimeUtc)}`}
                                    className={`schedule-block clickable ${card.state}`}
                                    pinned={hasPersistedAllocations() && manualAllocations.some(ma => ma.scheduleId === schedule.scheduleId)}
                                    details={getScheduleHoverDetails(schedule)}
                                    onClick={(e) => {
                                      e.stopPropagation(); // Prevent timeline click handler from firing
                                      handleScheduleClick(schedule);
                                    }}
                                    showFlag={isAdmin}
                                    flagActive={flags.hasOverride}
                                    onFlagClick={(rect) => setFlagPopover({ schedule, anchor: rect })}
                                    style={{
                                      // Coverage changes mid-case: green/red stripes at the exact minutes (see coverageSegmentsBackground).
                                      ...(card.splitBackground ? { background: card.splitBackground } : {}),
                                      position: 'absolute',
                                      left: `${clampedLeft}px`,
                                      width: `${clampedWidth}px`,
                                      top: '2px',
                                      height: '28px',
                                      borderRadius: '4px',
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'center',
                                      fontSize: '0.85em',
                                      overflow: 'hidden',
                                      whiteSpace: 'nowrap',
                                      textOverflow: 'ellipsis',
                                      zIndex: 2,
                                      boxSizing: 'border-box',
                                      padding: '2px 4px',
                                      ...markerStyle,
                                    }}
                                  />
                                );
                              })}
                              {/* Optional: 15-minute minor grid lines */}
                              {[...Array(timeSlots.length * 4 + 1)].map((_, idx) => (
                                <div key={idx} style={{
                                  position: 'absolute',
                                  left: `${(idx * 25)}px`, // 25px per 15 minutes (since 100px per hour)
                                  top: 0,
                                  bottom: 0,
                                  width: '1px',
                                  background: idx % 4 === 0 ? 'rgba(0,0,0,0.08)' : 'rgba(0,0,0,0.04)',
                                  zIndex: 1
                                }} />
                              ))}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
            );
          })}
        </div>
      )}
      </div>

      {!isBullpenOpen ? (
        <SidebarRail label="Bullpen" side="right" onExpand={() => setIsBullpenOpen(true)} />
      ) : (
      <div className={`card shifts-card ${!showShiftsCard ? 'mobile-hidden' : ''}`} style={{ position: 'sticky', top: '10px', maxHeight: 'calc(100vh - 20px)', overflowY: 'auto' }}>
        <div className="sidebar-header">
          <h3>Bullpen</h3>
          <SidebarCollapseButton label="Bullpen" side="right" onCollapse={() => setIsBullpenOpen(false)} />
        </div>
        <div style={{ display: 'flex', alignItems: 'flex-end', gap: '8px', marginBottom: '10px' }}>
          <div className="form-group" style={{ margin: 0, flex: 1 }}>
            <label htmlFor="shiftsView" style={{ fontSize: '0.85em', color: 'var(--text-secondary)' }}>View:</label>
            <select
              id="shiftsView"
              value={shiftsView}
              onChange={(e) => setShiftsView(e.target.value as 'available' | 'allocated' | 'all')}
              className="form-control"
            >
              <option value="available">Bullpen</option>
              <option value="allocated">Allocated Shifts</option>
              <option value="all">All Shifts</option>
            </select>
          </div>
        </div>

        {shiftsView === 'available' && (
          whosFree ? (
            // Grabbing the time bar swaps the bullpen for the time-scoped "Who's free?" panel.
            <WhosFreePanel
              timeLabel={formatSliderTime(targetMinutes)}
              columns={computeWhosFreeColumns()}
              onBackToBullpen={exitWhosFree}
              backLabel={isLocalMode() ? 'Back to bullpen' : 'Jump to now'}
            />
          ) : (
            <>
              <ColorLegend />
              <Bullpen employees={employees} shifts={shifts} hospitals={hospitals} excludeIds={new Set([...staffedEmployeeIds, ...reliefEmployeeIds, ...floatCrnaEmployeeIds(floatCrnas)])} dimIds={offDoctorIds} supervisionFor={supervisionFor} onRemoveStaff={handleRemoveStaff} colorOverrideFor={(id) => overrideColorByEmployee.get(id)} orderOverride={peckingOrderOverride} doctorNeeds={doctorNeeds} doctorNeedCount={doctorNeedCount} crnaNeeds={crnaNeeds} cardFor={bullpenCardFor} />
              {/* Move Log now lives at the very bottom of the bullpen (moved out of the top button row). */}
              <button
                onClick={() => setShowMoveLog(v => !v)}
                className="btn btn-secondary"
                title="Review the manual moves made on the board today"
                style={{ width: '100%', marginTop: '12px' }}
              >
                Move Log{moveLog.length ? ` (${moveLog.length})` : ''}
              </button>
            </>
          )
        )}

        {shiftsView === 'allocated' && (
          <AllocatedShiftsCard
            selectedDate={selectedDate}
            targetTimeUtc={targetTimeUtc}
            allocations={allocations}
            schedules={schedules}
            employees={employees}
            shifts={shifts}
          />
        )}

        {shiftsView === 'all' && (
          <ShiftsCard
            selectedDate={selectedDate}
            hasPersistedAllocations={hasPersistedAllocations()}
            showSearch={false}
            showUpload={false}
            filterMode="all"
            title="All Shifts"
            shifts={shifts}
            loading={loading}
          />
        )}
      </div>
      )}

      <ManualAllocationModal
        isOpen={isModalOpen}
        onClose={handleModalClose}
        onSave={handleManualAllocationSave}
        schedule={selectedSchedule}
        employees={employees}
        shifts={shifts}
        selectedDate={selectedDate}
        operatingRooms={operatingRooms}
        hospitals={hospitals}
        existingAllocation={getPinModalPrefill()}
        allAllocations={allocations}
        modifiedAllocations={modifiedAllocations}
        startTimeUtc={localDateToUtcStart(selectedDate)}
        endTimeUtc={localDateToUtcEnd(selectedDate)}
        onReloadAllocations={async () => { await reloadDay(); }}
        isModifying={isModifying}
      />

      {/* Case-markers popover (opened by the corner ⚑ on a case block): toggle nerve-block / pediatric.
          Props recompute from live caseMarkers state, so the case border updates as you toggle. */}
      {flagPopover && (() => {
        const f = effectiveCaseFlags(flagPopover.schedule, caseMarkers);
        return (
          <CaseMarkersPopover
            anchorRect={flagPopover.anchor}
            surgeryType={flagPopover.schedule.surgeryType}
            startTimeUtc={flagPopover.schedule.startTimeUtc}
            nerveBlock={f.nerveBlock}
            pediatric={f.pediatric}
            onToggle={(kind, next) => setCaseFlag(flagPopover.schedule.scheduleId, kind, next)}
            onResetAuto={(kind) => setCaseFlag(flagPopover.schedule.scheduleId, kind, null)}
            onClose={() => setFlagPopover(null)}
          />
        );
      })()}

      {/* Create Schedule/Shift Modal (Test Mode) */}
      {isCreateScheduleModalOpen && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h2>{isCreatingShift ? 'Create Employee Shift' : 'Create Operating Room Schedule'}</h2>
              <button className="modal-close" onClick={cancelScheduleCreation}>×</button>
            </div>
            <div className="modal-body">
              {!isCreatingShift ? (
                <>
                  <div className="form-group">
                    <label>Operating Room:</label>
                    <select
                      value={newScheduleData.operatingRoomId || ''}
                      onChange={(e) => setNewScheduleData({ ...newScheduleData, operatingRoomId: e.target.value })}
                      className="form-control"
                    >
                      {operatingRooms.map(room => (
                        <option key={room.operatingRoomId} value={room.operatingRoomId}>
                          {room.name} - {getHospitalName(room.hospitalId)}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="form-group">
                    <label>Surgery Type:</label>
                    <input
                      type="text"
                      value={newScheduleData.surgeryType || ''}
                      onChange={(e) => setNewScheduleData({ ...newScheduleData, surgeryType: e.target.value })}
                      className="form-control"
                    />
                  </div>
                  <div className="form-group">
                    <label>Start Time:</label>
                    <input
                      type="datetime-local"
                      value={newScheduleData.startTimeLocal ? newScheduleData.startTimeLocal.slice(0, 16) : ''}
                      onChange={(e) => {
                        setNewScheduleData({ ...newScheduleData, startTimeLocal: e.target.value + ':00' });
                      }}
                      className="form-control"
                    />
                  </div>
                  <div className="form-group">
                    <label>End Time:</label>
                    <input
                      type="datetime-local"
                      value={newScheduleData.endTimeLocal ? newScheduleData.endTimeLocal.slice(0, 16) : ''}
                      onChange={(e) => {
                        setNewScheduleData({ ...newScheduleData, endTimeLocal: e.target.value + ':00' });
                      }}
                      className="form-control"
                    />
                  </div>
                </>
              ) : (
                <>
                  <div className="form-group">
                    <label>Employee:</label>
                    <select
                      value={newScheduleData.employeeId || ''}
                      onChange={(e) => setNewScheduleData({ ...newScheduleData, employeeId: e.target.value })}
                      className="form-control"
                    >
                      <option value="">Select an employee...</option>
                      {employees.map(emp => (
                        <option key={emp.employeeId} value={emp.employeeId}>
                          {emp.firstName} {emp.lastName} ({emp.employeeType})
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="form-group">
                    <label>Start Time:</label>
                    <input
                      type="datetime-local"
                      value={newScheduleData.startTimeLocal ? newScheduleData.startTimeLocal.slice(0, 16) : ''}
                      onChange={(e) => {
                        setNewScheduleData({ ...newScheduleData, startTimeLocal: e.target.value + ':00' });
                      }}
                      className="form-control"
                    />
                  </div>
                  <div className="form-group">
                    <label>End Time:</label>
                    <input
                      type="datetime-local"
                      value={newScheduleData.endTimeLocal ? newScheduleData.endTimeLocal.slice(0, 16) : ''}
                      onChange={(e) => {
                        setNewScheduleData({ ...newScheduleData, endTimeLocal: e.target.value + ':00' });
                      }}
                      className="form-control"
                    />
                  </div>
                  {newScheduleData.employeeId && employees.find(e => e.employeeId === newScheduleData.employeeId)?.employeeType === 'doctor' && (
                    <div className="form-group">
                      <label>Order (Priority):</label>
                      <input
                        type="number"
                        value={newScheduleData.order || 1}
                        onChange={(e) => setNewScheduleData({ ...newScheduleData, order: parseInt(e.target.value) })}
                        className="form-control"
                        min="1"
                        placeholder="1 = highest priority"
                      />
                      <small style={{ display: 'block', marginTop: '4px', color: 'var(--text-muted)' }}>
                        Lower number = higher priority for allocation
                      </small>
                    </div>
                  )}
                </>
              )}
            </div>
            <div className="modal-footer" style={{ display: 'flex', justifyContent: 'space-between' }}>
              <AdminOnly>
                <button
                  className="btn btn-secondary"
                  onClick={() => setIsCreatingShift(!isCreatingShift)}
                >
                  Switch to {isCreatingShift ? 'Schedule' : 'Shift'}
                </button>
              </AdminOnly>
              <div>
                <AdminOnly>
                  <button
                    className="btn btn-primary"
                    onClick={isCreatingShift ? handleCreateShift : handleCreateSchedule}
                    style={{ marginRight: '10px' }}
                  >
                    Create {isCreatingShift ? 'Shift' : 'Schedule'}
                  </button>
                </AdminOnly>
                <button className="btn btn-secondary" onClick={cancelScheduleCreation}>Cancel</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* View Allocation Modal */}
      {isViewModalOpen && selectedSchedule && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h2>Allocation Details</h2>
              <button className="modal-close" onClick={handleViewModalClose}>×</button>
            </div>
            <div className="modal-body">
              <div className="schedule-details">
                {isEditingSchedule ? (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <input
                      type="text"
                      value={editSurgeryType}
                      onChange={(e) => setEditSurgeryType(e.target.value)}
                      style={{ fontSize: '1.2em', fontWeight: 'bold', padding: '4px 8px', flex: 1 }}
                      autoFocus
                    />
                    <AdminOnly>
                      <button
                        className="btn btn-primary"
                        onClick={handleSaveScheduleEdit}
                        disabled={isUpdatingSchedule || !editSurgeryType.trim()}
                        style={{ padding: '4px 12px' }}
                      >
                        {isUpdatingSchedule ? 'Saving...' : 'Save'}
                      </button>
                    </AdminOnly>
                    <button
                      className="btn btn-secondary"
                      onClick={handleCancelEditSchedule}
                      disabled={isUpdatingSchedule}
                      style={{ padding: '4px 12px' }}
                    >
                      Cancel
                    </button>
                  </div>
                ) : (
                  <h3>{selectedSchedule.surgeryType}</h3>
                )}
                <div className="time-details" style={{ marginTop: '6px', color: 'var(--text-secondary)' }}>
                  <strong>Time:</strong> {formatTime(selectedSchedule.startTimeUtc)} – {formatTime(selectedSchedule.endTimeUtc)}
                </div>
                <div className="date-details" style={{ marginTop: '6px', color: 'var(--text-secondary)' }}>
                  <strong>Date:</strong> {new Date(selectedSchedule.startTimeUtc).toLocaleDateString()}
                </div>
                <div className="room-details" style={{ marginTop: '6px', color: 'var(--text-secondary)' }}>
                  <strong>Operating Room:</strong> {getOperatingRoomName(selectedSchedule.operatingRoomId)}
                </div>
                {(() => {
                  const hospitalId = getOperatingRoomHospitalId(selectedSchedule.operatingRoomId);
                  return hospitalId ? (
                    <div className="hospital-details" style={{ marginTop: '6px', color: 'var(--text-secondary)' }}>
                      <strong>Hospital:</strong> {getHospitalName(hospitalId)}
                    </div>
                  ) : null;
                })()}
                {(() => {
                  const credential = getOperatingRoomCredential(selectedSchedule.operatingRoomId);
                  return credential ? (
                    <div className="credential-details" style={{ marginTop: '6px', color: 'var(--text-secondary)' }}>
                      <strong>Required Credential:</strong> {credential}
                    </div>
                  ) : (
                    <div className="credential-details" style={{ marginTop: '6px', color: 'var(--text-secondary)' }}>
                      <strong>Required Credential:</strong> <em>None</em>
                    </div>
                  );
                })()}
              </div>
              
              {(() => {
                const allocation = allocations.find(a => a.operatingRoomScheduleId === selectedSchedule.scheduleId) || null;
                if (allocation) {
                  return (
                    <div className="allocation-details">
                      <div className="allocated-staff">
                        <div className="doctors-section">
                          <h4>Allocated Doctors ({allocation.allocatedDoctorIds.length}):</h4>
                          {allocation.allocatedDoctorIds.length > 0 ? (
                            <ul>
                              {allocation.allocatedDoctorIds.map(doctorId => {
                                const doctor = employeeMap.get(doctorId);
                                const shift = getEmployeeShift(doctorId);
                                const isPeckingOrder = shift && shift.order !== undefined && shift.order !== null;
                                const homeHospital = doctor?.homeHospitalId ? getHospitalName(doctor.homeHospitalId) : null;
                                return (
                                  <li key={doctorId} className="employee-item doctor">
                                    {getEmployeeName(doctorId)}
                                    <span style={{ color: 'var(--text-muted)', fontSize: '0.9em' }}>
                                      {' — '}
                                      {isPeckingOrder ? (
                                        <span style={{ backgroundColor: 'rgba(217, 119, 6, 0.18)', color: 'var(--amber)', padding: '1px 6px', borderRadius: '10px', fontWeight: 'bold', fontSize: '0.85em' }}>
                                          Pecking #{shift.order}
                                        </span>
                                      ) : (
                                        <span style={{ backgroundColor: 'rgba(74, 159, 212, 0.16)', color: 'var(--or-blue-mid)', padding: '1px 6px', borderRadius: '10px', fontWeight: '500', fontSize: '0.85em' }}>
                                          Shift Doctor
                                        </span>
                                      )}
                                      {homeHospital && <span> — Home: {homeHospital}</span>}
                                      {!isPeckingOrder && shift && (
                                        <span> — Shift: {formatTime(shift.startTimeUtc)} – {formatTime(shift.endTimeUtc)}</span>
                                      )}
                                    </span>
                                  </li>
                                );
                              })}
                            </ul>
                          ) : (
                            <p className="text-muted">No doctors allocated</p>
                          )}
                        </div>
                        
                        <div className="nurses-section">
                          <h4>Allocated Nurses ({allocation.allocatedNurseIds.length}):</h4>
                          {allocation.allocatedNurseIds.length > 0 ? (
                            <ul>
                              {allocation.allocatedNurseIds.map(nurseId => {
                                const nurse = employeeMap.get(nurseId);
                                const typeLabel = nurse ? getEmployeeTypeDisplayName(nurse.employeeType) : '';
                                const homeHospital = nurse?.homeHospitalId ? getHospitalName(nurse.homeHospitalId) : null;
                                const shift = getEmployeeShift(nurseId);
                                return (
                                  <li key={nurseId} className="employee-item nurse">
                                    {getEmployeeName(nurseId)}
                                    <span style={{ color: 'var(--text-muted)', fontSize: '0.9em' }}>
                                      {typeLabel && <span> ({typeLabel})</span>}
                                      {homeHospital && <span> — Home: {homeHospital}</span>}
                                      {shift && (
                                        <span> — Shift: {formatTime(shift.startTimeUtc)} – {formatTime(shift.endTimeUtc)}</span>
                                      )}
                                    </span>
                                  </li>
                                );
                              })}
                            </ul>
                          ) : (
                            <p className="text-muted">No CRNAs allocated</p>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                } else {
                  return (
                    <div className="allocation-details">
                      <p className="text-muted" style={{ marginTop: '10px' }}>No allocation found for this schedule.</p>
                    </div>
                  );
                }
              })()}
            </div>
            <div className="modal-footer" style={{ display: 'flex', justifyContent: 'space-between' }}>
              <AdminOnly>
                <div style={{ display: 'flex', gap: '8px' }}>
                  <button className="btn btn-primary" onClick={handleModifyClick}>Modify Allocation</button>
                  <button className="btn btn-secondary" onClick={handlePinAndRerunClick} title="Pin this schedule's staff and re-run the algorithm to recompute the rest of the board">Pin &amp; re-run</button>
                  {!isEditingSchedule && (
                    <button className="btn btn-warning" onClick={handleEditScheduleClick}>Edit Case Name</button>
                  )}
                </div>
              </AdminOnly>
              <div style={{ display: 'flex', gap: '8px' }}>
                <AdminOnly>
                  {showDeleteConfirm ? (
                    <>
                      <span style={{ alignSelf: 'center', color: 'var(--danger)', fontWeight: 'bold' }}>Are you sure?</span>
                      <button
                        className="btn btn-danger"
                        onClick={handleDeleteSchedule}
                        disabled={isDeletingSchedule}
                      >
                        {isDeletingSchedule ? 'Deleting...' : 'Yes, Delete'}
                      </button>
                      <button
                        className="btn btn-secondary"
                        onClick={() => setShowDeleteConfirm(false)}
                        disabled={isDeletingSchedule}
                      >
                        Cancel
                      </button>
                    </>
                  ) : (
                    <button
                      className="btn btn-danger"
                      onClick={() => setShowDeleteConfirm(true)}
                    >
                      Delete Schedule
                    </button>
                  )}
                </AdminOnly>
                {!showDeleteConfirm && (
                  <button className="btn btn-secondary" onClick={handleViewModalClose}>Close</button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
      </div>
    </>
  );
};

export default BoardView;
